// src/visual-rendering/gradient.util.ts
function buildGradientCss(config) {
  if (!config?.enabled) return "";
  try {
    if (config.type === "linear") {
      const direction = config.direction || "180deg";
      return `linear-gradient(${direction}, ${config.colors})`;
    }
    if (config.type === "radial") {
      const position = config.position || "center";
      return `radial-gradient(circle at ${position}, ${config.colors})`;
    }
    return "";
  } catch (error) {
    console.error("Error building gradient CSS:", error);
    return "";
  }
}
function parseGradientCss(gradientCss) {
  if (!gradientCss) return null;
  try {
    const linearMatch = gradientCss.match(/linear-gradient\(([^,]+),\s*(.+)\)/);
    if (linearMatch) {
      return {
        enabled: true,
        type: "linear",
        direction: linearMatch[1].trim(),
        colors: linearMatch[2].trim()
      };
    }
    const radialMatch = gradientCss.match(/radial-gradient\(circle at ([^,]+),\s*(.+)\)/);
    if (radialMatch) {
      return {
        enabled: true,
        type: "radial",
        position: radialMatch[1].trim(),
        colors: radialMatch[2].trim()
      };
    }
    return null;
  } catch (error) {
    console.error("Error parsing gradient CSS:", error);
    return null;
  }
}
function validateGradientColors(colors) {
  if (!colors) return false;
  const colorArray = colors.split(",").map((c) => c.trim());
  if (colorArray.length < 2) return false;
  const colorRegex = /^(#[0-9a-fA-F]{3,8}|rgb|rgba|hsl|hsla|[a-z]+).*$/;
  return colorArray.every((color) => colorRegex.test(color));
}

// src/visual-rendering/overlay.util.ts
function buildOverlayCss(config) {
  if (!config?.enabled) return {};
  try {
    const colorWithOpacity = applyOpacity(config.color, config.opacity);
    return {
      backgroundColor: colorWithOpacity,
      mixBlendMode: config.blendMode,
      // Overlay should be positioned absolutely to cover parent
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%"
    };
  } catch (error) {
    console.error("Error building overlay CSS:", error);
    return {};
  }
}
function applyOpacity(color, opacity) {
  if (color.startsWith("rgba")) {
    return color.replace(/rgba\((.+),\s*[\d.]+\)/, `rgba($1, ${opacity})`);
  }
  if (color.startsWith("rgb")) {
    return color.replace("rgb(", "rgba(").replace(")", `, ${opacity})`);
  }
  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    const r7 = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r7},${g},${b},${opacity})`;
  }
  return `rgba(0,0,0,${opacity})`;
}
function buildOverlayMjml(config, targetClass) {
  if (!config?.enabled) return "";
  const overlayId = `overlay-${Math.random().toString(36).substring(7)}`;
  const css = buildOverlayCss(config);
  return `
    <mj-raw>
      <style>
        .${targetClass} {
          position: relative;
        }
        .${targetClass}::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: ${css.backgroundColor};
          ${css.mixBlendMode ? `mix-blend-mode: ${css.mixBlendMode};` : ""}
          z-index: 1;
          pointer-events: none;
        }
        .${targetClass} > * {
          position: relative;
          z-index: 2;
        }
      </style>
    </mj-raw>
  `;
}

// src/visual-rendering/shape-divider.util.ts
function buildShapeDividerSvg(config) {
  if (!config?.enabled) return "";
  try {
    const height = parseInt(config.height) || 100;
    const color = config.color || "#ffffff";
    const flip = config.flip || false;
    const invert = config.invert || false;
    let svgPath;
    switch (config.type) {
      case "wave":
        svgPath = "M0,50 Q25,20 50,50 T100,50 L100,100 L0,100 Z";
        break;
      case "curve":
        svgPath = "M0,100 Q50,0 100,100 L100,100 L0,100 Z";
        break;
      case "triangle":
        svgPath = "M0,100 L50,0 L100,100 Z";
        break;
      case "arrow":
        svgPath = "M0,100 L50,20 L100,100 Z";
        break;
      case "zigzag":
        svgPath = "M0,100 L12.5,0 L25,100 L37.5,0 L50,100 L62.5,0 L75,100 L87.5,0 L100,100 Z";
        break;
      case "diagonal":
        svgPath = "M0,0 L100,100 L100,0 Z";
        break;
      case "scallop":
        svgPath = "M0,50 Q12.5,100 25,50 Q37.5,0 50,50 Q62.5,100 75,50 Q87.5,0 100,50 L100,100 L0,100 Z";
        break;
      case "organic":
        svgPath = "M0,20 Q20,80 40,30 Q60,90 80,40 Q90,10 100,50 L100,100 L0,100 Z";
        break;
      case "tilt":
        svgPath = "M0,40 L100,80 L100,100 L0,100 Z";
        break;
      case "mountains":
        svgPath = "M0,60 L16,20 L33,70 L50,30 L66,65 L83,15 L100,50 L100,100 L0,100 Z";
        break;
      case "drops":
        svgPath = "M0,0 Q12.5,60 25,0 Q37.5,60 50,0 Q62.5,60 75,0 Q87.5,60 100,0 L100,100 L0,100 Z";
        break;
      case "clouds":
        svgPath = "M0,50 Q10,20 20,50 Q30,80 40,50 Q50,20 60,50 Q70,80 80,50 Q90,20 100,50 L100,100 L0,100 Z";
        break;
      default:
        svgPath = "M0,50 Q25,20 50,50 T100,50 L100,100 L0,100 Z";
    }
    let transform = "";
    if (flip) {
      transform += " scaleX(-1)";
    }
    if (invert) {
      transform += " scaleY(-1)";
    }
    if (config.position === "top") {
      transform += " scaleY(-1)";
    }
    const transformAttr = transform ? ` transform="${transform.trim()}"` : "";
    return `
      <svg
        width="100%"
        height="${height}"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="${svgPath}"
          fill="${color}"${transformAttr}
          vector-effect="non-scaling-stroke"
        />
      </svg>
    `;
  } catch (error) {
    console.error("Error building shape divider SVG:", error);
    return "";
  }
}
function buildShapeDividerMjml(config, width = "600px") {
  if (!config?.enabled) return "";
  const svg = buildShapeDividerSvg(config);
  const position = config.position === "top" ? "top: 0;" : "bottom: 0;";
  return `
    <mj-raw>
      <div style="
        position: absolute;
        ${position}
        left: 0;
        width: 100%;
        height: ${config.height};
        line-height: 0;
        z-index: 1;
        overflow: hidden;
      ">
        ${svg}
      </div>
    </mj-raw>
  `;
}
function getShapeDividerTypes() {
  return [
    "wave",
    "curve",
    "triangle",
    "arrow",
    "zigzag",
    "diagonal",
    "scallop",
    "organic",
    "tilt",
    "mountains",
    "drops",
    "clouds"
  ];
}
function validateShapeDividerConfig(config) {
  if (!config) return false;
  if (!config.enabled) return true;
  const validTypes = [
    "wave",
    "curve",
    "triangle",
    "arrow",
    "zigzag",
    "diagonal",
    "scallop",
    "organic",
    "tilt",
    "mountains",
    "drops",
    "clouds"
  ];
  if (!validTypes.includes(config.type)) {
    return false;
  }
  if (!config.color || !config.color.match(/^#[0-9a-fA-F]{3,8}$/)) {
    return false;
  }
  if (!config.height || isNaN(parseInt(config.height))) {
    return false;
  }
  if (!["top", "bottom"].includes(config.position)) {
    return false;
  }
  return true;
}

// src/visual-rendering/style-computation.util.ts
function computeBackgroundStyle(styleConfig) {
  if (!styleConfig) return void 0;
  const gradientCss = buildGradientCss(styleConfig.gradient);
  if (gradientCss) return gradientCss;
  if (styleConfig.backgroundImage) {
    const size = styleConfig.backgroundSize || "cover";
    const position = styleConfig.backgroundPosition || "center";
    const repeat = styleConfig.backgroundRepeat || "no-repeat";
    return `url(${styleConfig.backgroundImage}) ${position}/${size} ${repeat}`;
  }
  if (styleConfig.backgroundColor) {
    return styleConfig.backgroundColor;
  }
  return void 0;
}
function computePadding(padding) {
  if (!padding) return "0";
  if (typeof padding === "object") {
    const { top = "0", right = "0", bottom = "0", left = "0" } = padding;
    return `${normalizeUnit(top)} ${normalizeUnit(right)} ${normalizeUnit(bottom)} ${normalizeUnit(left)}`;
  }
  const normalized = padding.split(/\s+/).map((value) => normalizeUnit(value)).join(" ");
  return normalized;
}
function normalizeUnit(value) {
  if (value === "0" || value === "") return "0";
  if (/^\d+$/.test(value)) return `${value}px`;
  return value;
}
function computeMargin(margin) {
  if (!margin) return "0";
  return computePadding(margin);
}
function computeBorder(border) {
  if (!border) return void 0;
  return border;
}
function computeFontWeight(fontWeight) {
  if (fontWeight === void 0) return void 0;
  return String(fontWeight);
}
function computeClassNames(styleConfig) {
  if (!styleConfig?.customClasses || styleConfig.customClasses.length === 0) {
    return void 0;
  }
  return styleConfig.customClasses.join(" ");
}
function computeInlineStyles(styleConfig) {
  if (!styleConfig) return {};
  const styles = {};
  if (styleConfig.padding) styles.padding = computePadding(styleConfig.padding);
  if (styleConfig.margin) styles.margin = computeMargin(styleConfig.margin);
  if (styleConfig.width) styles.width = styleConfig.width;
  if (styleConfig.height) styles.height = styleConfig.height;
  if (styleConfig.fontSize) styles.fontSize = styleConfig.fontSize;
  if (styleConfig.fontWeight) {
    const weight = computeFontWeight(styleConfig.fontWeight);
    styles.fontWeight = weight ? isNaN(Number(weight)) ? weight : Number(weight) : void 0;
  }
  if (styleConfig.fontFamily) styles.fontFamily = styleConfig.fontFamily;
  if (styleConfig.color) styles.color = styleConfig.color;
  if (styleConfig.lineHeight) styles.lineHeight = styleConfig.lineHeight;
  if (styleConfig.letterSpacing) styles.letterSpacing = styleConfig.letterSpacing;
  if (styleConfig.textAlign) styles.textAlign = styleConfig.textAlign;
  if (styleConfig.textDecoration) styles.textDecoration = styleConfig.textDecoration;
  if (styleConfig.fontStyle) styles.fontStyle = styleConfig.fontStyle;
  if (styleConfig.textTransform) styles.textTransform = styleConfig.textTransform;
  const background = computeBackgroundStyle(styleConfig);
  if (background) styles.background = background;
  if (styleConfig.border) styles.border = styleConfig.border;
  if (styleConfig.borderRadius) styles.borderRadius = styleConfig.borderRadius;
  if (styleConfig.borderTop) styles.borderTop = styleConfig.borderTop;
  if (styleConfig.borderRight) styles.borderRight = styleConfig.borderRight;
  if (styleConfig.borderBottom) styles.borderBottom = styleConfig.borderBottom;
  if (styleConfig.borderLeft) styles.borderLeft = styleConfig.borderLeft;
  if (styleConfig.verticalAlign) styles.verticalAlign = styleConfig.verticalAlign;
  if (styleConfig.zIndex) styles.zIndex = styleConfig.zIndex;
  if (styleConfig.opacity) styles.opacity = styleConfig.opacity;
  if (styleConfig.boxShadow) styles.boxShadow = styleConfig.boxShadow;
  return styles;
}
function isTextOnGradient(styleConfig) {
  if (!styleConfig) return false;
  return styleConfig.gradient?.enabled === true;
}
function suggestTextOnGradientPadding(currentPadding) {
  if (!currentPadding) return "20px 30px";
  const parts = currentPadding.split(/\s+/);
  const values = parts.map((p) => parseInt(p));
  if (values.some((v) => v < 15)) {
    return "20px 30px";
  }
  return currentPadding;
}
function validateColor(color) {
  if (!color) return false;
  if (/^#[0-9a-fA-F]{3,8}$/.test(color)) return true;
  if (/^rgba?\(/.test(color)) return true;
  if (/^hsla?\(/.test(color)) return true;
  const namedColors = [
    "black",
    "white",
    "red",
    "blue",
    "green",
    "yellow",
    "orange",
    "purple",
    "pink",
    "gray",
    "grey",
    "transparent"
  ];
  if (namedColors.includes(color.toLowerCase())) return true;
  return false;
}
function validateDimension(value) {
  if (!value) return false;
  if (value === "0") return true;
  if (/^\d+(\.\d+)?(px|%|em|rem|vh|vw|vmin|vmax|pt|cm|mm|in)$/.test(value)) return true;
  return false;
}

// src/visual-rendering/canvaskit-paint.ts
function parseColor(ck, color) {
  const rgbaMatch = color.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+))?\s*\)/
  );
  if (rgbaMatch) {
    return ck.Color(
      Number(rgbaMatch[1]),
      Number(rgbaMatch[2]),
      Number(rgbaMatch[3]),
      rgbaMatch[4] != null ? Number(rgbaMatch[4]) : 1
    );
  }
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const r7 = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    return ck.Color(r7, g, b, a);
  }
  return ck.Color(0, 0, 0, 1);
}
function resolveLinearGradientCoords(nodeX, nodeY, nodeW, nodeH, startPoint, endPoint, angle) {
  if (startPoint && endPoint) {
    return {
      start: [nodeX + startPoint.x * nodeW, nodeY + startPoint.y * nodeH],
      end: [nodeX + endPoint.x * nodeW, nodeY + endPoint.y * nodeH]
    };
  }
  const rad = (angle ?? 0) * Math.PI / 180;
  const dirX = Math.sin(rad);
  const dirY = -Math.cos(rad);
  const cx = nodeX + nodeW / 2;
  const cy = nodeY + nodeH / 2;
  const halfDiag = Math.sqrt(nodeW * nodeW + nodeH * nodeH) / 2;
  return {
    start: [cx - dirX * halfDiag, cy - dirY * halfDiag],
    end: [cx + dirX * halfDiag, cy + dirY * halfDiag]
  };
}

// src/visual-rendering/image-adjustments.ts
var LR = 0.2126;
var LG = 0.7152;
var LB = 0.0722;
var IDENTITY = [
  1,
  0,
  0,
  0,
  0,
  0,
  1,
  0,
  0,
  0,
  0,
  0,
  1,
  0,
  0,
  0,
  0,
  0,
  1,
  0
];
function isIdentity(m) {
  for (let i = 0; i < 20; i++) {
    if (Math.abs(m[i] - IDENTITY[i]) > 1e-9) return false;
  }
  return true;
}
function multiply(outer, inner) {
  const out = new Array(20).fill(0);
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += outer[row * 5 + k] * inner[k * 5 + col];
      out[row * 5 + col] = v;
    }
    let t2 = outer[row * 5 + 4];
    for (let k = 0; k < 4; k++) t2 += outer[row * 5 + k] * inner[k * 5 + 4];
    out[row * 5 + 4] = t2;
  }
  return out;
}
function saturationMatrix(s) {
  return [
    LR + (1 - LR) * s,
    LG - LG * s,
    LB - LB * s,
    0,
    0,
    LR - LR * s,
    LG + (1 - LG) * s,
    LB - LB * s,
    0,
    0,
    LR - LR * s,
    LG - LG * s,
    LB + (1 - LB) * s,
    0,
    0,
    0,
    0,
    0,
    1,
    0
  ];
}
function brightnessMatrix(b) {
  return [
    b,
    0,
    0,
    0,
    0,
    0,
    b,
    0,
    0,
    0,
    0,
    0,
    b,
    0,
    0,
    0,
    0,
    0,
    1,
    0
  ];
}
function contrastMatrix(c) {
  const t2 = 0.5 - 0.5 * c;
  return [
    c,
    0,
    0,
    0,
    t2,
    0,
    c,
    0,
    0,
    t2,
    0,
    0,
    c,
    0,
    t2,
    0,
    0,
    0,
    1,
    0
  ];
}
function hueRotateMatrix(degrees) {
  const rad = degrees * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return [
    LR + cos * (1 - LR) + sin * -LR,
    LG + cos * -LG + sin * -LG,
    LB + cos * -LB + sin * (1 - LB),
    0,
    0,
    LR + cos * -LR + sin * 0.143,
    LG + cos * (1 - LG) + sin * 0.14,
    LB + cos * -LB + sin * -0.283,
    0,
    0,
    LR + cos * -LR + sin * -(1 - LR),
    LG + cos * -LG + sin * LG,
    LB + cos * (1 - LB) + sin * LB,
    0,
    0,
    0,
    0,
    0,
    1,
    0
  ];
}
function duotoneMatrix(duo) {
  const s = parseColorComponents(duo.shadows);
  const h = parseColorComponents(duo.highlights);
  const mix = clamp01(duo.mix ?? 1);
  const inv = 1 - mix;
  const row = (i) => {
    const span = h[i] - s[i];
    return [
      mix * span * LR + inv * (i === 0 ? 1 : 0),
      mix * span * LG + inv * (i === 1 ? 1 : 0),
      mix * span * LB + inv * (i === 2 ? 1 : 0),
      0,
      mix * s[i]
    ];
  };
  return [...row(0), ...row(1), ...row(2), 0, 0, 0, 1, 0];
}
function clamp01(v) {
  return Math.min(1, Math.max(0, v));
}
function parseColorComponents(color) {
  const rgbaMatch = color.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/
  );
  if (rgbaMatch) {
    return [
      Number(rgbaMatch[1]) / 255,
      Number(rgbaMatch[2]) / 255,
      Number(rgbaMatch[3]) / 255
    ];
  }
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      hex = hex.split("").map((ch) => ch + ch).join("");
    }
    return [
      parseInt(hex.slice(0, 2), 16) / 255,
      parseInt(hex.slice(2, 4), 16) / 255,
      parseInt(hex.slice(4, 6), 16) / 255
    ];
  }
  return [0, 0, 0];
}
function hasDuotone(adj) {
  return !!adj?.duotone && clamp01(adj.duotone.mix ?? 1) > 0;
}
function hasVisibleAdjustments(adj) {
  if (!adj) return false;
  return adjustmentsToColorMatrix(adj) != null || adj.blur != null && adj.blur > 0;
}
function adjustmentsToColorMatrix(adj) {
  if (!adj) return null;
  let m = IDENTITY;
  if (adj.grayscale != null && adj.grayscale !== 0) {
    m = multiply(saturationMatrix(1 - clamp01(adj.grayscale)), m);
  }
  if (adj.brightness != null && adj.brightness !== 1) {
    m = multiply(brightnessMatrix(Math.max(0, adj.brightness)), m);
  }
  if (adj.contrast != null && adj.contrast !== 1) {
    m = multiply(contrastMatrix(Math.max(0, adj.contrast)), m);
  }
  if (adj.saturation != null && adj.saturation !== 1) {
    m = multiply(saturationMatrix(Math.max(0, adj.saturation)), m);
  }
  if (adj.hueRotate != null && adj.hueRotate % 360 !== 0) {
    m = multiply(hueRotateMatrix(adj.hueRotate), m);
  }
  if (hasDuotone(adj)) {
    m = multiply(duotoneMatrix(adj.duotone), m);
  }
  return isIdentity(m) ? null : m;
}
function adjustmentsToCssFilter(adj) {
  if (!adj) return null;
  if (hasDuotone(adj)) return null;
  const parts = [];
  if (adj.grayscale != null && adj.grayscale !== 0) {
    parts.push(`grayscale(${round(clamp01(adj.grayscale))})`);
  }
  if (adj.brightness != null && adj.brightness !== 1) {
    parts.push(`brightness(${round(Math.max(0, adj.brightness))})`);
  }
  if (adj.contrast != null && adj.contrast !== 1) {
    parts.push(`contrast(${round(Math.max(0, adj.contrast))})`);
  }
  if (adj.saturation != null && adj.saturation !== 1) {
    parts.push(`saturate(${round(Math.max(0, adj.saturation))})`);
  }
  if (adj.hueRotate != null && adj.hueRotate % 360 !== 0) {
    parts.push(`hue-rotate(${round(adj.hueRotate)}deg)`);
  }
  if (adj.blur != null && adj.blur > 0) {
    parts.push(`blur(${round(adj.blur)}px)`);
  }
  return parts.length ? parts.join(" ") : null;
}
function round(v) {
  return Math.round(v * 1e3) / 1e3;
}

// src/visual-rendering/rich-text.ts
function isUnderlined(style) {
  return style.underline ?? !!style.link;
}
function getRichList(pluginData) {
  const rich = pluginData?.["richText"];
  const v = rich?.list;
  return v === "bullet" || v === "ordered" ? v : null;
}
function spansForLine(spans, lineStart, lineLength) {
  const lineEnd = lineStart + lineLength;
  const out = [];
  for (const s of spans) {
    if (s.end <= lineStart || s.start >= lineEnd) continue;
    out.push({
      ...s,
      start: Math.max(0, s.start - lineStart),
      end: Math.min(lineLength, s.end - lineStart)
    });
  }
  return out;
}
function getRichSpans(pluginData, textLength) {
  const rich = pluginData?.["richText"];
  const raw = rich?.spans;
  if (!Array.isArray(raw)) return [];
  const spans = [];
  for (const s of raw) {
    if (!s || typeof s !== "object") continue;
    const span = s;
    const start = Math.max(0, Math.min(textLength, Math.floor(span.start)));
    const end = Math.max(0, Math.min(textLength, Math.floor(span.end)));
    if (!(end > start)) continue;
    const fontSize = typeof span.fontSize === "number" && Number.isFinite(span.fontSize) && span.fontSize > 0 ? span.fontSize : void 0;
    const fontFamily = typeof span.fontFamily === "string" && span.fontFamily.trim() ? span.fontFamily : void 0;
    const clean = { ...span, start, end, fontSize, fontFamily };
    if (!hasOverride(clean)) continue;
    spans.push(clean);
  }
  return spans;
}
function hasOverride(s) {
  return !!(s.bold || s.italic || s.underline || s.strikethrough || s.color || s.link || s.fontSize || s.fontFamily);
}
function splitIntoRuns(text, spans) {
  if (!spans.length) return [{ text, start: 0, end: text.length, style: null }];
  const bounds = /* @__PURE__ */ new Set([0, text.length]);
  for (const s of spans) {
    bounds.add(s.start);
    bounds.add(s.end);
  }
  const cuts = [...bounds].filter((n) => n >= 0 && n <= text.length).sort((a, b) => a - b);
  const runs = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const a = cuts[i];
    const b = cuts[i + 1];
    if (b <= a) continue;
    let style = null;
    for (const s of spans) {
      if (s.start <= a && s.end >= b) {
        style ?? (style = {});
        if (s.bold) style.bold = true;
        if (s.italic) style.italic = true;
        if (s.underline != null) style.underline = s.underline;
        if (s.strikethrough) style.strikethrough = true;
        if (s.color) style.color = s.color;
        if (s.link) style.link = s.link;
        if (s.fontSize) style.fontSize = s.fontSize;
        if (s.fontFamily) style.fontFamily = s.fontFamily;
      }
    }
    runs.push({ text: text.slice(a, b), start: a, end: b, style });
  }
  return runs;
}

// src/visual-rendering/font-variants.ts
var GENERIC_FONT_FAMILIES = /* @__PURE__ */ new Set([
  // CSS generics + bundled
  "roboto",
  "sans-serif",
  "serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-sans-serif",
  "ui-serif",
  "ui-monospace",
  "inherit",
  "initial",
  "unset",
  "",
  // Web-safe system fonts (not on Google Fonts / Fontsource)
  "arial",
  "arial black",
  "helvetica",
  "helvetica neue",
  "times",
  "times new roman",
  "georgia",
  "garamond",
  "palatino",
  "palatino linotype",
  "optima",
  "courier",
  "courier new",
  "verdana",
  "tahoma",
  "geneva",
  "trebuchet ms",
  "lucida grande",
  "lucida sans unicode",
  "segoe ui",
  "impact",
  "comic sans ms"
]);
var EMOJI_FONT_FAMILY = "Noto Color Emoji";
var EMOJI_FONT_URL = "https://cdn.jsdelivr.net/gh/googlefonts/noto-emoji@v2.047/fonts/NotoColorEmoji.ttf";
var EMOJI_FONT_FACE_CSS = `@font-face { font-family: '${EMOJI_FONT_FAMILY}'; src: url('${EMOJI_FONT_URL}') format('truetype'); }`;
function buildFontFaceCss(family, fileUrl, weight, style) {
  const format = fileUrl.endsWith(".woff2") ? "woff2" : fileUrl.endsWith(".woff") ? "woff" : fileUrl.endsWith(".otf") ? "opentype" : "truetype";
  return [
    "@font-face {",
    `  font-family: '${family}';`,
    `  src: url('${fileUrl}') format('${format}');`,
    `  font-weight: ${weight || 400};`,
    `  font-style: ${style || "normal"};`,
    "  font-display: swap;",
    "}"
  ].join("\n");
}
var EMOJI_RE = /\p{Extended_Pictographic}/u;
function textHasEmoji(text) {
  return typeof text === "string" && EMOJI_RE.test(text);
}
var SYMBOL_FONT_FAMILY = "Noto Sans Symbols 2";
var SYMBOL_FONT_URL = "https://cdn.jsdelivr.net/gh/google/fonts@7b6724ac7ececc713e9ba93af309f7520c9a80a3/ofl/notosanssymbols2/NotoSansSymbols2-Regular.ttf";
var SYMBOL_FONT_FACE_CSS = `@font-face { font-family: '${SYMBOL_FONT_FAMILY}'; src: url('${SYMBOL_FONT_URL}') format('truetype'); }`;
var SYMBOL_RE = /[\u2190-\u21FF\u2300-\u23FF\u25A0-\u27BF\u2B00-\u2BFF]/u;
function textHasSymbol(text) {
  return typeof text === "string" && SYMBOL_RE.test(text);
}
function primaryFontFamily(value) {
  if (typeof value !== "string") return null;
  const first = value.split(",")[0].trim().replace(/^["']|["']$/g, "");
  if (!first || GENERIC_FONT_FAMILIES.has(first.toLowerCase())) return null;
  return first;
}
function normalizeWeight(w) {
  const n = typeof w === "number" && Number.isFinite(w) ? w : 400;
  return Math.min(900, Math.max(100, Math.round(n / 100) * 100));
}
function addFontVariant(set, family, weight, italic) {
  const axes = set[family] ?? (set[family] = { weights: [], italicWeights: [] });
  const w = normalizeWeight(weight);
  const list = italic ? axes.italicWeights : axes.weights;
  if (!list.includes(w)) list.push(w);
}
function collectSceneFontVariants(nodes, into = {}) {
  if (!nodes) return into;
  for (const node of Object.values(nodes)) {
    if (textHasEmoji(node?.text)) {
      addFontVariant(into, EMOJI_FONT_FAMILY, 400, false);
    }
    if (textHasSymbol(node?.text)) {
      addFontVariant(into, SYMBOL_FONT_FAMILY, 400, false);
    }
    const family = primaryFontFamily(node?.fontFamily);
    if (!family) continue;
    const weight = typeof node.fontWeight === "number" ? node.fontWeight : 400;
    const italic = node.fontStyle === "italic";
    addFontVariant(into, family, weight, italic);
    const spans = node.pluginData?.richText?.spans;
    for (const span of Array.isArray(spans) ? spans : []) {
      if (!span) continue;
      const spanWeight = span.bold ? Math.max(weight, 700) : weight;
      const spanItalic = italic || !!span.italic;
      addFontVariant(into, family, spanWeight, spanItalic);
      const spanFamily = primaryFontFamily(span.fontFamily);
      if (spanFamily) addFontVariant(into, spanFamily, spanWeight, spanItalic);
    }
  }
  return into;
}
function buildGoogleFontVariantUrls(family, axes) {
  const upright = /* @__PURE__ */ new Set([400, 700, ...axes?.weights ?? []]);
  const italic = new Set(axes?.italicWeights ?? []);
  const encoded = family.replace(/ /g, "+");
  const weights = [...upright].sort((a, b) => a - b).join(";");
  const urls = [`https://fonts.googleapis.com/css2?family=${encoded}:wght@${weights}&display=swap`];
  if (italic.size > 0) {
    const tuples = [...italic].sort((a, b) => a - b).map((w) => `1,${w}`).join(";");
    urls.push(`https://fonts.googleapis.com/css2?family=${encoded}:ital,wght@${tuples}&display=swap`);
  }
  return urls;
}
function googleFontVariantUrls(set) {
  return Object.keys(set).sort().filter((family) => family !== EMOJI_FONT_FAMILY && family !== SYMBOL_FONT_FAMILY).flatMap((family) => buildGoogleFontVariantUrls(family, set[family]));
}
function degradeGoogleFontUrl(url) {
  const ital = url.match(/^(.*family=[^:]+):ital,wght@[^&]*(.*)$/);
  if (ital) return `${ital[1]}:wght@400;700${ital[2]}`;
  const wght = url.match(/^(.*family=[^:]+):wght@[^&]*(.*)$/);
  if (wght) return `${wght[1]}${wght[2]}`;
  return null;
}

// src/visual-rendering/canvaskit-text-measure.ts
function applyTextTransform(text, transform) {
  if (transform === "uppercase") return text.toUpperCase();
  if (transform === "lowercase") return text.toLowerCase();
  if (transform === "capitalize") {
    return text.replace(/(^|[^\p{L}\p{N}])(\p{L})/gu, (_m, sep, ch) => sep + ch.toUpperCase());
  }
  return text;
}
function buildTextParagraph(ck, fontMgr, style, fontSize, layoutWidth, color, foregroundPaint) {
  const baseWeight = ckFontWeight(ck, style.fontWeight);
  const baseColor = color ?? ck.Color(0, 0, 0, 1);
  const baseTextStyle = {
    color: baseColor,
    fontSize,
    // Requested font first, Roboto only as a fallback for glyphs the requested
    // font lacks. CanvasKit resolves families left-to-right, so listing Roboto
    // first made it satisfy every Latin glyph and the requested font (e.g. the
    // imported Figma font) was never reached -- text measured in Roboto metrics,
    // leaving the box wider than the glyphs and exaggerating justified spacing.
    // The emoji family is last: no text font carries emoji glyphs, and it's a
    // no-op when the FontMgr has no emoji font registered.
    fontFamilies: fontFamilyFallbacks(style.fontFamily, style.fontWeight),
    fontStyle: {
      weight: baseWeight,
      slant: style.fontStyle === "italic" ? ck.FontSlant.Italic : ck.FontSlant.Upright
    },
    letterSpacing: style.letterSpacing ?? 0,
    heightMultiplier: style.lineHeight ?? 1.2
  };
  const ps = new ck.ParagraphStyle({
    textStyle: baseTextStyle,
    textAlign: style.textAlign === "center" ? ck.TextAlign.Center : style.textAlign === "right" ? ck.TextAlign.Right : style.textAlign === "justify" ? ck.TextAlign.Justify : ck.TextAlign.Left
  });
  const b = ck.ParagraphBuilder.Make(ps, fontMgr);
  let bgPaint = null;
  if (foregroundPaint) {
    bgPaint = new ck.Paint();
    bgPaint.setColor(ck.Color(0, 0, 0, 0));
    b.pushPaintStyle(new ck.TextStyle(baseTextStyle), foregroundPaint, bgPaint);
  }
  const emitRuns = (text, spans) => {
    for (const run of splitIntoRuns(text, spans)) {
      const runText = applyTextTransform(run.text, style.textTransform);
      if (run.style && !foregroundPaint) {
        const ts = new ck.TextStyle(runTextStyle(ck, baseTextStyle, baseColor, style, run.style));
        b.pushStyle(ts);
        b.addText(runText);
        b.pop();
      } else {
        b.addText(runText);
      }
    }
  };
  if (style.list) {
    const spans = style.spans ?? [];
    const lines = style.text.split("\n");
    let base = 0;
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) b.addText("\n");
      const marker = style.list === "ordered" ? `${i + 1}.  ` : "\u2022  ";
      b.addText(applyTextTransform(marker, style.textTransform));
      emitRuns(lines[i], spansForLine(spans, base, lines[i].length));
      base += lines[i].length + 1;
    }
  } else if (style.spans?.length) {
    emitRuns(style.text, style.spans);
  } else {
    b.addText(applyTextTransform(style.text, style.textTransform));
  }
  if (foregroundPaint) b.pop();
  const p = b.build();
  p.layout(layoutWidth);
  b.delete();
  bgPaint?.delete();
  return p;
}
var WEIGHT_FAMILY_ALIASES = {
  100: "Thin",
  200: "ExtraLight",
  300: "Light",
  500: "Medium",
  600: "SemiBold",
  800: "ExtraBold",
  900: "Black"
};
function fontFamilyFallbacks(family, weight) {
  const base = family ?? "sans-serif";
  const rounded = Math.min(900, Math.max(100, Math.round((weight ?? 400) / 100) * 100));
  const suffix = WEIGHT_FAMILY_ALIASES[rounded];
  const list = suffix ? [`${base} ${suffix}`, base] : [base];
  list.push("Roboto", EMOJI_FONT_FAMILY, SYMBOL_FONT_FAMILY);
  return list;
}
function ckFontWeight(ck, weight) {
  const w = weight ?? 400;
  if (w < 150) return ck.FontWeight.Thin;
  if (w < 250) return ck.FontWeight.ExtraLight;
  if (w < 350) return ck.FontWeight.Light;
  if (w < 450) return ck.FontWeight.Normal;
  if (w < 550) return ck.FontWeight.Medium;
  if (w < 650) return ck.FontWeight.SemiBold;
  if (w < 750) return ck.FontWeight.Bold;
  if (w < 850) return ck.FontWeight.ExtraBold;
  return ck.FontWeight.Black;
}
function runTextStyle(ck, baseTextStyle, baseColor, style, run) {
  const italic = run.italic || style.fontStyle === "italic";
  const color = run.color ? parseColor(ck, run.color) : baseColor;
  let decoration = 0;
  if (isUnderlined(run)) decoration |= ck.UnderlineDecoration;
  if (run.strikethrough) decoration |= ck.LineThroughDecoration;
  const numericWeight = run.bold ? Math.max(style.fontWeight ?? 400, 700) : style.fontWeight ?? 400;
  const weight = ckFontWeight(ck, numericWeight);
  const renderBaseSize = baseTextStyle.fontSize;
  const authoredBase = style.fontSize && style.fontSize > 0 ? style.fontSize : renderBaseSize;
  const shrinkRatio = authoredBase > 0 ? renderBaseSize / authoredBase : 1;
  const fontSize = run.fontSize != null ? run.fontSize * shrinkRatio : renderBaseSize;
  const fontFamilies = run.fontFamily ? fontFamilyFallbacks(run.fontFamily, numericWeight) : baseTextStyle.fontFamilies;
  return {
    ...baseTextStyle,
    fontSize,
    fontFamilies,
    color,
    fontStyle: {
      weight,
      slant: italic ? ck.FontSlant.Italic : ck.FontSlant.Upright
    },
    ...decoration ? { decoration, decorationColor: color } : {}
  };
}
function measureTextHeight(ck, fontMgr, style, width) {
  const fontSize = style.fontSize ?? 16;
  const layoutWidth = width > 0 ? width : 1e4;
  const p = buildTextParagraph(ck, fontMgr, style, fontSize, layoutWidth);
  const height = p.getHeight();
  p.delete();
  return height;
}
function measureText(ck, fontMgr, style, layoutWidth) {
  const fontSize = style.fontSize ?? 16;
  const width = layoutWidth > 0 ? layoutWidth : 1e4;
  const p = buildTextParagraph(ck, fontMgr, style, fontSize, width);
  const out = {
    height: p.getHeight(),
    maxIntrinsicWidth: p.getMaxIntrinsicWidth(),
    longestLine: typeof p.getLongestLine === "function" ? p.getLongestLine() : p.getMaxWidth()
  };
  p.delete();
  return out;
}

// src/visual-rendering/canvaskit-text-warp.ts
var DEFAULT_WARP = { type: "none", bend: 0, distortion: 0 };
var WARP_TYPES = [
  { value: "none", label: "None" },
  { value: "arc", label: "Arc" },
  { value: "wave", label: "Wave" },
  { value: "flag", label: "Flag" },
  { value: "bulge", label: "Bulge" }
];
function invalidateOutlineCache(_nodeId) {
}
function computeWarpScale(totalWidth, nodeWidth) {
  if (totalWidth <= 0 || nodeWidth <= 0) return 1;
  return totalWidth > nodeWidth ? nodeWidth / totalWidth : 1;
}
var warpArc = (t2, w, _h, bend) => {
  const theta = bend / 100 * Math.PI;
  if (Math.abs(theta) < 0.01) return { x: t2 * w, y: _h / 2, angle: 0 };
  const sinHalf = Math.sin(theta / 2);
  const radius = w / 2 / Math.abs(sinHalf);
  const cx = w / 2;
  const cy = theta > 0 ? radius : -radius;
  const angle = (t2 - 0.5) * theta;
  const sign = theta > 0 ? 1 : -1;
  return {
    x: cx + radius * Math.sin(angle),
    y: cy - sign * radius * Math.cos(angle),
    angle
  };
};
var warpWave = (t2, w, h, bend, dist) => {
  const amplitude = bend / 100 * h * 0.4;
  const freq = 1 + Math.abs(dist / 100) * 2;
  const phase = t2 * Math.PI * 2 * freq;
  const y = h / 2 + Math.sin(phase) * amplitude;
  const dy = Math.cos(phase) * amplitude * Math.PI * 2 * freq / w;
  return { x: t2 * w, y, angle: Math.atan(dy) };
};
var warpFlag = (t2, w, h, bend) => {
  const amplitude = bend / 100 * h * 0.4;
  const phase = t2 * Math.PI * 2;
  const y = h / 2 + Math.sin(phase) * amplitude * t2;
  const dy = amplitude * (Math.sin(phase) / w + t2 * Math.cos(phase) * Math.PI * 2 / w);
  return { x: t2 * w, y, angle: Math.atan(dy) };
};
var warpBulge = (t2, w, h, bend) => {
  const strength = bend / 100;
  const nt = t2 - 0.5;
  const factor = 1 + strength * Math.max(0, 1 - 4 * nt * nt);
  const y = h / 2 + (factor - 1) * h * 0.3 * (strength > 0 ? -1 : 1);
  return { x: t2 * w, y, angle: 0 };
};
var WARP_FNS = {
  arc: warpArc,
  wave: warpWave,
  flag: warpFlag,
  bulge: warpBulge
};
function renderWarpedText(ck, canvas, fontMgr, node, config) {
  if (!node.text) return;
  const fn = WARP_FNS[config.type];
  if (!fn) return;
  const text = node.textTransform === "uppercase" ? node.text.toUpperCase() : node.textTransform === "lowercase" ? node.text.toLowerCase() : node.text;
  const fontSize = node.fontSize ?? 16;
  const fontFamily = node.fontFamily ?? "Roboto";
  const fontStyle = {
    weight: ckFontWeight(ck, node.fontWeight),
    width: ck.FontWidth.Normal,
    slant: node.fontStyle === "italic" ? ck.FontSlant.Italic : ck.FontSlant.Upright
  };
  let typeface = fontMgr.matchFamilyStyle(fontFamily, fontStyle);
  if (!typeface) typeface = fontMgr.matchFamilyStyle("Roboto", fontStyle);
  if (!typeface) return;
  const font = new ck.Font(typeface, fontSize);
  font.setEdging(ck.FontEdging.AntiAlias);
  const glyphIds = font.getGlyphIDs(text);
  let widths = font.getGlyphWidths(glyphIds);
  let totalWidth = widths.reduce((s, w) => s + w, 0);
  if (totalWidth <= 0) {
    font.delete();
    return;
  }
  const scale = computeWarpScale(totalWidth, node.width);
  const effFontSize = fontSize * scale;
  if (scale < 1) {
    font.setSize(effFontSize);
    widths = widths.map((w) => w * scale);
    totalWidth = node.width;
  }
  const rsxforms = [];
  let xAccum = 0;
  for (let i = 0; i < text.length; i++) {
    const glyphW = widths[i] ?? 0;
    const glyphCenter = xAccum + glyphW / 2;
    const t2 = glyphCenter / totalWidth;
    const placement = fn(t2, node.width, node.height, config.bend, config.distortion);
    const cos = Math.cos(placement.angle);
    const sin = Math.sin(placement.angle);
    const anchorX = glyphW / 2;
    const anchorY = -effFontSize * 0.35;
    const tx = placement.x - anchorX * cos + anchorY * sin;
    const ty = placement.y - anchorX * sin - anchorY * cos;
    rsxforms.push(cos, sin, tx, ty);
    xAccum += glyphW;
  }
  const blob = ck.TextBlob.MakeFromRSXform(text, rsxforms, font);
  if (!blob) {
    font.delete();
    return;
  }
  if (node.fills?.length) {
    for (const fill of node.fills) {
      if (fill.visible === false) continue;
      if (fill.type === "SOLID") {
        const paint = new ck.Paint();
        paint.setAntiAlias(true);
        const c = parseColor(ck, fill.color);
        if (fill.opacity != null && fill.opacity < 1) c[3] = fill.opacity;
        paint.setColor(c);
        canvas.drawTextBlob(blob, 0, 0, paint);
        paint.delete();
      }
    }
  } else {
    const paint = new ck.Paint();
    paint.setAntiAlias(true);
    paint.setColor(ck.Color(0, 0, 0, 1));
    canvas.drawTextBlob(blob, 0, 0, paint);
    paint.delete();
  }
  blob.delete();
  font.delete();
}

// src/visual-rendering/shape-paths.ts
function starPathData(cx, cy, outerR, innerR, numPoints) {
  const step = Math.PI / numPoints;
  const parts = [];
  for (let i = 0; i < numPoints * 2; i++) {
    const r7 = i % 2 === 0 ? outerR : innerR;
    const angle = i * step - Math.PI / 2;
    const px2 = cx + r7 * Math.cos(angle);
    const py = cy + r7 * Math.sin(angle);
    parts.push(i === 0 ? `M${px2},${py}` : `L${px2},${py}`);
  }
  parts.push("Z");
  return parts.join(" ");
}
function polygonPathData(cx, cy, radius, sides) {
  const parts = [];
  for (let i = 0; i < sides; i++) {
    const angle = i * 2 * Math.PI / sides - Math.PI / 2;
    const px2 = cx + radius * Math.cos(angle);
    const py = cy + radius * Math.sin(angle);
    parts.push(i === 0 ? `M${px2},${py}` : `L${px2},${py}`);
  }
  parts.push("Z");
  return parts.join(" ");
}
function sealPathData(cx, cy, rxOuter, ryOuter, rxInner, ryInner, bumps, style) {
  const parts = [];
  if (style === "sharp") {
    const totalAnchors = bumps * 2;
    for (let i = 0; i < totalAnchors; i++) {
      const isOuter = i % 2 === 0;
      const rx = isOuter ? rxOuter : rxInner;
      const ry = isOuter ? ryOuter : ryInner;
      const angle = i * Math.PI / bumps - Math.PI / 2;
      const px2 = cx + rx * Math.cos(angle);
      const py = cy + ry * Math.sin(angle);
      parts.push(i === 0 ? `M${px2},${py}` : `L${px2},${py}`);
    }
  } else {
    const samplesPerBump = 8;
    const total = bumps * samplesPerBump;
    for (let i = 0; i < total; i++) {
      const t2 = i / total * Math.PI * 2;
      const w = (1 + Math.cos(bumps * t2 - Math.PI)) / 2;
      const rx = rxInner + (rxOuter - rxInner) * (1 - w);
      const ry = ryInner + (ryOuter - ryInner) * (1 - w);
      const angle = t2 - Math.PI / 2;
      const px2 = cx + rx * Math.cos(angle);
      const py = cy + ry * Math.sin(angle);
      parts.push(i === 0 ? `M${px2},${py}` : `L${px2},${py}`);
    }
  }
  parts.push("Z");
  return parts.join(" ");
}
function trapezoidPathData(width, height, skew) {
  const s = Math.max(-width, Math.min(width, skew));
  const topLeftX = s < 0 ? -s : 0;
  const topRightX = s < 0 ? width : width - s;
  return `M${topLeftX},0 L${topRightX},0 L${width},${height} L0,${height} Z`;
}

// src/visual-rendering/canvaskit-scene-renderer.ts
function deriveTransformFromFit(fit, slotW, slotH, imgW, imgH) {
  const scale = fit === "contain" ? Math.min(slotW / imgW, slotH / imgH) : Math.max(slotW / imgW, slotH / imgH);
  return {
    scale,
    offsetX: (slotW - imgW * scale) / 2,
    offsetY: (slotH - imgH * scale) / 2
  };
}
function rebaseTransformToBitmap(t2, imgW) {
  if (!t2.refWidth || imgW <= 0 || t2.refWidth === imgW) return t2;
  return { ...t2, scale: t2.scale * (t2.refWidth / imgW) };
}
function resolveImageTransform(node, imgW, imgH) {
  if (node.imageTransform) return rebaseTransformToBitmap(node.imageTransform, imgW);
  return deriveTransformFromFit(node.imageFit, node.width, node.height, imgW, imgH);
}
var DIAMOND_GRADIENT_SKSL = `
uniform shader ramp;
uniform float2 center;
uniform float2 invRadius;
half4 main(float2 xy) {
  float2 d = (xy - center) * invRadius;
  float t = clamp(abs(d.x) + abs(d.y), 0.0, 1.0);
  return ramp.eval(float2(t, 0.5));
}`;
var cachedDiamondCk = null;
var cachedDiamondEffect = null;
function getDiamondEffect(ck) {
  if (cachedDiamondCk !== ck) {
    cachedDiamondCk = ck;
    cachedDiamondEffect = typeof ck.RuntimeEffect?.Make === "function" ? ck.RuntimeEffect.Make(DIAMOND_GRADIENT_SKSL) : null;
  }
  return cachedDiamondEffect;
}
var NOISE_SKSL = `
uniform float cell;
uniform float density;
uniform float mode;
uniform half4 colorA;
uniform half4 colorB;
float hash21(float2 p) {
  p = fract(p * float2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
half4 premul(half4 c) { return half4(c.rgb * c.a, c.a); }
half4 main(float2 xy) {
  float2 id = floor(xy / cell);
  if (hash21(id) >= density) return half4(0.0);
  if (mode < 0.5) return premul(colorA);
  if (mode < 1.5) return premul(hash21(id + 19.19) < 0.5 ? colorA : colorB);
  return half4(half3(hash21(id + 1.7), hash21(id + 5.3), hash21(id + 9.1)), 1.0);
}`;
var cachedNoiseCk = null;
var cachedNoiseEffect = null;
function getNoiseEffect(ck) {
  if (cachedNoiseCk !== ck) {
    cachedNoiseCk = ck;
    cachedNoiseEffect = typeof ck.RuntimeEffect?.Make === "function" ? ck.RuntimeEffect.Make(NOISE_SKSL) : null;
  }
  return cachedNoiseEffect;
}
function applyFillToPaint(ck, paint, fill, nodeWidth, nodeHeight) {
  if (!fill) return;
  if (fill.type === "SOLID") {
    const color = parseColor(ck, fill.color);
    if (fill.opacity != null && fill.opacity < 1) {
      color[3] = fill.opacity;
    }
    paint.setColor(color);
  } else if (fill.type === "GRADIENT_LINEAR") {
    if (!fill.stops || fill.stops.length === 0) return;
    const { start: startPt, end: endPt } = resolveLinearGradientCoords(
      0,
      0,
      nodeWidth,
      nodeHeight,
      fill.startPoint,
      fill.endPoint,
      fill.angle
    );
    const colors = fill.stops.map((s) => parseColor(ck, s.color));
    const positions = fill.stops.map((s) => s.position);
    const shader = ck.Shader.MakeLinearGradient(
      startPt,
      endPt,
      colors,
      positions,
      ck.TileMode.Clamp
    );
    if (shader) {
      paint.setShader(shader);
      shader.delete();
    }
    if (fill.opacity != null && fill.opacity < 1) {
      paint.setAlphaf(fill.opacity);
    }
  } else if (fill.type === "GRADIENT_RADIAL") {
    if (!fill.stops || fill.stops.length === 0) return;
    const cx = fill.center?.x ?? nodeWidth / 2;
    const cy = fill.center?.y ?? nodeHeight / 2;
    const radius = fill.radius ?? Math.max(nodeWidth, nodeHeight) / 2;
    const colors = fill.stops.map((s) => parseColor(ck, s.color));
    const positions = fill.stops.map((s) => s.position);
    const shader = ck.Shader.MakeRadialGradient(
      [cx, cy],
      radius,
      colors,
      positions,
      ck.TileMode.Clamp
    );
    if (shader) {
      paint.setShader(shader);
      shader.delete();
    }
    if (fill.opacity != null && fill.opacity < 1) {
      paint.setAlphaf(fill.opacity);
    }
  } else if (fill.type === "GRADIENT_ANGULAR") {
    if (!fill.stops || fill.stops.length === 0) return;
    const cx = (fill.center?.x ?? 0.5) * nodeWidth;
    const cy = (fill.center?.y ?? 0.5) * nodeHeight;
    const colors = fill.stops.map((s) => parseColor(ck, s.color));
    const positions = fill.stops.map((s) => s.position);
    const rotation = fill.rotation ?? 0;
    const startAngle = rotation - 90;
    const endAngle = startAngle + 360;
    const shader = ck.Shader.MakeSweepGradient(
      cx,
      cy,
      colors,
      positions,
      ck.TileMode.Clamp,
      null,
      0,
      startAngle,
      endAngle
    );
    if (shader) {
      paint.setShader(shader);
      shader.delete();
    }
    if (fill.opacity != null && fill.opacity < 1) {
      paint.setAlphaf(fill.opacity);
    }
  } else if (fill.type === "GRADIENT_DIAMOND") {
    if (!fill.stops || fill.stops.length === 0) return;
    const cx = (fill.center?.x ?? 0.5) * nodeWidth;
    const cy = (fill.center?.y ?? 0.5) * nodeHeight;
    const mul = fill.radius ?? 1;
    const rx = mul * nodeWidth / 2;
    const ry = mul * nodeHeight / 2;
    const colors = fill.stops.map((s) => parseColor(ck, s.color));
    const positions = fill.stops.map((s) => s.position);
    const ramp = ck.Shader.MakeLinearGradient(
      [0, 0],
      [1, 0],
      colors,
      positions,
      ck.TileMode.Clamp
    );
    if (!ramp) return;
    const effect = getDiamondEffect(ck);
    if (effect) {
      const shader = effect.makeShaderWithChildren(
        new Float32Array([cx, cy, rx > 0 ? 1 / rx : 0, ry > 0 ? 1 / ry : 0]),
        [ramp]
      );
      if (shader) {
        paint.setShader(shader);
        shader.delete();
      }
    } else {
      paint.setShader(ramp);
    }
    ramp.delete();
    if (fill.opacity != null && fill.opacity < 1) {
      paint.setAlphaf(fill.opacity);
    }
  }
}
function applyAdjustmentsToPaint(ck, paint, adj) {
  if (!adj) return;
  const matrix = adjustmentsToColorMatrix(adj);
  const colorFilter = matrix ? ck.ColorFilter.MakeMatrix(matrix) : null;
  if (adj.blur != null && adj.blur > 0) {
    const inner = colorFilter ? ck.ImageFilter.MakeColorFilter(colorFilter, null) : null;
    const blurFilter = ck.ImageFilter.MakeBlur(adj.blur, adj.blur, ck.TileMode.Clamp, inner);
    paint.setImageFilter(blurFilter);
    blurFilter.delete();
    inner?.delete();
    colorFilter?.delete();
  } else if (colorFilter) {
    paint.setColorFilter(colorFilter);
    colorFilter.delete();
  }
}
function drawImageFill(ctx, fill, destW, destH) {
  if (fill.type !== "IMAGE" || !fill.src) return false;
  const { ck, canvas } = ctx;
  const imagePaint = fill;
  const src = imagePaint.src;
  const cached = ctx.resolveImage(src, destW, destH);
  if (!cached) {
    ctx.onImageMissing?.(src);
    return true;
  }
  const imgW = cached.width();
  const imgH = cached.height();
  const imgPaint = new ck.Paint();
  imgPaint.setAntiAlias(true);
  if (fill.opacity != null && fill.opacity < 1) imgPaint.setAlphaf(fill.opacity);
  applyAdjustmentsToPaint(ck, imgPaint, imagePaint.imageAdjustments);
  if (imagePaint.scaleMode === "TILE" && !imagePaint.imageTransform) {
    const shader = cached.makeShaderOptions(
      ck.TileMode.Repeat,
      ck.TileMode.Repeat,
      ck.FilterMode.Linear,
      ck.MipmapMode.None
    );
    imgPaint.setShader(shader);
    canvas.drawRect(ck.LTRBRect(0, 0, destW, destH), imgPaint);
    shader.delete();
  } else {
    const t2 = imagePaint.imageTransform ? rebaseTransformToBitmap(imagePaint.imageTransform, imgW) : deriveTransformFromFit(
      imagePaint.scaleMode === "FIT" ? "contain" : "cover",
      destW,
      destH,
      imgW,
      imgH
    );
    canvas.save();
    canvas.clipRect(ck.LTRBRect(0, 0, destW, destH), ck.ClipOp.Intersect, true);
    canvas.drawImageRect(
      cached,
      ck.LTRBRect(0, 0, imgW, imgH),
      ck.LTRBRect(t2.offsetX, t2.offsetY, t2.offsetX + imgW * t2.scale, t2.offsetY + imgH * t2.scale),
      imgPaint
    );
    canvas.restore();
  }
  imgPaint.delete();
  return true;
}
function buildStarPath(ck, cx, cy, outerR, innerR, numPoints) {
  return ck.Path.MakeFromSVGString(starPathData(cx, cy, outerR, innerR, numPoints));
}
function buildPolygonPath(ck, cx, cy, radius, sides) {
  return ck.Path.MakeFromSVGString(polygonPathData(cx, cy, radius, sides));
}
function buildSealPath(ck, cx, cy, rxOuter, ryOuter, rxInner, ryInner, bumps, style) {
  return ck.Path.MakeFromSVGString(
    sealPathData(cx, cy, rxOuter, ryOuter, rxInner, ryInner, bumps, style)
  );
}
function buildTrapezoidPath(ck, width, height, skew) {
  return ck.Path.MakeFromSVGString(trapezoidPathData(width, height, skew));
}
function toCornerRadius(value) {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return void 0;
  const match = value.match(/^(-?\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : void 0;
}
function makeRRect(ck, x, y, w, h, cornerRadius, cornerRadii) {
  if (cornerRadii) {
    return new Float32Array([
      x,
      y,
      x + w,
      y + h,
      cornerRadii[0],
      cornerRadii[0],
      cornerRadii[1],
      cornerRadii[1],
      cornerRadii[2],
      cornerRadii[2],
      cornerRadii[3],
      cornerRadii[3]
    ]);
  }
  return ck.RRectXY(ck.LTRBRect(x, y, x + w, y + h), cornerRadius ?? 0, cornerRadius ?? 0);
}
function strokeRRect(ck, node, w, h) {
  const align = node.strokeAlign ?? "CENTER";
  const weight = node.strokeWeight ?? 1;
  const inset = align === "INSIDE" ? weight / 2 : align === "OUTSIDE" ? -weight / 2 : 0;
  const baseRadius = toCornerRadius(node.cornerRadius);
  const adjust = (r7) => Math.max(0, r7 - inset);
  if (node.cornerRadii) {
    const r7 = node.cornerRadii.map(adjust);
    return new Float32Array([
      inset,
      inset,
      w - inset,
      h - inset,
      r7[0],
      r7[0],
      r7[1],
      r7[1],
      r7[2],
      r7[2],
      r7[3],
      r7[3]
    ]);
  }
  const radius = baseRadius != null ? adjust(baseRadius) : 0;
  return ck.RRectXY(ck.LTRBRect(inset, inset, w - inset, h - inset), radius, radius);
}
function directionToPoints(dir, w, h) {
  switch (dir) {
    case "top":
      return [0, h, 0, 0];
    case "left":
      return [w, 0, 0, 0];
    case "right":
      return [0, 0, w, 0];
    default:
      return [0, 0, 0, h];
  }
}
function renderDocumentBackground(ctx) {
  const { ck, canvas, doc } = ctx;
  if (!doc.fills?.length) return;
  const bgRect = ck.LTRBRect(0, 0, doc.width, doc.height);
  for (const fill of doc.fills) {
    if (fill.visible === false) continue;
    if (drawImageFill(ctx, fill, doc.width, doc.height)) continue;
    const fp = new ck.Paint();
    fp.setStyle(ck.PaintStyle.Fill);
    fp.setAntiAlias(false);
    applyFillToPaint(ck, fp, fill, doc.width, doc.height);
    canvas.drawRect(bgRect, fp);
    fp.delete();
  }
}
function ckBlendMode(ck, mode) {
  switch (mode) {
    case "MULTIPLY":
      return ck.BlendMode.Multiply;
    case "SCREEN":
      return ck.BlendMode.Screen;
    case "OVERLAY":
      return ck.BlendMode.Overlay;
    case "DARKEN":
      return ck.BlendMode.Darken;
    case "LIGHTEN":
      return ck.BlendMode.Lighten;
    case "COLOR_DODGE":
      return ck.BlendMode.ColorDodge;
    case "COLOR_BURN":
      return ck.BlendMode.ColorBurn;
    case "HARD_LIGHT":
      return ck.BlendMode.HardLight;
    case "SOFT_LIGHT":
      return ck.BlendMode.SoftLight;
    case "DIFFERENCE":
      return ck.BlendMode.Difference;
    case "EXCLUSION":
      return ck.BlendMode.Exclusion;
    case "HUE":
      return ck.BlendMode.Hue;
    case "SATURATION":
      return ck.BlendMode.Saturation;
    case "COLOR":
      return ck.BlendMode.Color;
    case "LUMINOSITY":
      return ck.BlendMode.Luminosity;
    default:
      return null;
  }
}
function applyFillBlend(ck, paint, fill) {
  if (!fill.blendMode || fill.blendMode === "NORMAL") return;
  const mode = ckBlendMode(ck, fill.blendMode);
  if (mode) paint.setBlendMode(mode);
}
function strokeCapEnum(ck, cap) {
  return cap === "ROUND" ? ck.StrokeCap.Round : cap === "SQUARE" ? ck.StrokeCap.Square : ck.StrokeCap.Butt;
}
function strokeJoinEnum(ck, join) {
  return join === "ROUND" ? ck.StrokeJoin.Round : join === "BEVEL" ? ck.StrokeJoin.Bevel : ck.StrokeJoin.Miter;
}
function applyVectorStrokeStyle(ck, paint, node, defaults) {
  paint.setStrokeWidth(node.strokeWeight ?? defaults.weight ?? 1);
  paint.setStrokeCap(strokeCapEnum(ck, node.strokeCap ?? defaults.cap));
  paint.setStrokeJoin(strokeJoinEnum(ck, node.strokeJoin ?? defaults.join));
  if (node.dashPattern && node.dashPattern.length >= 2) {
    const dash = ck.PathEffect.MakeDash(node.dashPattern, 0);
    if (dash) {
      paint.setPathEffect(dash);
      dash.delete();
    }
  }
}
function dropShadowFilter(ck, node) {
  const shadows = node.effects?.filter(
    (e) => e.visible !== false && e.type === "DROP_SHADOW"
  );
  if (!shadows?.length) return null;
  const makeDropShadow = (s, input) => {
    const sigma = Math.max((s.radius ?? 0) / 2, 0.4);
    const color = parseColor(ck, s.color ?? "rgba(0,0,0,0.25)");
    return ck.ImageFilter.MakeDropShadow(
      s.offsetX ?? 0,
      s.offsetY ?? 0,
      sigma,
      sigma,
      color,
      input
    );
  };
  if (shadows.length > 1 && typeof ck.ImageFilter.MakeMerge === "function") {
    const filters = shadows.map((s) => {
      const sigma = (s.radius ?? 0) / 2;
      const color = parseColor(ck, s.color ?? "rgba(0,0,0,0.25)");
      return ck.ImageFilter.MakeDropShadowOnly(
        s.offsetX ?? 0,
        s.offsetY ?? 0,
        sigma,
        sigma,
        color,
        null
      );
    });
    filters.push(null);
    return ck.ImageFilter.MakeMerge(filters, null);
  }
  let filter = null;
  for (const s of shadows) filter = makeDropShadow(s, filter);
  return filter;
}
function buildNodeSilhouettePath(ck, node, inset = 0) {
  const w = node.width;
  const h = node.height;
  if (!(w > 0) || !(h > 0)) return null;
  switch (node.type) {
    case "RECTANGLE":
    case "FRAME":
    case "GROUP":
    case "IMAGE": {
      const l = inset;
      const t2 = inset;
      const r7 = w - inset;
      const b = h - inset;
      if (r7 <= l || b <= t2) return null;
      const pb = new ck.PathBuilder();
      if (node.cornerRadii) {
        const rr = node.cornerRadii.map((c) => Math.max(0, c - inset));
        pb.addRRect(
          new Float32Array([l, t2, r7, b, rr[0], rr[0], rr[1], rr[1], rr[2], rr[2], rr[3], rr[3]])
        );
      } else {
        const rad = Math.max(0, (toCornerRadius(node.cornerRadius) ?? 0) - inset);
        pb.addRRect(ck.RRectXY(ck.LTRBRect(l, t2, r7, b), rad, rad));
      }
      return pb.detach();
    }
    case "ELLIPSE": {
      const l = inset;
      const t2 = inset;
      const r7 = w - inset;
      const b = h - inset;
      if (r7 <= l || b <= t2) return null;
      const pb = new ck.PathBuilder();
      pb.addOval(ck.LTRBRect(l, t2, r7, b));
      return pb.detach();
    }
    case "STAR": {
      const sides = node.sides ?? 5;
      const outerR = w / 2;
      const rawInner = node.innerRadius ?? 0.4;
      const innerR = rawInner <= 1 ? rawInner * outerR : rawInner;
      return buildStarPath(ck, w / 2, h / 2, outerR, innerR, sides);
    }
    case "POLYGON":
      return buildPolygonPath(ck, w / 2, h / 2, w / 2, node.sides ?? 6);
    case "SEAL": {
      const bumps = Math.max(3, Math.floor(node.sides ?? 16));
      const rxOuter = w / 2;
      const ryOuter = h / 2;
      const rawInner = node.innerRadius ?? 0.88;
      const innerRatio = rawInner <= 1 ? rawInner : rawInner / Math.max(rxOuter, ryOuter);
      return buildSealPath(
        ck,
        w / 2,
        h / 2,
        rxOuter,
        ryOuter,
        rxOuter * innerRatio,
        ryOuter * innerRatio,
        bumps,
        node.bumpStyle ?? "sharp"
      );
    }
    case "TRAPEZOID":
      return buildTrapezoidPath(ck, w, h, node.skew ?? 0);
    case "PATH": {
      if (!node.pathData) return null;
      const raw = ck.Path.MakeFromSVGString(node.pathData);
      if (!raw) return null;
      const verbatim = node.pluginData?.pathFit === "none";
      const flipX = node.pluginData?.flipX === true;
      const flipY = node.pluginData?.flipY === true;
      const bounds = raw.computeTightBounds();
      const pbW = bounds[2] - bounds[0];
      const pbH = bounds[3] - bounds[1];
      const scaleX = verbatim ? flipX ? -1 : 1 : pbW > 0 ? (flipX ? -1 : 1) * (w / pbW) : 1;
      const scaleY = verbatim ? flipY ? -1 : 1 : pbH > 0 ? (flipY ? -1 : 1) * (h / pbH) : 1;
      const fitOX = verbatim ? 0 : bounds[0];
      const fitOY = verbatim ? 0 : bounds[1];
      const tx = (flipX ? w : 0) - scaleX * fitOX;
      const ty = (flipY ? h : 0) - scaleY * fitOY;
      const pb = new ck.PathBuilder(raw);
      pb.transform([scaleX, 0, tx, 0, scaleY, ty, 0, 0, 1]);
      raw.delete();
      const fitted = pb.detach();
      const vectorPathsMeta = node.pluginData?.vectorPaths;
      if (vectorPathsMeta?.some((vp) => vp.windingRule === "evenodd")) {
        fitted.setFillType(ck.FillType.EvenOdd);
      }
      return fitted;
    }
    default:
      return null;
  }
}
function buildClipPath(ck, node) {
  if (!node.clipPath) return null;
  return ck.Path.MakeFromSVGString(node.clipPath) ?? null;
}
function drawInnerShadows(ctx, node) {
  const { ck, canvas } = ctx;
  const shadows = node.effects?.filter(
    (e) => e.visible !== false && e.type === "INNER_SHADOW"
  );
  if (!shadows?.length) return;
  const base = buildNodeSilhouettePath(ck, node, 0);
  if (!base) return;
  for (const s of shadows) {
    const spread = s.spread ?? 0;
    const cut = spread !== 0 ? buildNodeSilhouettePath(ck, node, spread) ?? base : base;
    canvas.save();
    canvas.clipPath(base, ck.ClipOp.Intersect, true);
    canvas.saveLayer(null);
    const fillPaint = new ck.Paint();
    fillPaint.setStyle(ck.PaintStyle.Fill);
    fillPaint.setAntiAlias(true);
    fillPaint.setColor(parseColor(ck, s.color ?? "rgba(0,0,0,0.25)"));
    canvas.drawPath(base, fillPaint);
    fillPaint.delete();
    const carvePaint = new ck.Paint();
    carvePaint.setStyle(ck.PaintStyle.Fill);
    carvePaint.setAntiAlias(true);
    carvePaint.setColor(ck.Color(0, 0, 0, 1));
    carvePaint.setBlendMode(ck.BlendMode.DstOut);
    const sigma = (s.radius ?? 0) / 2;
    if (sigma > 0) {
      const blur = ck.ImageFilter.MakeBlur(sigma, sigma, ck.TileMode.Decal, null);
      carvePaint.setImageFilter(blur);
      blur.delete();
    }
    canvas.save();
    canvas.translate(s.offsetX ?? 0, s.offsetY ?? 0);
    canvas.drawPath(cut, carvePaint);
    canvas.restore();
    carvePaint.delete();
    canvas.restore();
    canvas.restore();
    if (cut !== base) cut.delete();
  }
  base.delete();
}
function drawNoiseEffects(ctx, node) {
  const { ck, canvas } = ctx;
  const noises = node.effects?.filter(
    (e) => e.visible !== false && e.type === "NOISE"
  );
  if (!noises?.length) return;
  const effect = getNoiseEffect(ck);
  if (!effect) return;
  const silhouette = buildNodeSilhouettePath(ck, node, 0);
  for (const n of noises) {
    const cell = Math.max(1, n.size ?? 2);
    const density = Math.min(1, Math.max(0, n.density ?? 0.5));
    const opacity = Math.min(1, Math.max(0, n.opacity ?? 1));
    if (density <= 0 || opacity <= 0) continue;
    const mode = n.noiseType === "MULTI" ? 2 : n.noiseType === "DUO" ? 1 : 0;
    const colorA = parseColor(ck, n.color ?? "#000000");
    const colorB = parseColor(ck, n.secondaryColor ?? n.color ?? "#ffffff");
    const shader = effect.makeShader(
      new Float32Array([
        cell,
        density,
        mode,
        colorA[0],
        colorA[1],
        colorA[2],
        colorA[3],
        colorB[0],
        colorB[1],
        colorB[2],
        colorB[3]
      ])
    );
    if (!shader) continue;
    const paint = new ck.Paint();
    paint.setShader(shader);
    if (opacity < 1) paint.setAlphaf(opacity);
    canvas.save();
    if (silhouette) {
      canvas.clipPath(silhouette, ck.ClipOp.Intersect, true);
    } else {
      canvas.clipRect(ck.LTRBRect(0, 0, node.width, node.height), ck.ClipOp.Intersect, true);
    }
    canvas.drawRect(ck.LTRBRect(0, 0, node.width, node.height), paint);
    canvas.restore();
    shader.delete();
    paint.delete();
  }
  silhouette?.delete();
}
var PROGRESSIVE_BLUR_BANDS = 4;
function renderProgressiveBlur(ctx, node) {
  const { ck, canvas } = ctx;
  const pb = node.blurProgressive;
  const base = { ...node, blurProgressive: void 0, blur: void 0 };
  const ax = node.x + pb.startPosition.x * node.width;
  const ay = node.y + pb.startPosition.y * node.height;
  const bx = node.x + pb.endPosition.x * node.width;
  const by = node.y + pb.endPosition.y * node.height;
  const pad = Math.ceil(Math.max(pb.startRadius, pb.endRadius) * 3) + 2;
  const maskRect = ck.LTRBRect(
    node.x - pad,
    node.y - pad,
    node.x + node.width + pad,
    node.y + node.height + pad
  );
  const bands = PROGRESSIVE_BLUR_BANDS;
  for (let i = 0; i < bands; i++) {
    const t2 = i / (bands - 1);
    const sigma = pb.startRadius + (pb.endRadius - pb.startRadius) * t2;
    canvas.saveLayer(null);
    if (sigma > 0) {
      const blurFilter = ck.ImageFilter.MakeBlur(sigma, sigma, ck.TileMode.Decal, null);
      const blurPaint = new ck.Paint();
      blurPaint.setImageFilter(blurFilter);
      canvas.saveLayer(blurPaint);
      renderNode(ctx, base);
      canvas.restore();
      blurFilter.delete();
      blurPaint.delete();
    } else {
      renderNode(ctx, base);
    }
    if (i > 0) {
      const p0 = (i - 1) / (bands - 1);
      const p1 = i / (bands - 1);
      const grad = ck.Shader.MakeLinearGradient(
        [ax + (bx - ax) * p0, ay + (by - ay) * p0],
        [ax + (bx - ax) * p1, ay + (by - ay) * p1],
        [ck.Color(0, 0, 0, 0), ck.Color(0, 0, 0, 1)],
        [0, 1],
        ck.TileMode.Clamp
      );
      if (grad) {
        const maskPaint = new ck.Paint();
        maskPaint.setBlendMode(ck.BlendMode.DstIn);
        maskPaint.setShader(grad);
        canvas.drawRect(maskRect, maskPaint);
        grad.delete();
        maskPaint.delete();
      }
    }
    canvas.restore();
  }
}
function renderNode(ctx, node) {
  const { ck, canvas, doc } = ctx;
  if (node.visible === false) return;
  if (node.slotRole === "link-zone") return;
  if (node.type === "LINK_ZONE_GROUP" || node.type === "LINK_ZONE") return;
  if (ctx.editingTextId && node.id === ctx.editingTextId) return;
  const pb = node.blurProgressive;
  if (pb && (pb.startRadius > 0 || pb.endRadius > 0)) {
    renderProgressiveBlur(ctx, node);
    return;
  }
  canvas.save();
  canvas.translate(node.x, node.y);
  if (node.rotation) {
    canvas.translate(node.width / 2, node.height / 2);
    canvas.rotate(node.rotation, 0, 0);
    canvas.translate(-node.width / 2, -node.height / 2);
  }
  const hasBackdropBlur = node.backdropBlur != null && node.backdropBlur > 0;
  if (hasBackdropBlur) {
    canvas.save();
    const cornerRadius = toCornerRadius(node.cornerRadius);
    const hasRadius = !!(cornerRadius || node.cornerRadii);
    if (hasRadius) {
      const clipRRect = makeRRect(ck, 0, 0, node.width, node.height, cornerRadius, node.cornerRadii);
      canvas.clipRRect(clipRRect, ck.ClipOp.Intersect, true);
    } else {
      canvas.clipRect(ck.LTRBRect(0, 0, node.width, node.height), ck.ClipOp.Intersect, true);
    }
    const sigma = node.backdropBlur;
    const filter = ck.ImageFilter.MakeBlur(sigma, sigma, ck.TileMode.Clamp, null);
    const bounds = ck.LTRBRect(0, 0, node.width, node.height);
    canvas.saveLayer(null, bounds, filter, 0);
    filter.delete();
  }
  const blend = node.blendMode && node.blendMode !== "NORMAL" ? ckBlendMode(ck, node.blendMode) : null;
  if (blend) {
    const blendPaint = new ck.Paint();
    blendPaint.setBlendMode(blend);
    canvas.saveLayer(blendPaint);
    blendPaint.delete();
  }
  if (node.opacity != null && node.opacity < 1) {
    const layerPaint = new ck.Paint();
    layerPaint.setAlphaf(node.opacity);
    canvas.saveLayer(layerPaint);
    layerPaint.delete();
  }
  if (node.blur != null && node.blur > 0) {
    const sigma = node.blur;
    const blurFilter = ck.ImageFilter.MakeBlur(sigma, sigma, ck.TileMode.Decal, null);
    const blurPaint = new ck.Paint();
    blurPaint.setImageFilter(blurFilter);
    canvas.saveLayer(blurPaint);
    blurFilter.delete();
    blurPaint.delete();
  }
  const shadowFilter = dropShadowFilter(ck, node);
  if (shadowFilter) {
    const shadowPaint = new ck.Paint();
    shadowPaint.setImageFilter(shadowFilter);
    canvas.saveLayer(shadowPaint);
    shadowFilter.delete();
    shadowPaint.delete();
  }
  if (node.mask) {
    canvas.saveLayer(null);
  }
  switch (node.type) {
    case "RECTANGLE": {
      if (node.fills?.length) {
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          const cornerRadius = toCornerRadius(node.cornerRadius);
          const hasRadius = !!(cornerRadius || node.cornerRadii);
          if (hasRadius) canvas.save();
          if (hasRadius) {
            const clipRRect = makeRRect(ck, 0, 0, node.width, node.height, cornerRadius, node.cornerRadii);
            canvas.clipRRect(clipRRect, ck.ClipOp.Intersect, true);
          }
          if (drawImageFill(ctx, fill, node.width, node.height)) {
            if (hasRadius) canvas.restore();
            continue;
          }
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          applyFillBlend(ck, paint, fill);
          canvas.drawRect(ck.LTRBRect(0, 0, node.width, node.height), paint);
          paint.delete();
          if (hasRadius) canvas.restore();
        }
      }
      if (node.strokes?.length) {
        const hasRadius = !!(toCornerRadius(node.cornerRadius) || node.cornerRadii);
        const strokeShape = strokeRRect(ck, node, node.width, node.height);
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setStrokeWidth(node.strokeWeight ?? 1);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          if (hasRadius) {
            canvas.drawRRect(strokeShape, paint);
          } else {
            const align = node.strokeAlign ?? "CENTER";
            const w = node.strokeWeight ?? 1;
            const inset = align === "INSIDE" ? w / 2 : align === "OUTSIDE" ? -w / 2 : 0;
            canvas.drawRect(ck.LTRBRect(inset, inset, node.width - inset, node.height - inset), paint);
          }
          paint.delete();
        }
      }
      break;
    }
    case "ELLIPSE": {
      const oval = ck.LTRBRect(0, 0, node.width, node.height);
      if (node.fills?.length) {
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          if (fill.type === "IMAGE") {
            canvas.save();
            const ovalClip = ck.RRectXY(oval, node.width / 2, node.height / 2);
            canvas.clipRRect(ovalClip, ck.ClipOp.Intersect, true);
            drawImageFill(ctx, fill, node.width, node.height);
            canvas.restore();
            continue;
          }
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          canvas.drawOval(oval, paint);
          paint.delete();
        }
      }
      if (node.strokes?.length) {
        const align = node.strokeAlign ?? "CENTER";
        const w = node.strokeWeight ?? 1;
        const inset = align === "INSIDE" ? w / 2 : align === "OUTSIDE" ? -w / 2 : 0;
        const strokeOval = ck.LTRBRect(inset, inset, node.width - inset, node.height - inset);
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setStrokeWidth(node.strokeWeight ?? 1);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          canvas.drawOval(strokeOval, paint);
          paint.delete();
        }
      }
      break;
    }
    case "TEXT": {
      if (!node.text) break;
      const warpConfig = node.pluginData?.textWarp;
      if (warpConfig && warpConfig.type !== "none" && Math.abs(warpConfig.bend) >= 0.5) {
        renderWarpedText(ck, canvas, ctx.fontMgr, node, warpConfig);
        break;
      }
      const solidFill = node.fills?.find((f) => f.type === "SOLID");
      const fillColor = solidFill ? parseColor(ck, solidFill.color) : ck.Color(0, 0, 0, 1);
      const gradientFills = (node.fills ?? []).filter(
        (f) => f.visible !== false && (f.type === "GRADIENT_LINEAR" || f.type === "GRADIENT_RADIAL" || f.type === "GRADIENT_ANGULAR" || f.type === "GRADIENT_DIAMOND")
      );
      const drawBaseFill = gradientFills.length === 0 || !!solidFill;
      const baseFontSize = node.fontSize ?? 16;
      const layoutWidth = node.width > 0 ? node.width : 1e4;
      const MIN_FONT_SIZE = 8;
      const paragraphSpacing = node.paragraphSpacing ?? 0;
      if (paragraphSpacing > 0 && node.text.includes("\n")) {
        const allSpans = getRichSpans(node.pluginData, node.text.length);
        const chunks = node.text.split("\n");
        const starts = [];
        for (let i = 0, b = 0; i < chunks.length; i++) {
          starts.push(b);
          b += chunks[i].length + 1;
        }
        const chunkStyle = (i) => ({
          text: chunks[i],
          fontSize: node.fontSize,
          fontFamily: node.fontFamily,
          fontWeight: node.fontWeight,
          fontStyle: node.fontStyle,
          lineHeight: node.lineHeight,
          letterSpacing: node.letterSpacing,
          textAlign: node.textAlign,
          textTransform: node.textTransform,
          spans: allSpans.length ? spansForLine(allSpans, starts[i], chunks[i].length) : []
        });
        const buildAll = (fontSize) => chunks.map(
          (_c, i) => buildTextParagraph(ck, ctx.fontMgr, chunkStyle(i), fontSize, layoutWidth, fillColor)
        );
        const stackedHeight = (ps) => ps.reduce((h, p) => h + p.getHeight(), 0) + paragraphSpacing * (ps.length - 1);
        let paras = buildAll(baseFontSize);
        let usedFontSize2 = baseFontSize;
        if (node.height > 0 && stackedHeight(paras) > node.height && baseFontSize > MIN_FONT_SIZE) {
          let fontSize = baseFontSize - 2;
          while (fontSize > MIN_FONT_SIZE) {
            paras.forEach((p) => p.delete());
            paras = buildAll(fontSize);
            usedFontSize2 = fontSize;
            if (stackedHeight(paras) <= node.height) break;
            fontSize -= 2;
          }
        }
        const total = stackedHeight(paras);
        let y = 0;
        if (node.verticalAlign === "middle") y = Math.max(0, (node.height - total) / 2);
        else if (node.verticalAlign === "bottom") y = Math.max(0, node.height - total);
        const strokeDef = (() => {
          const s = node.strokes?.find((st) => st.visible !== false && st.type === "SOLID");
          if (!s || !node.strokeWeight) return null;
          const align = node.strokeAlign ?? "OUTSIDE";
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setAntiAlias(true);
          paint.setStrokeJoin(ck.StrokeJoin.Round);
          paint.setStrokeWidth(node.strokeWeight * (align === "CENTER" ? 1 : 2));
          paint.setColor(parseColor(ck, s.color));
          return { paint, align };
        })();
        for (let i = 0; i < paras.length; i++) {
          const p = paras[i];
          const drawStroke = () => {
            if (!strokeDef) return;
            const sp = buildTextParagraph(
              ck,
              ctx.fontMgr,
              chunkStyle(i),
              usedFontSize2,
              layoutWidth,
              fillColor,
              strokeDef.paint
            );
            canvas.drawParagraph(sp, 0, y);
            sp.delete();
          };
          if (strokeDef && strokeDef.align !== "INSIDE") drawStroke();
          canvas.drawParagraph(p, 0, y);
          if (strokeDef && strokeDef.align === "INSIDE") drawStroke();
          y += p.getHeight() + paragraphSpacing;
          p.delete();
        }
        strokeDef?.paint.delete();
        break;
      }
      const textStyle = {
        text: node.text,
        fontSize: node.fontSize,
        fontFamily: node.fontFamily,
        fontWeight: node.fontWeight,
        fontStyle: node.fontStyle,
        lineHeight: node.lineHeight,
        letterSpacing: node.letterSpacing,
        textAlign: node.textAlign,
        textTransform: node.textTransform,
        spans: getRichSpans(node.pluginData, node.text.length),
        list: getRichList(node.pluginData) ?? void 0
      };
      const buildParagraph = (fontSize) => buildTextParagraph(ck, ctx.fontMgr, textStyle, fontSize, layoutWidth, fillColor);
      let paragraph = buildParagraph(baseFontSize);
      let usedFontSize = baseFontSize;
      const autoResizesToContent = node.textAutoResize === "AUTO_HEIGHT" || node.textAutoResize === "AUTO_WIDTH";
      if (!autoResizesToContent && node.height > 0 && paragraph.getHeight() > node.height && baseFontSize > MIN_FONT_SIZE) {
        let fontSize = baseFontSize - 2;
        while (fontSize > MIN_FONT_SIZE) {
          paragraph.delete();
          paragraph = buildParagraph(fontSize);
          usedFontSize = fontSize;
          if (paragraph.getHeight() <= node.height) break;
          fontSize -= 2;
        }
        if (paragraph.getHeight() <= node.height && fontSize + 1 < baseFontSize) {
          const tryP = buildParagraph(fontSize + 1);
          if (tryP.getHeight() <= node.height) {
            paragraph.delete();
            paragraph = tryP;
            usedFontSize = fontSize + 1;
          } else {
            tryP.delete();
          }
        }
      }
      let yOffset = 0;
      if (node.verticalAlign === "middle") {
        yOffset = Math.max(0, (node.height - paragraph.getHeight()) / 2);
      } else if (node.verticalAlign === "bottom") {
        yOffset = Math.max(0, node.height - paragraph.getHeight());
      }
      const strokePaintDef = (() => {
        const s = node.strokes?.find((st) => st.visible !== false && st.type === "SOLID");
        if (!s || !node.strokeWeight) return null;
        const align = node.strokeAlign ?? "OUTSIDE";
        const paint = new ck.Paint();
        paint.setStyle(ck.PaintStyle.Stroke);
        paint.setAntiAlias(true);
        paint.setStrokeJoin(ck.StrokeJoin.Round);
        paint.setStrokeWidth(node.strokeWeight * (align === "CENTER" ? 1 : 2));
        paint.setColor(parseColor(ck, s.color));
        return { paint, align };
      })();
      const drawStrokePass = () => {
        if (!strokePaintDef) return;
        const sp = buildTextParagraph(
          ck,
          ctx.fontMgr,
          textStyle,
          usedFontSize,
          layoutWidth,
          fillColor,
          strokePaintDef.paint
        );
        canvas.drawParagraph(sp, 0, yOffset);
        sp.delete();
      };
      const drawGradientPasses = () => {
        for (const gf of gradientFills) {
          const gPaint = new ck.Paint();
          gPaint.setAntiAlias(true);
          applyFillToPaint(ck, gPaint, gf, node.width, node.height);
          const gp = buildTextParagraph(
            ck,
            ctx.fontMgr,
            textStyle,
            usedFontSize,
            layoutWidth,
            fillColor,
            gPaint
          );
          canvas.drawParagraph(gp, 0, yOffset);
          gp.delete();
          gPaint.delete();
        }
      };
      if (strokePaintDef && strokePaintDef.align !== "INSIDE") drawStrokePass();
      if (drawBaseFill) canvas.drawParagraph(paragraph, 0, yOffset);
      drawGradientPasses();
      if (strokePaintDef && strokePaintDef.align === "INSIDE") drawStrokePass();
      strokePaintDef?.paint.delete();
      paragraph.delete();
      break;
    }
    case "IMAGE": {
      const rawSrc = node.imageSrc;
      if (!rawSrc) {
        const paint = new ck.Paint();
        paint.setColor(ck.Color(200, 200, 200, 1));
        canvas.drawRect(ck.LTRBRect(0, 0, node.width, node.height), paint);
        paint.delete();
        break;
      }
      const cached = ctx.resolveImage(rawSrc, node.width, node.height);
      if (cached && node.tileWidth && node.tileHeight) {
        const imgW = cached.width();
        const imgH = cached.height();
        const sx = node.tileWidth / imgW;
        const sy = node.tileHeight / imgH;
        const localMatrix = [sx, 0, 0, 0, sy, 0, 0, 0, 1];
        const shader = cached.makeShaderOptions(
          ck.TileMode.Repeat,
          ck.TileMode.Repeat,
          ck.FilterMode.Linear,
          ck.MipmapMode.None,
          localMatrix
        );
        const paint = new ck.Paint();
        paint.setAntiAlias(true);
        paint.setShader(shader);
        applyAdjustmentsToPaint(ck, paint, node.imageAdjustments);
        const tileCr = toCornerRadius(node.cornerRadius);
        const tileHasRadius = !!(tileCr || node.cornerRadii);
        if (tileHasRadius) {
          canvas.save();
          canvas.clipRRect(
            makeRRect(ck, 0, 0, node.width, node.height, tileCr, node.cornerRadii),
            ck.ClipOp.Intersect,
            true
          );
        }
        canvas.drawRect(ck.LTRBRect(0, 0, node.width, node.height), paint);
        if (tileHasRadius) canvas.restore();
        shader.delete();
        paint.delete();
      } else if (cached) {
        const paint = new ck.Paint();
        paint.setAntiAlias(true);
        applyAdjustmentsToPaint(ck, paint, node.imageAdjustments);
        const imgW = cached.width();
        const imgH = cached.height();
        const slotW = node.width;
        const slotH = node.height;
        const { scale, offsetX, offsetY } = resolveImageTransform(node, imgW, imgH);
        const drawW = imgW * scale;
        const drawH = imgH * scale;
        canvas.save();
        const imgCr = toCornerRadius(node.cornerRadius);
        if (imgCr || node.cornerRadii) {
          canvas.clipRRect(
            makeRRect(ck, 0, 0, slotW, slotH, imgCr, node.cornerRadii),
            ck.ClipOp.Intersect,
            true
          );
        } else {
          canvas.clipRect(ck.LTRBRect(0, 0, slotW, slotH), ck.ClipOp.Intersect, true);
        }
        canvas.drawImageRect(
          cached,
          ck.LTRBRect(0, 0, imgW, imgH),
          ck.LTRBRect(offsetX, offsetY, offsetX + drawW, offsetY + drawH),
          paint
        );
        canvas.restore();
        paint.delete();
        const overlayFills = node.fills?.length ? node.fills : null;
        if (overlayFills) {
          for (const fill of overlayFills) {
            if (fill.visible === false) continue;
            const fillPaint = new ck.Paint();
            fillPaint.setStyle(ck.PaintStyle.Fill);
            fillPaint.setAntiAlias(true);
            applyFillToPaint(ck, fillPaint, fill, slotW, slotH);
            applyFillBlend(ck, fillPaint, fill);
            canvas.drawRect(ck.LTRBRect(0, 0, slotW, slotH), fillPaint);
            fillPaint.delete();
          }
        } else if (node.imageScrim) {
          const scrim = node.imageScrim;
          const scrimPaint = new ck.Paint();
          scrimPaint.setAntiAlias(true);
          if (scrim.gradient) {
            const dir = scrim.gradientDirection || "bottom";
            const transparent = parseColor(ck, scrim.color);
            transparent[3] = 0;
            const solid = parseColor(ck, scrim.color);
            solid[3] = scrim.opacity;
            const [sx, sy, ex, ey] = directionToPoints(dir, slotW, slotH);
            const shader = ck.Shader.MakeLinearGradient(
              [sx, sy],
              [ex, ey],
              [transparent, solid],
              [0, 1],
              ck.TileMode.Clamp
            );
            if (shader) {
              scrimPaint.setShader(shader);
              shader.delete();
            }
          } else {
            const c = parseColor(ck, scrim.color);
            c[3] = scrim.opacity;
            scrimPaint.setColor(c);
          }
          canvas.drawRect(ck.LTRBRect(0, 0, slotW, slotH), scrimPaint);
          scrimPaint.delete();
        }
      } else {
        ctx.onImageMissing?.(rawSrc);
      }
      if (node.strokes?.length) {
        const hasRadius = !!(toCornerRadius(node.cornerRadius) || node.cornerRadii);
        const strokeShape = strokeRRect(ck, node, node.width, node.height);
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setStrokeWidth(node.strokeWeight ?? 1);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          if (hasRadius) {
            canvas.drawRRect(strokeShape, paint);
          } else {
            const align = node.strokeAlign ?? "CENTER";
            const w = node.strokeWeight ?? 1;
            const inset = align === "INSIDE" ? w / 2 : align === "OUTSIDE" ? -w / 2 : 0;
            canvas.drawRect(ck.LTRBRect(inset, inset, node.width - inset, node.height - inset), paint);
          }
          paint.delete();
        }
      }
      break;
    }
    case "PATH": {
      if (!node.pathData) break;
      const path3 = ck.Path.MakeFromSVGString(node.pathData);
      if (!path3) break;
      const vectorPathsMeta = node.pluginData?.vectorPaths;
      if (vectorPathsMeta?.some((vp) => vp.windingRule === "evenodd")) {
        path3.setFillType(ck.FillType.EvenOdd);
      }
      const pathBounds2 = path3.computeTightBounds();
      const pbW = pathBounds2[2] - pathBounds2[0];
      const pbH = pathBounds2[3] - pathBounds2[1];
      const flipX = node.pluginData?.flipX === true;
      const flipY = node.pluginData?.flipY === true;
      const verbatim = node.pluginData?.pathFit === "none";
      const scaleX = verbatim ? flipX ? -1 : 1 : pbW > 0 ? (flipX ? -1 : 1) * (node.width / pbW) : 1;
      const scaleY = verbatim ? flipY ? -1 : 1 : pbH > 0 ? (flipY ? -1 : 1) * (node.height / pbH) : 1;
      const fitOX = verbatim ? 0 : pathBounds2[0];
      const fitOY = verbatim ? 0 : pathBounds2[1];
      if (node.fills?.length) {
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          if (fill.type === "IMAGE") {
            canvas.save();
            if (flipX) canvas.translate(node.width, 0);
            if (flipY) canvas.translate(0, node.height);
            canvas.scale(scaleX, scaleY);
            canvas.translate(-fitOX, -fitOY);
            canvas.clipPath(path3, ck.ClipOp.Intersect, true);
            canvas.translate(fitOX, fitOY);
            canvas.scale(1 / scaleX, 1 / scaleY);
            if (flipX) canvas.translate(-node.width, 0);
            if (flipY) canvas.translate(0, -node.height);
            drawImageFill(ctx, fill, node.width, node.height);
            canvas.restore();
            continue;
          }
          canvas.save();
          if (flipX) canvas.translate(node.width, 0);
          if (flipY) canvas.translate(0, node.height);
          canvas.scale(scaleX, scaleY);
          canvas.translate(-fitOX, -fitOY);
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          canvas.drawPath(path3, paint);
          paint.delete();
          canvas.restore();
        }
      }
      if (node.strokes?.length) {
        canvas.save();
        if (flipX) canvas.translate(node.width, 0);
        if (flipY) canvas.translate(0, node.height);
        canvas.scale(scaleX, scaleY);
        canvas.translate(-fitOX, -fitOY);
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          applyVectorStrokeStyle(ck, paint, node, { cap: "ROUND", join: "ROUND" });
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          canvas.drawPath(path3, paint);
          paint.delete();
        }
        canvas.restore();
      }
      path3.delete();
      break;
    }
    case "LINE": {
      if (!node.points || node.points.length < 4) break;
      const lineBuilder = new ck.PathBuilder();
      lineBuilder.moveTo(node.points[0], node.points[1]);
      for (let i = 2; i < node.points.length; i += 2) {
        lineBuilder.lineTo(node.points[i], node.points[i + 1]);
      }
      const path3 = lineBuilder.detach();
      const paint = new ck.Paint();
      paint.setStyle(ck.PaintStyle.Stroke);
      applyVectorStrokeStyle(ck, paint, node, { weight: 2 });
      paint.setAntiAlias(true);
      const linePaint = node.strokes?.find((s) => s.visible !== false && s.type !== "IMAGE") ?? node.fills?.find((f) => f.visible !== false && f.type !== "IMAGE");
      if (linePaint) {
        applyFillToPaint(ck, paint, linePaint, node.width, node.height);
      } else {
        paint.setColor(ck.Color(0, 0, 0, 1));
      }
      canvas.drawPath(path3, paint);
      path3.delete();
      paint.delete();
      break;
    }
    case "STAR": {
      const sides = node.sides ?? 5;
      const outerR = node.width / 2;
      const rawInner = node.innerRadius ?? 0.4;
      const innerR = rawInner <= 1 ? rawInner * outerR : rawInner;
      const cx = node.width / 2;
      const cy = node.height / 2;
      const starPath = buildStarPath(ck, cx, cy, outerR, innerR, sides);
      if (!starPath) break;
      if (node.fills?.length) {
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          if (fill.type === "IMAGE") {
            canvas.save();
            canvas.clipPath(starPath, ck.ClipOp.Intersect, true);
            drawImageFill(ctx, fill, node.width, node.height);
            canvas.restore();
            continue;
          }
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          canvas.drawPath(starPath, paint);
          paint.delete();
        }
      }
      if (node.strokes?.length) {
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setStrokeWidth(node.strokeWeight ?? 1);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          canvas.drawPath(starPath, paint);
          paint.delete();
        }
      }
      starPath.delete();
      break;
    }
    case "POLYGON": {
      const polyS = node.sides ?? 6;
      const polyR = node.width / 2;
      const polyCx = node.width / 2;
      const polyCy = node.height / 2;
      const polyPath = buildPolygonPath(ck, polyCx, polyCy, polyR, polyS);
      if (!polyPath) break;
      if (node.fills?.length) {
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          if (fill.type === "IMAGE") {
            canvas.save();
            canvas.clipPath(polyPath, ck.ClipOp.Intersect, true);
            drawImageFill(ctx, fill, node.width, node.height);
            canvas.restore();
            continue;
          }
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          canvas.drawPath(polyPath, paint);
          paint.delete();
        }
      }
      if (node.strokes?.length) {
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setStrokeWidth(node.strokeWeight ?? 1);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          canvas.drawPath(polyPath, paint);
          paint.delete();
        }
      }
      polyPath.delete();
      break;
    }
    case "TRAPEZOID": {
      const trapPath = buildTrapezoidPath(ck, node.width, node.height, node.skew ?? 0);
      if (!trapPath) break;
      if (node.fills?.length) {
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          if (fill.type === "IMAGE") {
            canvas.save();
            canvas.clipPath(trapPath, ck.ClipOp.Intersect, true);
            drawImageFill(ctx, fill, node.width, node.height);
            canvas.restore();
            continue;
          }
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          canvas.drawPath(trapPath, paint);
          paint.delete();
        }
      }
      if (node.strokes?.length) {
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setStrokeWidth(node.strokeWeight ?? 1);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          canvas.drawPath(trapPath, paint);
          paint.delete();
        }
      }
      trapPath.delete();
      break;
    }
    case "SEAL": {
      const bumps = Math.max(3, Math.floor(node.sides ?? 16));
      const rxOuter = node.width / 2;
      const ryOuter = node.height / 2;
      const innerRatio = (() => {
        const r7 = node.innerRadius ?? 0.88;
        return r7 <= 1 ? r7 : r7 / Math.max(rxOuter, ryOuter);
      })();
      const rxInner = rxOuter * innerRatio;
      const ryInner = ryOuter * innerRatio;
      const cx = node.width / 2;
      const cy = node.height / 2;
      const sealPath = buildSealPath(
        ck,
        cx,
        cy,
        rxOuter,
        ryOuter,
        rxInner,
        ryInner,
        bumps,
        node.bumpStyle ?? "sharp"
      );
      if (!sealPath) break;
      if (node.fills?.length) {
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          if (fill.type === "IMAGE") {
            canvas.save();
            canvas.clipPath(sealPath, ck.ClipOp.Intersect, true);
            drawImageFill(ctx, fill, node.width, node.height);
            canvas.restore();
            continue;
          }
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          canvas.drawPath(sealPath, paint);
          paint.delete();
        }
      }
      if (node.strokes?.length) {
        for (const stroke of node.strokes) {
          if (stroke.visible === false) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Stroke);
          paint.setStrokeWidth(node.strokeWeight ?? 1);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, stroke, node.width, node.height);
          canvas.drawPath(sealPath, paint);
          paint.delete();
        }
      }
      sealPath.delete();
      break;
    }
    case "GROUP":
    case "FRAME": {
      const frameCornerRadius = toCornerRadius(node.cornerRadius);
      const clipPath = buildClipPath(ck, node);
      const needsClipSave = !!node.clipContent || !!clipPath;
      if (needsClipSave) {
        canvas.save();
        if (node.clipContent) {
          if (frameCornerRadius || node.cornerRadii) {
            const rrect = makeRRect(ck, 0, 0, node.width, node.height, frameCornerRadius, node.cornerRadii);
            canvas.clipRRect(rrect, ck.ClipOp.Intersect, true);
          } else {
            canvas.clipRect(ck.LTRBRect(0, 0, node.width, node.height), ck.ClipOp.Intersect, true);
          }
        }
        if (clipPath) {
          canvas.clipPath(clipPath, ck.ClipOp.Intersect, true);
          clipPath.delete();
        }
      }
      if (node.fills?.length) {
        const hasFrameRadius = !!(frameCornerRadius || node.cornerRadii);
        for (const fill of node.fills) {
          if (fill.visible === false) continue;
          if (drawImageFill(ctx, fill, node.width, node.height)) continue;
          const paint = new ck.Paint();
          paint.setStyle(ck.PaintStyle.Fill);
          paint.setAntiAlias(true);
          applyFillToPaint(ck, paint, fill, node.width, node.height);
          if (hasFrameRadius) {
            const rrect = makeRRect(ck, 0, 0, node.width, node.height, frameCornerRadius, node.cornerRadii);
            canvas.drawRRect(rrect, paint);
          } else {
            canvas.drawRect(ck.LTRBRect(0, 0, node.width, node.height), paint);
          }
          paint.delete();
        }
      }
      if (node.children) {
        for (const childId of node.children) {
          const child = doc.nodes[childId];
          if (child) {
            renderNode(ctx, child);
          }
        }
      }
      if (needsClipSave) {
        canvas.restore();
      }
      break;
    }
  }
  drawInnerShadows(ctx, node);
  drawNoiseEffects(ctx, node);
  if (node.mask) {
    const maskPaint = new ck.Paint();
    maskPaint.setBlendMode(ck.BlendMode.DstIn);
    maskPaint.setAntiAlias(true);
    if (node.mask.type === "IMAGE" && node.mask.src) {
      const maskImage = ctx.resolveImage(node.mask.src, node.width, node.height);
      if (maskImage) {
        const t2 = node.mask.imageTransform ? rebaseTransformToBitmap(node.mask.imageTransform, maskImage.width()) : deriveTransformFromFit(
          node.mask.scaleMode === "FIT" ? "contain" : "cover",
          node.width,
          node.height,
          maskImage.width(),
          maskImage.height()
        );
        const localMatrix = [t2.scale, 0, t2.offsetX, 0, t2.scale, t2.offsetY, 0, 0, 1];
        const shader = maskImage.makeShaderOptions(
          ck.TileMode.Decal,
          ck.TileMode.Decal,
          ck.FilterMode.Linear,
          ck.MipmapMode.None,
          localMatrix
        );
        maskPaint.setShader(shader);
        canvas.drawRect(ck.LTRBRect(0, 0, node.width, node.height), maskPaint);
        shader.delete();
      } else {
        ctx.onImageMissing?.(node.mask.src);
      }
    } else {
      applyFillToPaint(ck, maskPaint, node.mask, node.width, node.height);
      canvas.drawRect(ck.LTRBRect(0, 0, node.width, node.height), maskPaint);
    }
    maskPaint.delete();
    canvas.restore();
  }
  if (shadowFilter) {
    canvas.restore();
  }
  if (node.blur != null && node.blur > 0) {
    canvas.restore();
  }
  if (node.opacity != null && node.opacity < 1) {
    canvas.restore();
  }
  if (blend) {
    canvas.restore();
  }
  if (hasBackdropBlur) {
    canvas.restore();
    canvas.restore();
  }
  canvas.restore();
}

// src/canvas-patterns/generators/_helpers/rng.ts
import seedrandom from "seedrandom";
function seededRng(seed) {
  const rng = seedrandom(seed);
  return {
    next: () => rng(),
    nextBetween: (min, max) => min + (max - min) * rng(),
    nextInt: (min, max) => Math.floor(min + (max - min) * rng()),
    pick: (arr) => arr.length === 0 ? void 0 : arr[Math.floor(rng() * arr.length)],
    shuffle: (arr) => {
      const out = [...arr];
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    }
  };
}

// src/canvas-patterns/generators/_helpers/simplex.ts
import { createNoise2D } from "simplex-noise";
import seedrandom2 from "seedrandom";
function seededNoise2d(seed) {
  const rng = seedrandom2(seed);
  return createNoise2D(rng);
}

// src/canvas-patterns/generators/_helpers/path-builders.ts
function polarToCartesian(angle, radius, cx = 0, cy = 0) {
  return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
}
function catmullRomToBezier(points, tension = 0.5) {
  if (points.length < 2) return "";
  const n = points.length;
  const parts = [];
  parts.push(`M${r(points[0].x)},${r(points[0].y)}`);
  for (let i = 0; i < n; i++) {
    const p0 = points[(i - 1 + n) % n];
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    const p3 = points[(i + 2) % n];
    const c1x = p1.x + (p2.x - p0.x) * tension / 3;
    const c1y = p1.y + (p2.y - p0.y) * tension / 3;
    const c2x = p2.x - (p3.x - p1.x) * tension / 3;
    const c2y = p2.y - (p3.y - p1.y) * tension / 3;
    parts.push(
      `C${r(c1x)},${r(c1y)} ${r(c2x)},${r(c2y)} ${r(p2.x)},${r(p2.y)}`
    );
  }
  parts.push("Z");
  return parts.join(" ");
}
function openCatmullRomToBezier(points, tension = 0.5) {
  if (points.length < 2) return "";
  const n = points.length;
  const parts = [];
  parts.push(`M${r(points[0].x)},${r(points[0].y)}`);
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? points[i + 1];
    const c1x = p1.x + (p2.x - p0.x) * tension / 3;
    const c1y = p1.y + (p2.y - p0.y) * tension / 3;
    const c2x = p2.x - (p3.x - p1.x) * tension / 3;
    const c2y = p2.y - (p3.y - p1.y) * tension / 3;
    parts.push(
      `C${r(c1x)},${r(c1y)} ${r(c2x)},${r(c2y)} ${r(p2.x)},${r(p2.y)}`
    );
  }
  return parts.join(" ");
}
function r(n) {
  return Math.round(n * 100) / 100;
}

// src/visual-rendering/organic-paths.ts
var r2 = (n) => Math.round(n * 100) / 100;
function num(params, key, fallback) {
  const v = params[key];
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
function str(params, key, fallback) {
  const v = params[key];
  return typeof v === "string" && v ? v : fallback;
}
function tornLinePoints(width, depthPx, roughness, seed, row) {
  const noise = seededNoise2d(`${seed}-coarse-${row}`);
  const rng = seededRng(`${seed}-fine-${row}`);
  const segments = Math.min(96, Math.max(16, Math.round(width / 14)));
  const points = [];
  for (let i = 0; i <= segments; i += 1) {
    const t2 = i / segments;
    const coarse = (noise(t2 * 4.2, row * 7.3) + 1) / 2;
    const fine = rng.nextBetween(-1, 1) * roughness * depthPx * 0.35;
    const y = Math.min(depthPx, Math.max(0, coarse * depthPx + fine));
    const x = t2 * width;
    const interior = i > 0 && i < segments;
    if (interior && rng.next() < roughness * 0.12) {
      const half = width / segments * rng.nextBetween(0.1, 0.35);
      const spikeY = Math.min(
        depthPx,
        Math.max(0, y + (rng.next() < 0.5 ? -1 : 1) * depthPx * rng.nextBetween(0.45, 1))
      );
      points.push({ x: x - half, y });
      points.push({ x, y: spikeY });
      points.push({ x: x + half, y });
    } else {
      points.push({ x, y });
    }
  }
  return points;
}
function tornEdgePathData(params, width, height) {
  const edge = str(params, "edge", "bottom");
  const roughness = Math.min(1, Math.max(0, num(params, "roughness", 0.5)));
  const tearDepth = Math.min(0.45, Math.max(0.02, num(params, "tearDepth", 0.18)));
  const seed = str(params, "seed", "torn-1");
  const depthPx = tearDepth * height;
  const top = edge !== "bottom" ? tornLinePoints(width, depthPx, roughness, seed, 0) : null;
  const bottom = edge !== "top" ? tornLinePoints(width, depthPx, roughness, seed, 1) : null;
  const parts = [];
  if (top) {
    parts.push(`M${r2(top[0].x)},${r2(top[0].y)}`);
    for (let i = 1; i < top.length; i += 1) parts.push(`L${r2(top[i].x)},${r2(top[i].y)}`);
  } else {
    parts.push(`M0,0 L${r2(width)},0`);
  }
  if (bottom) {
    for (let i = bottom.length - 1; i >= 0; i -= 1) {
      parts.push(`L${r2(bottom[i].x)},${r2(height - bottom[i].y)}`);
    }
  } else {
    parts.push(`L${r2(width)},${r2(height)} L0,${r2(height)}`);
  }
  parts.push("Z");
  return parts.join(" ");
}
function blobPathData(params, width, height) {
  const complexity = Math.min(16, Math.max(5, Math.round(num(params, "complexity", 8))));
  const contrast = Math.min(1, Math.max(0, num(params, "contrast", 0.5)));
  const seed = str(params, "seed", "blob-1");
  const noise = seededNoise2d(seed);
  const cx = width / 2;
  const cy = height / 2;
  const maxGrow = 1 + contrast * 0.45;
  const rx = width / 2 / maxGrow;
  const ry = height / 2 / maxGrow;
  const points = [];
  for (let i = 0; i < complexity; i += 1) {
    const angle = i / complexity * Math.PI * 2;
    const n = noise(Math.cos(angle) * 1.5, Math.sin(angle) * 1.5);
    const grow = 1 + n * contrast * 0.45;
    points.push({
      x: cx + Math.cos(angle) * rx * grow,
      y: cy + Math.sin(angle) * ry * grow
    });
  }
  return catmullRomToBezier(points, 0.5);
}
function zigzagPathData(params, width, height) {
  const teeth = Math.min(40, Math.max(2, Math.round(num(params, "teeth", 10))));
  const amplitude = Math.min(0.9, Math.max(0.1, num(params, "amplitude", 0.55)));
  const rise = amplitude * height;
  const thickness = height - rise;
  const top = [];
  const steps = teeth * 2;
  for (let i = 0; i <= steps; i += 1) {
    top.push({ x: i / steps * width, y: i % 2 === 0 ? rise : 0 });
  }
  const parts = [`M${r2(top[0].x)},${r2(top[0].y)}`];
  for (let i = 1; i < top.length; i += 1) parts.push(`L${r2(top[i].x)},${r2(top[i].y)}`);
  for (let i = top.length - 1; i >= 0; i -= 1) {
    parts.push(`L${r2(top[i].x)},${r2(top[i].y + thickness)}`);
  }
  parts.push("Z");
  return parts.join(" ");
}
function shapedEdgeOf(params) {
  return str(params, "edge", "bottom") === "top" ? "top" : "bottom";
}
function closeToFlatSide(d, width, height, edge) {
  const flatY = edge === "bottom" ? 0 : height;
  d.push(`L${r2(width)},${r2(flatY)}`, `L0,${r2(flatY)}`, "Z");
  return d.join(" ");
}
function bandControlPoints(width, height, segments, amplitude, seed) {
  const rng = seededRng(seed);
  const midY = height / 2;
  const amp = amplitude * height / 2;
  const points = [];
  for (let i = 0; i <= segments; i += 1) {
    const dir = i % 2 === 0 ? -1 : 1;
    const jitter = 0.6 + rng.next() * 0.4;
    points.push({ x: i * width / segments, y: midY + dir * amp * jitter });
  }
  return points;
}
function waveEdgePathData(params, width, height) {
  const complexity = Math.min(100, Math.max(0, num(params, "complexity", 50)));
  const amplitude = Math.min(1, Math.max(0.1, num(params, "amplitude", 0.7)));
  const seed = str(params, "seed", "wave-1");
  const segments = Math.max(2, Math.round(2 + complexity / 100 * 10));
  const pts = bandControlPoints(width, height, segments, amplitude, seed);
  const d = [`M${r2(pts[0].x)},${r2(pts[0].y)}`];
  for (let i = 1; i < pts.length; i += 1) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const midX = (prev.x + curr.x) / 2;
    d.push(`C${r2(midX)},${r2(prev.y)} ${r2(midX)},${r2(curr.y)} ${r2(curr.x)},${r2(curr.y)}`);
  }
  return closeToFlatSide(d, width, height, shapedEdgeOf(params));
}
function peaksEdgePathData(params, width, height) {
  const complexity = Math.min(100, Math.max(0, num(params, "complexity", 50)));
  const amplitude = Math.min(1, Math.max(0.1, num(params, "amplitude", 0.7)));
  const seed = str(params, "seed", "peaks-1");
  const segments = Math.max(2, Math.round(2 + complexity / 100 * 12));
  const pts = bandControlPoints(width, height, segments, amplitude, seed);
  const d = [`M${r2(pts[0].x)},${r2(pts[0].y)}`];
  for (let i = 1; i < pts.length; i += 1) d.push(`L${r2(pts[i].x)},${r2(pts[i].y)}`);
  return closeToFlatSide(d, width, height, shapedEdgeOf(params));
}
function mountainsEdgePathData(params, width, height) {
  const complexity = Math.min(100, Math.max(0, num(params, "complexity", 50)));
  const amplitude = Math.min(1, Math.max(0.1, num(params, "amplitude", 0.8)));
  const seed = str(params, "seed", "mtn-1");
  const rng = seededRng(seed);
  const peaks = Math.max(2, Math.round(2 + complexity / 100 * 8));
  const segWidth = width / peaks;
  const midY = height / 2;
  const baseAmp = amplitude * height / 2;
  const d = [`M0,${r2(midY + baseAmp * (0.4 + rng.next() * 0.4))}`];
  for (let i = 0; i < peaks; i += 1) {
    const peakX = i * segWidth + segWidth * (0.3 + rng.next() * 0.4);
    const peakY = midY - (0.55 + rng.next() * 0.45) * baseAmp;
    const valleyY = midY + baseAmp * (0.4 + rng.next() * 0.4);
    d.push(`L${r2(peakX)},${r2(peakY)}`, `L${r2((i + 1) * segWidth)},${r2(valleyY)}`);
  }
  return closeToFlatSide(d, width, height, shapedEdgeOf(params));
}
function stepsEdgePathData(params, width, height) {
  const complexity = Math.min(100, Math.max(0, num(params, "complexity", 50)));
  const amplitude = Math.min(1, Math.max(0.1, num(params, "amplitude", 0.7)));
  const steps = Math.max(2, Math.round(2 + complexity / 100 * 10));
  const stepWidth = width / steps;
  const midY = height / 2;
  const amp = amplitude * height / 2;
  const high = midY - amp;
  const low = midY + amp;
  const d = [`M0,${r2(high)}`];
  for (let i = 0; i < steps; i += 1) {
    const stepY = i % 2 === 0 ? high : low;
    const nextX = (i + 1) * stepWidth;
    d.push(`L${r2(nextX)},${r2(stepY)}`);
    if (i < steps - 1) {
      d.push(`L${r2(nextX)},${r2((i + 1) % 2 === 0 ? high : low)}`);
    }
  }
  return closeToFlatSide(d, width, height, shapedEdgeOf(params));
}
var KAPPA = 0.5523;
function scallopsEdgePathData(params, width, height) {
  const complexity = Math.min(100, Math.max(0, num(params, "complexity", 50)));
  const amplitude = Math.min(1, Math.max(0.1, num(params, "amplitude", 0.7)));
  const edge = shapedEdgeOf(params);
  const count = Math.max(2, Math.round(2 + complexity / 100 * 10));
  const rx = width / count / 2;
  const ry = amplitude * height / 2 * 0.95;
  const midY = height / 2;
  const bulge = edge === "bottom" ? 1 : -1;
  const d = [`M0,${r2(midY)}`];
  for (let i = 0; i < count; i += 1) {
    const sx = i * width / count;
    const cx = sx + rx;
    const apexY = midY + bulge * ry;
    d.push(
      `C${r2(sx)},${r2(midY + bulge * ry * KAPPA)} ${r2(cx - rx * KAPPA)},${r2(apexY)} ${r2(cx)},${r2(apexY)}`,
      `C${r2(cx + rx * KAPPA)},${r2(apexY)} ${r2(sx + rx * 2)},${r2(midY + bulge * ry * KAPPA)} ${r2(sx + rx * 2)},${r2(midY)}`
    );
  }
  return closeToFlatSide(d, width, height, edge);
}
function diagonalEdgePathData(params, width, height) {
  const amplitude = Math.min(1, Math.max(0.1, num(params, "amplitude", 1)));
  const direction = str(params, "direction", "down");
  const half = amplitude * height / 2;
  const midY = height / 2;
  const leftY = direction === "down" ? midY - half : midY + half;
  const rightY = direction === "down" ? midY + half : midY - half;
  const d = [`M0,${r2(leftY)}`, `L${r2(width)},${r2(rightY)}`];
  return closeToFlatSide(d, width, height, shapedEdgeOf(params));
}
var SHAPE_GENERATORS = {
  "torn-edge": {
    key: "torn-edge",
    label: "Torn paper",
    paramsSchema: {
      edge: { type: "enum", options: ["top", "bottom", "both"], default: "bottom", label: "Torn edge" },
      tearDepth: { type: "number", min: 0.02, max: 0.45, step: 0.01, default: 0.18, label: "Tear depth" },
      roughness: { type: "number", min: 0, max: 1, step: 0.05, default: 0.5, label: "Roughness" },
      seed: { type: "seed", default: "torn-1" }
    },
    defaultSize: { width: 600, height: 120 },
    build: tornEdgePathData
  },
  blob: {
    key: "blob",
    label: "Blob",
    paramsSchema: {
      complexity: { type: "number", min: 5, max: 16, step: 1, default: 8, label: "Points" },
      contrast: { type: "number", min: 0, max: 1, step: 0.05, default: 0.5, label: "Irregularity" },
      seed: { type: "seed", default: "blob-1" }
    },
    defaultSize: { width: 280, height: 260 },
    build: blobPathData
  },
  zigzag: {
    key: "zigzag",
    label: "Zigzag band",
    paramsSchema: {
      teeth: { type: "number", min: 2, max: 40, step: 1, default: 10, label: "Teeth" },
      amplitude: { type: "number", min: 0.1, max: 0.9, step: 0.05, default: 0.55, label: "Amplitude" }
    },
    defaultSize: { width: 600, height: 36 },
    build: zigzagPathData
  },
  wave: {
    key: "wave",
    label: "Wave edge",
    paramsSchema: {
      edge: { type: "enum", options: ["top", "bottom"], default: "bottom", label: "Shaped edge" },
      complexity: { type: "number", min: 0, max: 100, step: 5, default: 50, label: "Waves" },
      amplitude: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.7, label: "Amplitude" },
      seed: { type: "seed", default: "wave-1" }
    },
    defaultSize: { width: 600, height: 80 },
    build: waveEdgePathData
  },
  peaks: {
    key: "peaks",
    label: "Peaks edge",
    paramsSchema: {
      edge: { type: "enum", options: ["top", "bottom"], default: "bottom", label: "Shaped edge" },
      complexity: { type: "number", min: 0, max: 100, step: 5, default: 50, label: "Peaks" },
      amplitude: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.7, label: "Amplitude" },
      seed: { type: "seed", default: "peaks-1" }
    },
    defaultSize: { width: 600, height: 80 },
    build: peaksEdgePathData
  },
  mountains: {
    key: "mountains",
    label: "Mountains edge",
    paramsSchema: {
      edge: { type: "enum", options: ["top", "bottom"], default: "bottom", label: "Shaped edge" },
      complexity: { type: "number", min: 0, max: 100, step: 5, default: 50, label: "Ridges" },
      amplitude: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.8, label: "Amplitude" },
      seed: { type: "seed", default: "mtn-1" }
    },
    defaultSize: { width: 600, height: 100 },
    build: mountainsEdgePathData
  },
  steps: {
    key: "steps",
    label: "Steps edge",
    paramsSchema: {
      edge: { type: "enum", options: ["top", "bottom"], default: "bottom", label: "Shaped edge" },
      complexity: { type: "number", min: 0, max: 100, step: 5, default: 50, label: "Steps" },
      amplitude: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.7, label: "Amplitude" }
    },
    defaultSize: { width: 600, height: 80 },
    build: stepsEdgePathData
  },
  scallops: {
    key: "scallops",
    label: "Scallops edge",
    paramsSchema: {
      edge: { type: "enum", options: ["top", "bottom"], default: "bottom", label: "Shaped edge" },
      complexity: { type: "number", min: 0, max: 100, step: 5, default: 50, label: "Scallops" },
      amplitude: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.7, label: "Amplitude" }
    },
    defaultSize: { width: 600, height: 80 },
    build: scallopsEdgePathData
  },
  diagonal: {
    key: "diagonal",
    label: "Diagonal edge",
    paramsSchema: {
      edge: { type: "enum", options: ["top", "bottom"], default: "bottom", label: "Shaped edge" },
      direction: { type: "enum", options: ["down", "up"], default: "down", label: "Slope" },
      amplitude: { type: "number", min: 0.1, max: 1, step: 0.05, default: 1, label: "Amplitude" }
    },
    defaultSize: { width: 600, height: 120 },
    build: diagonalEdgePathData
  }
};
function shapeGenDefaults(key) {
  const def = SHAPE_GENERATORS[key];
  if (!def) return {};
  const out = {};
  for (const [k, field] of Object.entries(def.paramsSchema)) {
    out[k] = field.default;
  }
  return out;
}
function buildShapeGenPath(config, width, height) {
  if (!config?.key) return null;
  const def = SHAPE_GENERATORS[config.key];
  if (!def) return null;
  return def.build(config.params ?? {}, width, height);
}

// src/visual-rendering/scene-to-html.ts
async function sceneToHtml(doc, options = {}) {
  const rasterizedNodeIds = [];
  const rasterUrls = /* @__PURE__ */ new Map();
  if (options.rasterizeNode) {
    const targets = [];
    walk(doc, (node) => {
      if (nodeNeedsRaster(node)) targets.push(node);
    });
    const results = await Promise.all(
      targets.map(
        (node) => options.rasterizeNode(buildRasterSubDoc(node)).then((url) => ({ id: node.id, url }))
      )
    );
    for (const { id, url } of results) {
      if (url) {
        rasterUrls.set(id, url);
        rasterizedNodeIds.push(id);
      }
    }
  }
  const ctx = { doc, options, rasterUrls };
  const children = doc.rootChildren.map((id) => doc.nodes[id]).filter((n) => n != null).map((n) => emitNode(n, ctx)).join("");
  const rootStyle = styleString([
    "position:relative",
    `width:${px(doc.width)}`,
    `height:${px(doc.height)}`,
    "overflow:hidden",
    ...backgroundDeclarations(doc.fills, doc.width, doc.height)
  ]);
  const fontImport = options.fontImportUrl;
  const fontStyle = fontImport ? fontImport.includes("@import") || fontImport.includes("@font-face") ? `<style>${fontImport}</style>` : `<style>@import url("${escapeAttr(fontImport)}");</style>` : "";
  const docAttr = options.embedScene ? ` data-nm-doc="${escapeAttr(JSON.stringify(doc))}"` : "";
  const html = `${fontStyle}<div${docAttr} style="${rootStyle}">${children}</div>`;
  return { html, rasterizedNodeIds };
}
function nodeNeedsRaster(node) {
  if (node.type === "PATH" && !canInlinePath(node)) return true;
  if (node.type === "TEXT" && hasActiveWarp(node)) return true;
  if (node.type === "TEXT" && node.strokes?.length && node.strokeWeight) return true;
  if (BOX_STROKE_TYPES.has(node.type) && hasGradientStroke(node)) return true;
  if (INNER_SHADOW_RASTER_TYPES.has(node.type) && hasInnerShadow(node)) return true;
  if (SVG_SHAPE_TYPES.has(node.type) && hasAngularGradientPaint(node)) return true;
  if (hasDiamondGradientPaint(node)) return true;
  if (hasNoiseEffect(node)) return true;
  if (hasProgressiveBlur(node)) return true;
  if (node.type === "IMAGE" && hasDuotone(node.imageAdjustments)) return true;
  for (const fill of node.fills ?? []) {
    if (fill.type !== "IMAGE" || fill.visible === false) continue;
    if (node.type === "IMAGE") continue;
    if (hasVisibleAdjustments(fill.imageAdjustments)) return true;
    if (fill.imageTransform) return true;
    if (fill.scaleMode === "TILE" && SVG_SHAPE_TYPES.has(node.type)) return true;
  }
  return false;
}
var SVG_SHAPE_TYPES = /* @__PURE__ */ new Set([
  "STAR",
  "POLYGON",
  "SEAL",
  "TRAPEZOID",
  "LINE",
  "PATH"
]);
var BOX_STROKE_TYPES = /* @__PURE__ */ new Set(["RECTANGLE", "ELLIPSE", "IMAGE"]);
var INNER_SHADOW_RASTER_TYPES = /* @__PURE__ */ new Set([
  "STAR",
  "POLYGON",
  "SEAL",
  "TRAPEZOID",
  "PATH"
]);
var INNER_SHADOW_CSS_TYPES = /* @__PURE__ */ new Set([
  "RECTANGLE",
  "ELLIPSE",
  "FRAME",
  "GROUP",
  "IMAGE"
]);
function hasActiveWarp(node) {
  const warp = node.pluginData?.textWarp;
  return !!warp && warp.type !== "none" && Math.abs(warp.bend ?? 0) >= 0.5;
}
function hasGradientStroke(node) {
  if (!node.strokes?.length || !node.strokeWeight || node.strokeWeight <= 0) return false;
  return node.strokes.some(
    (s) => s.visible !== false && (s.type === "GRADIENT_LINEAR" || s.type === "GRADIENT_RADIAL" || s.type === "GRADIENT_ANGULAR")
  );
}
function hasInnerShadow(node) {
  return !!node.effects?.some((e) => e.visible !== false && e.type === "INNER_SHADOW");
}
function hasNoiseEffect(node) {
  return !!node.effects?.some((e) => e.visible !== false && e.type === "NOISE");
}
function hasProgressiveBlur(node) {
  const p = node.blurProgressive;
  return !!p && ((p.startRadius ?? 0) > 0 || (p.endRadius ?? 0) > 0);
}
function hasAngularGradientPaint(node) {
  const paints = [...node.fills ?? [], ...node.strokes ?? []];
  return paints.some((p) => p.visible !== false && p.type === "GRADIENT_ANGULAR");
}
function hasDiamondGradientPaint(node) {
  const paints = [
    ...node.fills ?? [],
    ...node.strokes ?? [],
    ...node.mask ? [node.mask] : []
  ];
  return paints.some((p) => p.visible !== false && p.type === "GRADIENT_DIAMOND");
}
function emitNode(node, ctx) {
  if (node.visible === false) return "";
  let content = emitNodeContent(node, ctx);
  if (content === null) return "";
  if (node.linkUrl && node.type !== "TEXT" && node.type !== "IMAGE" && node.type !== "LINK_ZONE") {
    content += `<a href="${escapeAttr(node.linkUrl)}" style="${styleString([
      "position:absolute",
      "inset:0",
      "display:block"
    ])}"></a>`;
  }
  if (!nodeNeedsRaster(node)) {
    content += innerShadowOverlay(node);
  }
  const wrapperStyle = styleString(wrapperDeclarations(node));
  const idAttr = ctx.options.emitNodeIds ? ` data-node-id="${escapeAttr(node.id)}"` : "";
  return `<div${idAttr} style="${wrapperStyle}">${content}</div>`;
}
function wrapperDeclarations(node) {
  const decls = [
    "position:absolute",
    `left:${px(node.x)}`,
    `top:${px(node.y)}`,
    `width:${px(node.width)}`,
    `height:${px(node.height)}`
  ];
  if (node.rotation) {
    decls.push(`transform:rotate(${num2(node.rotation)}deg)`);
    decls.push("transform-origin:center center");
  }
  if (node.opacity != null && node.opacity < 1) {
    decls.push(`opacity:${num2(node.opacity)}`);
  }
  if (node.blendMode && node.blendMode !== "NORMAL") {
    decls.push(`mix-blend-mode:${node.blendMode.toLowerCase().replace(/_/g, "-")}`);
  }
  if (node.blur != null && node.blur > 0 && !hasProgressiveBlur(node)) {
    decls.push(`filter:blur(${px(node.blur)})`);
  }
  if (node.backdropBlur != null && node.backdropBlur > 0) {
    decls.push(`backdrop-filter:blur(${px(node.backdropBlur)})`);
    decls.push(`-webkit-backdrop-filter:blur(${px(node.backdropBlur)})`);
    const radius = radiusCss(node);
    if (radius) decls.push(`border-radius:${radius}`);
  }
  if (node.mask) {
    const maskImage = maskImageCss(node.mask, node.width, node.height);
    if (maskImage) {
      const maskSize = node.mask.type === "IMAGE" ? "cover" : "100% 100%";
      decls.push(`-webkit-mask-image:${maskImage}`);
      decls.push(`mask-image:${maskImage}`);
      decls.push(`-webkit-mask-size:${maskSize}`);
      decls.push(`mask-size:${maskSize}`);
      decls.push("-webkit-mask-position:center");
      decls.push("mask-position:center");
      decls.push("-webkit-mask-repeat:no-repeat");
      decls.push("mask-repeat:no-repeat");
    }
  }
  return decls;
}
function emitNodeContent(node, ctx) {
  if (nodeNeedsRaster(node)) {
    const url = ctx.rasterUrls.get(node.id);
    if (!url) return null;
    return `<img src="${escapeAttr(url)}" alt="" style="${styleString([
      "display:block",
      "width:100%",
      "height:100%",
      // The PNG is rendered at the node's intrinsic geometry, so stretch 1:1.
      "object-fit:fill"
    ])}" />`;
  }
  switch (node.type) {
    case "RECTANGLE":
      return emitShapeBox(
        node,
        /* ellipse */
        false
      );
    case "ELLIPSE":
      return emitShapeBox(
        node,
        /* ellipse */
        true
      );
    case "FRAME":
    case "GROUP":
    case "LINK_ZONE_GROUP":
      return emitFrame(node, ctx);
    case "LINK_ZONE":
      return emitLinkZone(node);
    case "STAR":
    case "POLYGON":
    case "SEAL":
    case "TRAPEZOID":
    case "LINE": {
      const d = shapeData(node);
      return d ? emitSvgShape(node, d) : "";
    }
    case "PATH":
      return emitPathSvg(node);
    case "TEXT":
      return emitText(node);
    case "IMAGE":
      return emitImage(node);
    default:
      return "";
  }
}
function emitLinkZone(node) {
  if (!node.linkUrl) return "";
  return `<a href="${escapeAttr(node.linkUrl)}" data-link-zone style="${styleString([
    "position:absolute",
    "inset:0",
    "display:block"
  ])}"></a>`;
}
function emitShapeBox(node, ellipse) {
  const decls = ["width:100%", "height:100%"];
  const radius = ellipse ? "50%" : radiusCss(node);
  const fills = node.fills ?? [];
  const hasBlend = fills.some((f) => f.blendMode && f.blendMode !== "NORMAL");
  if (hasBlend) {
    decls.push("position:relative");
  } else {
    decls.push(...backgroundDeclarations(node.fills, node.width, node.height));
  }
  if (radius) decls.push(`border-radius:${radius}`);
  const stroke = strokeBoxShadow(node);
  const shadows = [...stroke ? [stroke] : [], ...dropShadowSegments(node)];
  if (shadows.length) decls.push(`box-shadow:${shadows.join(",")}`);
  const inner = hasBlend ? fillLayerDivs(fills, node.width, node.height, radius) : "";
  return `<div style="${styleString(decls)}">${inner}</div>`;
}
function shapeData(node) {
  const w = node.width;
  const h = node.height;
  switch (node.type) {
    case "STAR": {
      const outerR = w / 2;
      const rawInner = node.innerRadius ?? 0.4;
      const innerR = rawInner <= 1 ? rawInner * outerR : rawInner;
      return starPathData(w / 2, h / 2, outerR, innerR, node.sides ?? 5);
    }
    case "POLYGON":
      return polygonPathData(w / 2, h / 2, w / 2, node.sides ?? 6);
    case "SEAL": {
      const bumps = Math.max(3, Math.floor(node.sides ?? 16));
      const rxOuter = w / 2;
      const ryOuter = h / 2;
      const r7 = node.innerRadius ?? 0.88;
      const innerRatio = r7 <= 1 ? r7 : r7 / Math.max(rxOuter, ryOuter);
      return sealPathData(
        w / 2,
        h / 2,
        rxOuter,
        ryOuter,
        rxOuter * innerRatio,
        ryOuter * innerRatio,
        bumps,
        node.bumpStyle ?? "sharp"
      );
    }
    case "TRAPEZOID":
      return trapezoidPathData(w, h, node.skew ?? 0);
    case "LINE": {
      if (!node.points || node.points.length < 4) return null;
      const [x0, y0, ...rest] = node.points;
      let d = `M${x0},${y0}`;
      for (let i = 0; i < rest.length - 1; i += 2) d += ` L${rest[i]},${rest[i + 1]}`;
      return d;
    }
    default:
      return null;
  }
}
function emitSvgShape(node, d, opts = {}) {
  const w = node.width;
  const h = node.height;
  const sid = node.id.replace(/[^a-zA-Z0-9_-]/g, "_");
  const fillRuleAttr = opts.fillRule ? ` fill-rule="${opts.fillRule}"` : "";
  const imageRect = opts.imageRect ?? { x: 0, y: 0, w, h };
  const flipAttr = opts.flipTransform ? ` transform="${opts.flipTransform}"` : "";
  const defs = [];
  const layers = [];
  const fills = node.fills?.filter((f) => f.visible !== false) ?? [];
  fills.forEach((fill, i) => {
    if (fill.type === "IMAGE") {
      if (!fill.src) return;
      const clipId = `clip-${sid}-${i}`;
      defs.push(`<clipPath id="${clipId}"><path d="${d}"${flipAttr}/></clipPath>`);
      const par = fill.scaleMode === "FIT" ? "xMidYMid meet" : "xMidYMid slice";
      layers.push(
        `<image href="${escapeAttr(fill.src)}" x="${num2(imageRect.x)}" y="${num2(imageRect.y)}" width="${num2(imageRect.w)}" height="${num2(imageRect.h)}" preserveAspectRatio="${par}" clip-path="url(#${clipId})"/>`
      );
    } else if (fill.type === "SOLID") {
      const { color, opacity } = svgSolid(fill);
      layers.push(`<path d="${d}" fill="${color}" fill-opacity="${opacity}"${fillRuleAttr}${flipAttr}/>`);
    } else if (fill.type === "GRADIENT_LINEAR" || fill.type === "GRADIENT_RADIAL") {
      const gid = `grad-${sid}-${i}`;
      defs.push(svgGradientDef(fill, gid, w, h));
      layers.push(`<path d="${d}" fill="url(#${gid})"${fillRuleAttr}${flipAttr}/>`);
    }
  });
  const stroke = node.strokes?.find((s) => s.visible !== false);
  const weight = node.strokeWeight ?? 0;
  if (stroke && weight > 0) {
    let strokeColor = "rgb(0,0,0)";
    let strokeOpacityAttr = "";
    if (stroke.type === "SOLID") {
      const solid = svgSolid(stroke);
      strokeColor = solid.color;
      strokeOpacityAttr = ` stroke-opacity="${solid.opacity}"`;
    } else if (stroke.type === "GRADIENT_LINEAR" || stroke.type === "GRADIENT_RADIAL") {
      const sgid = `sgrad-${sid}`;
      defs.push(svgGradientDef(stroke, sgid, w, h));
      strokeColor = `url(#${sgid})`;
    }
    const join = node.strokeJoin ? ` stroke-linejoin="${node.strokeJoin.toLowerCase()}"` : "";
    const cap = node.strokeCap ? ` stroke-linecap="${node.strokeCap.toLowerCase()}"` : "";
    const dash = node.dashPattern?.length ? ` stroke-dasharray="${node.dashPattern.map(num2).join(",")}"` : "";
    layers.push(
      `<path d="${d}" fill="none" stroke="${strokeColor}"${strokeOpacityAttr} stroke-width="${num2(weight)}"${join}${cap}${dash}${flipAttr}/>`
    );
  }
  const body = layers.join("");
  const defsBlock = defs.length ? `<defs>${defs.join("")}</defs>` : "";
  return `<svg width="100%" height="100%" viewBox="${opts.viewBox ?? `0 0 ${num2(w)} ${num2(h)}`}" preserveAspectRatio="none" style="${styleString([
    "position:absolute",
    "inset:0",
    "display:block",
    "overflow:visible"
  ])}">${defsBlock}${body}</svg>`;
}
function pathBounds(d) {
  if (/[mlcsqtz]/.test(d.replace(/[zZ]/g, ""))) return null;
  if (/[HVAhv a]/.test(d) && /[HVA]/.test(d)) return null;
  if (/[aA]/.test(d)) return null;
  const nums = d.match(/-?\d+(?:\.\d+)?(?:e-?\d+)?/gi);
  if (!nums || nums.length < 2) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = Number(nums[i]);
    const y = Number(nums[i + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const w = maxX - minX;
  const h = maxY - minY;
  if (!(w > 0) || !(h > 0)) return null;
  return { x: minX, y: minY, w, h };
}
function canInlinePath(node) {
  if (!node.pathData) return false;
  return pathBounds(node.pathData) != null;
}
function emitPathSvg(node) {
  if (!node.pathData) return "";
  const vectorPaths = node.pluginData?.vectorPaths;
  const fillRule = vectorPaths?.some((vp) => vp.windingRule === "evenodd") ? "evenodd" : void 0;
  if (node.pluginData?.pathFit === "none") {
    const box = { x: 0, y: 0, w: node.width, h: node.height };
    return emitSvgShape(node, node.pathData, {
      viewBox: `0 0 ${num2(node.width)} ${num2(node.height)}`,
      fillRule,
      imageRect: box
    });
  }
  const bounds = pathBounds(node.pathData);
  if (!bounds) return "";
  const fx = node.pluginData?.flipX === true;
  const fy = node.pluginData?.flipY === true;
  const flipTransform = fx || fy ? `translate(${num2(fx ? 2 * bounds.x + bounds.w : 0)},${num2(fy ? 2 * bounds.y + bounds.h : 0)}) scale(${fx ? -1 : 1},${fy ? -1 : 1})` : void 0;
  return emitSvgShape(node, node.pathData, {
    viewBox: `${num2(bounds.x)} ${num2(bounds.y)} ${num2(bounds.w)} ${num2(bounds.h)}`,
    flipTransform,
    fillRule,
    // An IMAGE fill covers the path's bbox, which the viewBox maps to the node box.
    imageRect: bounds
  });
}
function svgSolid(fill) {
  const rgba = parseColorToRgba(fill.color) ?? { r: 0, g: 0, b: 0, a: 1 };
  return {
    color: `rgb(${rgba.r},${rgba.g},${rgba.b})`,
    opacity: round2(rgba.a * (fill.opacity ?? 1))
  };
}
function svgGradientDef(fill, id, w, h) {
  const opacity = fill.opacity ?? 1;
  const stops = fill.stops.map((s) => {
    const rgba = parseColorToRgba(s.color) ?? { r: 0, g: 0, b: 0, a: 1 };
    return `<stop offset="${num2((s.position ?? 0) * 100)}%" stop-color="rgb(${rgba.r},${rgba.g},${rgba.b})" stop-opacity="${round2(rgba.a * opacity)}"/>`;
  }).join("");
  if (fill.type === "GRADIENT_RADIAL") {
    const cx = fill.center?.x ?? w / 2;
    const cy = fill.center?.y ?? h / 2;
    const radius = fill.radius ?? Math.max(w, h) / 2;
    return `<radialGradient id="${id}" gradientUnits="userSpaceOnUse" cx="${num2(cx)}" cy="${num2(cy)}" r="${num2(radius)}">${stops}</radialGradient>`;
  }
  const { start, end } = resolveLinearGradientCoords(
    0,
    0,
    w,
    h,
    fill.startPoint,
    fill.endPoint,
    fill.angle
  );
  return `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${num2(start[0])}" y1="${num2(start[1])}" x2="${num2(end[0])}" y2="${num2(end[1])}">${stops}</linearGradient>`;
}
function emitFrame(node, ctx) {
  const decls = ["position:absolute", "inset:0"];
  decls.push(...backgroundDeclarations(node.fills, node.width, node.height));
  const radius = radiusCss(node);
  if (radius) decls.push(`border-radius:${radius}`);
  if (node.clipContent) decls.push("overflow:hidden");
  if (node.clipPath) decls.push(`clip-path:path('${escapeAttr(node.clipPath)}')`);
  const shadows = dropShadowSegments(node);
  if (shadows.length) decls.push(`box-shadow:${shadows.join(",")}`);
  const children = (node.children ?? []).map((id) => ctx.doc.nodes[id]).filter((n) => n != null).map((child) => emitNode(child, ctx)).join("");
  return `<div style="${styleString(decls)}">${children}</div>`;
}
function emitText(node) {
  const text = node.text ?? "";
  const family = node.fontFamily ? `${cssFontFamily(node.fontFamily)}, 'Roboto', sans-serif` : `'Roboto', sans-serif`;
  const color = firstSolidColor(node.fills) ?? "#000000";
  const isBold = node.fontWeight != null && node.fontWeight >= 600;
  const textDecls = [
    "margin:0",
    "width:100%",
    "padding:0",
    `font-size:${px(node.fontSize ?? 16)}`,
    `font-family:${family}`,
    `font-weight:${node.fontWeight ?? (isBold ? 700 : 400)}`,
    `line-height:${num2(node.lineHeight ?? 1.2)}`,
    `color:${color}`,
    `text-align:${node.textAlign ?? "left"}`,
    "white-space:pre-wrap",
    "overflow-wrap:break-word"
  ];
  if (node.fontStyle === "italic") textDecls.push("font-style:italic");
  if (node.letterSpacing) textDecls.push(`letter-spacing:${px(node.letterSpacing)}`);
  if (node.textDecoration && node.textDecoration !== "none") {
    textDecls.push(`text-decoration:${node.textDecoration}`);
  }
  if (node.textTransform && node.textTransform !== "none") {
    textDecls.push(`text-transform:${node.textTransform}`);
  }
  const textShadow = textShadowCss(node);
  if (textShadow) textDecls.push(`text-shadow:${textShadow}`);
  const textStroke = node.strokes?.find((s) => s.visible !== false && s.type === "SOLID");
  if (textStroke && node.strokeWeight) {
    const align = node.strokeAlign ?? "OUTSIDE";
    const w = node.strokeWeight * (align === "CENTER" ? 1 : 2);
    textDecls.push(`-webkit-text-stroke:${px(w)} ${textStroke.color}`);
    if (align !== "INSIDE") textDecls.push("paint-order:stroke fill");
  }
  const justify = node.verticalAlign === "middle" ? "center" : node.verticalAlign === "bottom" ? "flex-end" : "flex-start";
  const inner = `<div style="${styleString(textDecls)}">${richTextInnerHtml(node, text)}</div>`;
  const linked = node.linkUrl ? `<a href="${escapeAttr(node.linkUrl)}" style="color:inherit;text-decoration:none;display:block;width:100%;">${inner}</a>` : inner;
  const containerStyle = styleString([
    "width:100%",
    "height:100%",
    "display:flex",
    "flex-direction:column",
    `justify-content:${justify}`
  ]);
  return `<div style="${containerStyle}">${linked}</div>`;
}
function richTextInnerHtml(node, text) {
  const pluginData = node.pluginData;
  const spans = getRichSpans(pluginData, text.length);
  const list = getRichList(pluginData);
  if (list) {
    const tag = list === "ordered" ? "ol" : "ul";
    const lines = text.split("\n");
    let base = 0;
    const items = lines.map((line) => {
      const lineSpans = spans.length ? spansForLine(spans, base, line.length) : [];
      base += line.length + 1;
      const body = lineSpans.length ? splitIntoRuns(line, lineSpans).map(runHtml).join("") : escapeHtml(line);
      return `<li>${body || "<br>"}</li>`;
    }).join("");
    return `<${tag} style="${styleString(["margin:0", "padding-left:1.4em"])}">${items}</${tag}>`;
  }
  const paragraphSpacing = node.paragraphSpacing ?? 0;
  if (paragraphSpacing > 0 && text.includes("\n")) {
    const paras = text.split("\n");
    let base = 0;
    const blocks = paras.map((para, i) => {
      const paraSpans = spans.length ? spansForLine(spans, base, para.length) : [];
      base += para.length + 1;
      const body = paraSpans.length ? splitIntoRuns(para, paraSpans).map(runHtml).join("") : escapeHtml(para) || "<br>";
      const decls = ["margin:0"];
      if (i > 0) decls.push(`margin-top:${px(paragraphSpacing)}`);
      return `<div style="${styleString(decls)}">${body}</div>`;
    });
    return blocks.join("");
  }
  if (!spans.length) return escapeHtml(text);
  return splitIntoRuns(text, spans).map(runHtml).join("");
}
function runHtml(run) {
  const body = escapeHtml(run.text);
  if (!run.style) return body;
  const decls = [];
  if (run.style.bold) decls.push("font-weight:700");
  if (run.style.italic) decls.push("font-style:italic");
  const deco = [];
  if (isUnderlined(run.style)) deco.push("underline");
  if (run.style.strikethrough) deco.push("line-through");
  if (deco.length) decls.push(`text-decoration:${deco.join(" ")}`);
  if (run.style.color) decls.push(`color:${run.style.color}`);
  if (run.style.fontSize != null) decls.push(`font-size:${px(run.style.fontSize)}`);
  if (run.style.fontFamily) {
    decls.push(`font-family:${cssFontFamily(run.style.fontFamily)}, 'Roboto', sans-serif`);
  }
  if (run.style.link) {
    const style = styleString(["color:inherit", ...decls]);
    return `<a href="${escapeAttr(run.style.link)}" style="${style}">${body}</a>`;
  }
  return `<span style="${styleString(decls)}">${body}</span>`;
}
function emitImage(node) {
  if (!node.imageSrc) {
    return `<div style="${styleString(["width:100%", "height:100%", "background:#c8c8c8"])}"></div>`;
  }
  const radius = radiusCss(node);
  const stroke = strokeBoxShadow(node);
  const shadows = [...stroke ? [stroke] : [], ...dropShadowSegments(node)];
  const alt = escapeAttr(node.imageAlt ?? "");
  const cssFilter = adjustmentsToCssFilter(node.imageAdjustments);
  if (node.tileWidth && node.tileHeight) {
    const tileDecls = [
      "width:100%",
      "height:100%",
      `background-image:${cssUrlAttr(node.imageSrc)}`,
      "background-repeat:repeat",
      `background-size:${px(node.tileWidth)} ${px(node.tileHeight)}`,
      ...radius ? [`border-radius:${radius}`] : []
    ];
    if (!cssFilter) {
      const decls = [
        ...tileDecls,
        ...shadows.length ? [`box-shadow:${shadows.join(",")}`] : []
      ];
      return `<div role="img" aria-label="${alt}" style="${styleString(decls)}"></div>`;
    }
    const innerTile = `<div style="${styleString([
      "position:absolute",
      "inset:0",
      ...tileDecls.slice(2),
      `filter:${cssFilter}`
    ])}"></div>`;
    return `<div role="img" aria-label="${alt}" style="${styleString([
      "position:relative",
      "width:100%",
      "height:100%",
      "overflow:hidden",
      ...radius ? [`border-radius:${radius}`] : [],
      ...shadows.length ? [`box-shadow:${shadows.join(",")}`] : []
    ])}">${innerTile}</div>`;
  }
  let img;
  if (node.imageTransform) {
    const { scale, offsetX, offsetY, refWidth, refHeight } = node.imageTransform;
    const inner = `<img src="${escapeAttr(node.imageSrc)}" alt="${alt}" style="${styleString([
      "position:absolute",
      "left:0",
      "top:0",
      "max-width:none",
      ...refWidth && refHeight ? [`width:${px(refWidth)}`, `height:${px(refHeight)}`] : [],
      `transform:translate(${px(offsetX)},${px(offsetY)}) scale(${num2(scale)})`,
      "transform-origin:0 0",
      "display:block",
      ...cssFilter ? [`filter:${cssFilter}`] : []
    ])}" />`;
    img = `<div style="${styleString([
      "position:absolute",
      "inset:0",
      "overflow:hidden",
      ...radius ? [`border-radius:${radius}`] : [],
      ...shadows.length ? [`box-shadow:${shadows.join(",")}`] : []
    ])}">${inner}</div>`;
  } else if (cssFilter) {
    const inner = `<img src="${escapeAttr(node.imageSrc)}" alt="${alt}" style="${styleString([
      "display:block",
      "width:100%",
      "height:100%",
      `object-fit:${node.imageFit === "contain" ? "contain" : "cover"}`,
      "object-position:center",
      `filter:${cssFilter}`
    ])}" />`;
    img = `<div style="${styleString([
      "position:absolute",
      "inset:0",
      "overflow:hidden",
      ...radius ? [`border-radius:${radius}`] : [],
      ...shadows.length ? [`box-shadow:${shadows.join(",")}`] : []
    ])}">${inner}</div>`;
  } else {
    const fit = node.imageFit === "contain" ? "contain" : "cover";
    const imgStyle = styleString([
      "display:block",
      "width:100%",
      "height:100%",
      `object-fit:${fit}`,
      "object-position:center",
      ...radius ? [`border-radius:${radius}`] : [],
      ...shadows.length ? [`box-shadow:${shadows.join(",")}`] : []
    ]);
    img = `<img src="${escapeAttr(node.imageSrc)}" alt="${alt}" style="${imgStyle}" />`;
  }
  if (node.linkUrl) {
    img = `<a href="${escapeAttr(node.linkUrl)}" style="display:block;width:100%;height:100%;text-decoration:none;">${img}</a>`;
  }
  const overlays = overlayFillDivs(node, radius);
  if (!overlays) return img;
  return `<div style="${styleString(["position:relative", "width:100%", "height:100%"])}">${img}${overlays}</div>`;
}
function fillLayerDivs(fills, w, h, radius) {
  return fills.map((fill) => {
    const bg = backgroundDeclarations([fill], w, h);
    if (!bg.length) return "";
    return `<div style="${styleString([
      "position:absolute",
      "inset:0",
      ...bg,
      ...fill.blendMode && fill.blendMode !== "NORMAL" ? [`mix-blend-mode:${fill.blendMode.toLowerCase().replace(/_/g, "-")}`] : [],
      ...radius ? [`border-radius:${radius}`] : [],
      "pointer-events:none"
    ])}"></div>`;
  }).join("");
}
function overlayFillDivs(node, radius) {
  const fills = node.fills?.length ? node.fills : scrimToFills(node);
  if (!fills.length) return "";
  if (fills.some((f) => f.blendMode && f.blendMode !== "NORMAL")) {
    return fillLayerDivs(fills, node.width, node.height, radius);
  }
  const bg = backgroundDeclarations(fills, node.width, node.height);
  if (!bg.length) return "";
  return `<div style="${styleString([
    "position:absolute",
    "inset:0",
    ...bg,
    ...radius ? [`border-radius:${radius}`] : [],
    "pointer-events:none"
  ])}"></div>`;
}
function scrimToFills(node) {
  const scrim = node.imageScrim;
  if (!scrim) return [];
  if (scrim.gradient) {
    const dir = scrim.gradientDirection ?? "bottom";
    const coords = dir === "top" ? { startPoint: { x: 0, y: 1 }, endPoint: { x: 0, y: 0 } } : dir === "right" ? { startPoint: { x: 0, y: 0 }, endPoint: { x: 1, y: 0 } } : dir === "left" ? { startPoint: { x: 1, y: 0 }, endPoint: { x: 0, y: 0 } } : { startPoint: { x: 0, y: 0 }, endPoint: { x: 0, y: 1 } };
    return [
      {
        type: "GRADIENT_LINEAR",
        stops: [
          { position: 0, color: withAlpha(scrim.color, 0) },
          { position: 1, color: withAlpha(scrim.color, scrim.opacity) }
        ],
        ...coords
      }
    ];
  }
  return [{ type: "SOLID", color: scrim.color, opacity: scrim.opacity }];
}
function backgroundDeclarations(fills, w, h) {
  if (!fills?.length) return [];
  const layers = [];
  for (const fill of fills) {
    if (fill.visible === false) continue;
    const layer = paintToBackgroundLayer(fill, w, h);
    if (layer) layers.unshift(layer);
  }
  if (!layers.length) return [];
  return [`background:${layers.join(",")}`];
}
function paintToBackgroundLayer(fill, w, h) {
  switch (fill.type) {
    case "SOLID": {
      const c = solidCss(fill);
      return `linear-gradient(${c},${c})`;
    }
    case "GRADIENT_LINEAR":
      return linearGradientCss(fill, w, h);
    case "GRADIENT_RADIAL":
      return radialGradientCss(fill, w, h);
    case "GRADIENT_ANGULAR":
      return angularGradientCss(fill);
    case "IMAGE":
      return imagePaintCss(fill);
    default:
      return null;
  }
}
function imagePaintCss(fill) {
  if (!fill.src) return null;
  const size = fill.scaleMode === "FIT" ? "contain" : fill.scaleMode === "TILE" ? "auto" : "cover";
  const repeat = fill.scaleMode === "TILE" ? "repeat" : "no-repeat";
  return `${cssUrlAttr(fill.src)} center / ${size} ${repeat}`;
}
function linearGradientCss(fill, w, h) {
  const opacity = fill.opacity ?? 1;
  if (fill.angle != null && !fill.startPoint) {
    const stops2 = fill.stops.map((s) => `${bakeAlpha(s.color, opacity)} ${num2((s.position ?? 0) * 100)}%`).join(",");
    return `linear-gradient(${num2(fill.angle)}deg,${stops2})`;
  }
  const { start, end } = resolveLinearGradientCoords(
    0,
    0,
    w,
    h,
    fill.startPoint,
    fill.endPoint,
    fill.angle
  );
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const angleDeg = Math.atan2(dx, -dy) * 180 / Math.PI;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) {
    const stops2 = fill.stops.map((s) => `${bakeAlpha(s.color, opacity)} ${num2((s.position ?? 0) * 100)}%`).join(",");
    return `linear-gradient(${num2(angleDeg)}deg,${stops2})`;
  }
  const dirX = dx / len;
  const dirY = dy / len;
  const lineLen = Math.abs(w * dirX) + Math.abs(h * dirY);
  const lineStartX = w / 2 - lineLen / 2 * dirX;
  const lineStartY = h / 2 - lineLen / 2 * dirY;
  const base = (start[0] - lineStartX) * dirX + (start[1] - lineStartY) * dirY;
  const stops = fill.stops.map((s) => `${bakeAlpha(s.color, opacity)} ${px(base + (s.position ?? 0) * len)}`).join(",");
  return `linear-gradient(${num2(angleDeg)}deg,${stops})`;
}
function radialGradientCss(fill, w, h) {
  const opacity = fill.opacity ?? 1;
  const cx = fill.center?.x ?? w / 2;
  const cy = fill.center?.y ?? h / 2;
  const radius = fill.radius ?? Math.max(w, h) / 2;
  const stops = fill.stops.map((s) => `${bakeAlpha(s.color, opacity)} ${num2((s.position ?? 0) * 100)}%`).join(",");
  return `radial-gradient(circle ${px(radius)} at ${px(cx)} ${px(cy)},${stops})`;
}
function angularGradientCss(fill) {
  const opacity = fill.opacity ?? 1;
  const cx = (fill.center?.x ?? 0.5) * 100;
  const cy = (fill.center?.y ?? 0.5) * 100;
  const rotation = fill.rotation ?? 0;
  const stops = fill.stops.map((s) => `${bakeAlpha(s.color, opacity)} ${num2((s.position ?? 0) * 100)}%`).join(",");
  return `conic-gradient(from ${num2(rotation)}deg at ${num2(cx)}% ${num2(cy)}%,${stops})`;
}
function maskImageCss(mask, w, h) {
  switch (mask.type) {
    case "SOLID": {
      const c = solidCss(mask);
      return `linear-gradient(${c},${c})`;
    }
    case "GRADIENT_LINEAR":
      return linearGradientCss(mask, w, h);
    case "GRADIENT_RADIAL":
      return radialGradientCss(mask, w, h);
    case "GRADIENT_ANGULAR":
      return angularGradientCss(mask);
    case "IMAGE":
      return mask.src ? cssUrlAttr(mask.src) : null;
    default:
      return null;
  }
}
function dropShadowSegments(node) {
  const shadows = node.effects?.filter(
    (e) => e.visible !== false && e.type === "DROP_SHADOW"
  );
  if (!shadows?.length) return [];
  return shadows.map(
    (s) => `${px(s.offsetX ?? 0)} ${px(s.offsetY ?? 0)} ${px(s.radius ?? 0)} ${px(s.spread ?? 0)} ${s.color ?? "rgba(0,0,0,0.25)"}`
  );
}
function innerShadowSegments(node) {
  const shadows = node.effects?.filter(
    (e) => e.visible !== false && e.type === "INNER_SHADOW"
  );
  if (!shadows?.length) return [];
  return shadows.map(
    (s) => `inset ${px(s.offsetX ?? 0)} ${px(s.offsetY ?? 0)} ${px(s.radius ?? 0)} ${px(s.spread ?? 0)} ${s.color ?? "rgba(0,0,0,0.25)"}`
  );
}
function innerShadowOverlay(node) {
  if (!INNER_SHADOW_CSS_TYPES.has(node.type)) return "";
  const segments = innerShadowSegments(node);
  if (!segments.length) return "";
  const radius = node.type === "ELLIPSE" ? "50%" : radiusCss(node);
  return `<div style="${styleString([
    "position:absolute",
    "inset:0",
    "pointer-events:none",
    ...radius ? [`border-radius:${radius}`] : [],
    `box-shadow:${segments.join(",")}`
  ])}"></div>`;
}
function textShadowCss(node) {
  const shadows = node.effects?.filter(
    (e) => e.visible !== false && e.type === "DROP_SHADOW"
  );
  if (!shadows?.length) return null;
  return shadows.map(
    (s) => `${px(s.offsetX ?? 0)} ${px(s.offsetY ?? 0)} ${px(s.radius ?? 0)} ${s.color ?? "rgba(0,0,0,0.25)"}`
  ).join(",");
}
function strokeBoxShadow(node) {
  if (!node.strokes?.length) return null;
  const stroke = node.strokes.find((s) => s.visible !== false);
  if (!stroke) return null;
  const weight = node.strokeWeight ?? 1;
  if (weight <= 0) return null;
  const color = stroke.type === "SOLID" ? solidCss(stroke) : "#000000";
  const align = node.strokeAlign ?? "CENTER";
  if (align === "INSIDE") return `inset 0 0 0 ${px(weight)} ${color}`;
  if (align === "OUTSIDE") return `0 0 0 ${px(weight)} ${color}`;
  const half = weight / 2;
  return `0 0 0 ${px(half)} ${color}, inset 0 0 0 ${px(half)} ${color}`;
}
function buildRasterSubDoc(node) {
  const bare = {
    ...node,
    x: 0,
    y: 0,
    parentId: null,
    rotation: void 0,
    opacity: void 0,
    blur: void 0,
    backdropBlur: void 0,
    blendMode: void 0,
    mask: void 0
  };
  return {
    version: 2,
    width: Math.max(1, Math.round(node.width)),
    height: Math.max(1, Math.round(node.height)),
    nodes: { [node.id]: bare },
    rootChildren: [node.id]
  };
}
function walk(doc, visitor) {
  const visit = (id) => {
    const node = doc.nodes[id];
    if (!node) return;
    visitor(node);
    if (node.children) for (const childId of node.children) visit(childId);
  };
  for (const rootId of doc.rootChildren) visit(rootId);
}
function solidCss(fill) {
  return fill.opacity != null && fill.opacity < 1 ? bakeAlpha(fill.color, fill.opacity) : fill.color;
}
function firstSolidColor(fills) {
  const fill = fills?.find((f) => f.visible !== false && f.type === "SOLID");
  return fill ? solidCss(fill) : null;
}
function bakeAlpha(color, factor) {
  if (factor >= 1) return color;
  const rgba = parseColorToRgba(color);
  if (!rgba) return color;
  return `rgba(${rgba.r},${rgba.g},${rgba.b},${round2(rgba.a * factor)})`;
}
function withAlpha(color, alpha) {
  const rgba = parseColorToRgba(color);
  if (!rgba) return color;
  return `rgba(${rgba.r},${rgba.g},${rgba.b},${round2(alpha)})`;
}
function parseColorToRgba(color) {
  const trimmed = color.trim();
  const rgbaMatch = trimmed.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+))?\s*\)/
  );
  if (rgbaMatch) {
    return {
      r: Math.round(Number(rgbaMatch[1])),
      g: Math.round(Number(rgbaMatch[2])),
      b: Math.round(Number(rgbaMatch[3])),
      a: rgbaMatch[4] != null ? Number(rgbaMatch[4]) : 1
    };
  }
  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    if (hex.length === 4) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    const r7 = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    if ([r7, g, b].every(Number.isFinite)) return { r: r7, g, b, a };
  }
  return null;
}
function radiusCss(node) {
  if (node.cornerRadii && node.cornerRadii.length === 4) {
    return node.cornerRadii.map((r8) => px(r8)).join(" ");
  }
  const r7 = toCornerRadius2(node.cornerRadius);
  return r7 ? px(r7) : "";
}
function toCornerRadius2(value) {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return void 0;
  const match = value.match(/^(-?\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : void 0;
}
function cssFontFamily(family) {
  const trimmed = family.trim();
  if (/^['"]/.test(trimmed)) return trimmed;
  return /\s/.test(trimmed) ? `'${trimmed}'` : trimmed;
}
function styleString(decls) {
  return decls.join(";");
}
function px(value) {
  return `${round2(value)}px`;
}
function num2(value) {
  return String(round2(value));
}
function round2(value) {
  return Math.round(value * 1e3) / 1e3;
}
function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escapeAttr(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function cssUrlAttr(src) {
  const cssSafe = src.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  return `url('${escapeAttr(cssSafe)}')`;
}

// src/visual-rendering/html-to-scene.ts
function unescapeAttr(text) {
  return text.replace(/&quot;/g, '"').replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/&amp;/g, "&");
}
var NM_DOC_RE = /\sdata-nm-doc="([^"]*)"/;
function htmlToScene(html) {
  const match = NM_DOC_RE.exec(html);
  if (!match) return null;
  try {
    return JSON.parse(unescapeAttr(match[1]));
  } catch {
    return null;
  }
}
function htmlToScenes(html) {
  const re = new RegExp(NM_DOC_RE.source, "g");
  const out = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    try {
      out.push(JSON.parse(unescapeAttr(m[1])));
    } catch {
    }
  }
  return out;
}

// src/visual-rendering/scene-layout.ts
function computeLayout(doc) {
  if (!hasAnyLayoutWork(doc)) return doc;
  const patches = /* @__PURE__ */ new Map();
  function getNode(id) {
    const base = doc.nodes[id];
    const patch = patches.get(id);
    return patch ? { ...base, ...patch } : base;
  }
  function visit(nodeId) {
    const node = doc.nodes[nodeId];
    if (!node) return;
    for (const childId of node.children ?? []) visit(childId);
    if (node.layoutMode && node.layoutMode !== "NONE" && node.children?.length) {
      layoutContainer(nodeId, getNode, patches);
    } else if (isBoundsHugContainer(node) && node.children?.length) {
      hugNoneContainer(nodeId, getNode, patches);
    } else if (isDerivedBoundsGroup(node) && node.children?.length) {
      tightenGroup(nodeId, getNode, patches);
    }
  }
  for (const rootId of doc.rootChildren) visit(rootId);
  if (patches.size === 0) return doc;
  const nextNodes = { ...doc.nodes };
  for (const [id, patch] of patches) {
    nextNodes[id] = { ...nextNodes[id], ...patch };
  }
  return { ...doc, nodes: nextNodes };
}
function tightenGroups(doc) {
  const patches = /* @__PURE__ */ new Map();
  function getNode(id) {
    const base = doc.nodes[id];
    const patch = patches.get(id);
    return patch ? { ...base, ...patch } : base;
  }
  function visit(nodeId) {
    const node = doc.nodes[nodeId];
    if (!node) return;
    for (const childId of node.children ?? []) visit(childId);
    if (isDerivedBoundsGroup(node) && node.children?.length) {
      tightenGroup(nodeId, getNode, patches);
    }
  }
  for (const rootId of doc.rootChildren) visit(rootId);
  if (patches.size === 0) return doc;
  const nextNodes = { ...doc.nodes };
  for (const [id, patch] of patches) {
    nextNodes[id] = { ...nextNodes[id], ...patch };
  }
  return { ...doc, nodes: nextNodes };
}
function isBoundsHugContainer(node) {
  return (node.type === "FRAME" || node.type === "GROUP") && (!node.layoutMode || node.layoutMode === "NONE") && node.layoutSizingVertical === "HUG";
}
function hasAnyLayoutWork(doc) {
  return Object.values(doc.nodes).some(
    (n) => n.layoutMode && n.layoutMode !== "NONE" || isBoundsHugContainer(n) || isDerivedBoundsGroup(n)
  );
}
function isDerivedBoundsGroup(node) {
  return node.type === "GROUP" && (!node.layoutMode || node.layoutMode === "NONE") && !(node.rotation ?? 0) && !node.clipContent && !node.clipPath && !node.fills?.length && !node.strokes?.length && !node.effects?.length;
}
function tightenGroup(groupId, getNode, patches) {
  const group = getNode(groupId);
  const childIds = group.children ?? [];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const id of childIds) {
    const child = getNode(id);
    if (!child) continue;
    if (child.visible === false) continue;
    if (child.slotRole === "link-zone") continue;
    const b = rotatedBounds(child);
    minX = Math.min(minX, b.left);
    minY = Math.min(minY, b.top);
    maxX = Math.max(maxX, b.right);
    maxY = Math.max(maxY, b.bottom);
  }
  if (!Number.isFinite(minX)) return;
  const width = maxX - minX;
  const height = maxY - minY;
  const originMoved = minX !== 0 || minY !== 0;
  if (!originMoved && width === group.width && height === group.height) return;
  patches.set(groupId, {
    ...patches.get(groupId) ?? {},
    x: group.x + minX,
    y: group.y + minY,
    width,
    height
  });
  if (!originMoved) return;
  for (const id of childIds) {
    const child = getNode(id);
    if (!child) continue;
    patches.set(id, {
      ...patches.get(id) ?? {},
      x: child.x - minX,
      y: child.y - minY
    });
  }
}
function hugNoneContainer(containerId, getNode, patches) {
  const container = getNode(containerId);
  const childIds = container.children ?? [];
  let contentBottom = 0;
  let hasMeasurable = false;
  const backgroundIds = [];
  for (const id of childIds) {
    const child = getNode(id);
    if (!child) continue;
    if (child.visible === false) continue;
    if (child.slotRole === "link-zone") continue;
    if (child.pluginData?.["isBackground"] === true || child.constraintVertical === "TOP_BOTTOM" || child.slotRole === "background" || child.slotRole === "background-image") {
      backgroundIds.push(id);
      continue;
    }
    hasMeasurable = true;
    contentBottom = Math.max(contentBottom, rotatedBottom(child));
  }
  const legacyFloor = asNumber(container.pluginData?.["minHeight"]);
  const minH = container.minHeight ?? legacyFloor;
  const floor = minH ?? 0;
  const rawHeight = hasMeasurable ? Math.ceil(contentBottom) + (container.layoutPaddingBottom ?? 0) : floor > 0 ? floor : container.height;
  const newHeight = clampSize(rawHeight, minH, container.maxHeight);
  if (newHeight !== container.height) {
    patches.set(containerId, {
      ...patches.get(containerId) ?? {},
      height: newHeight
    });
  }
  for (const id of backgroundIds) {
    const bg = getNode(id);
    const stretched = Math.max(1, newHeight - bg.y);
    if (stretched !== bg.height) {
      patches.set(id, { ...patches.get(id) ?? {}, height: stretched });
    }
  }
}
function rotatedBounds(node) {
  const rotation = node.rotation ?? 0;
  if (!rotation) {
    return {
      left: node.x,
      top: node.y,
      right: node.x + node.width,
      bottom: node.y + node.height
    };
  }
  const rad = rotation * Math.PI / 180;
  const cx = node.x + node.width / 2;
  const cy = node.y + node.height / 2;
  const hw = node.width / 2;
  const hh = node.height / 2;
  const extentX = Math.abs(hw * Math.cos(rad)) + Math.abs(hh * Math.sin(rad));
  const extentY = Math.abs(hw * Math.sin(rad)) + Math.abs(hh * Math.cos(rad));
  return {
    left: cx - extentX,
    top: cy - extentY,
    right: cx + extentX,
    bottom: cy + extentY
  };
}
function rotatedBottom(node) {
  return rotatedBounds(node).bottom;
}
function commitDocSize(doc) {
  const laidOut = computeLayout(doc);
  if (doc.rootChildren.length !== 1) return laidOut;
  const root = laidOut.nodes[doc.rootChildren[0]];
  if (!root) return laidOut;
  if (laidOut.width === root.width && laidOut.height === root.height) return laidOut;
  return { ...laidOut, width: root.width, height: root.height };
}
function clampSize(value, min, max) {
  let v = value;
  if (typeof max === "number") v = Math.min(v, max);
  if (typeof min === "number") v = Math.max(v, min);
  return v;
}
function asNumber(value) {
  return typeof value === "number" ? value : void 0;
}
function layoutContainer(containerId, getNode, patches) {
  const container = getNode(containerId);
  if (container.layoutMode === "GRID") {
    layoutGridContainer(containerId, getNode, patches);
    return;
  }
  if (container.layoutMode === "HORIZONTAL" && container.layoutWrap === "WRAP" && container.layoutSizingHorizontal !== "HUG") {
    layoutWrapContainer(containerId, getNode, patches);
    return;
  }
  const childIds = (container.children ?? []).filter(
    (id) => getNode(id).layoutPositioning !== "ABSOLUTE"
  );
  if (childIds.length === 0) return;
  const isH = container.layoutMode === "HORIZONTAL";
  const gap = container.layoutGap ?? 0;
  const padTop = container.layoutPaddingTop ?? 0;
  const padRight = container.layoutPaddingRight ?? 0;
  const padBottom = container.layoutPaddingBottom ?? 0;
  const padLeft = container.layoutPaddingLeft ?? 0;
  const primaryAlign = container.primaryAxisAlign ?? "START";
  const counterAlign = container.counterAxisAlign ?? "START";
  const innerW = container.width - padLeft - padRight;
  const innerH = container.height - padTop - padBottom;
  const innerPrimary = isH ? innerW : innerH;
  const innerCounter = isH ? innerH : innerW;
  const children = childIds.map(getNode);
  const resolved = [];
  let fillCount = 0;
  let fixedPrimarySum = 0;
  for (const child of children) {
    const sizingP = isH ? child.layoutSizingHorizontal : child.layoutSizingVertical;
    const sizingC = isH ? child.layoutSizingVertical : child.layoutSizingHorizontal;
    const primaryMin = isH ? child.minWidth : child.minHeight;
    const primaryMax = isH ? child.maxWidth : child.maxHeight;
    const counterMin = isH ? child.minHeight : child.minWidth;
    const counterMax = isH ? child.maxHeight : child.maxWidth;
    const counter = clampSize(
      sizingC === "FILL" || counterAlign === "STRETCH" ? innerCounter : isH ? child.height : child.width,
      counterMin,
      counterMax
    );
    if (sizingP === "FILL") {
      fillCount++;
      resolved.push({ primary: 0, counter, fill: true, primaryMin, primaryMax });
    } else {
      const primary = clampSize(isH ? child.width : child.height, primaryMin, primaryMax);
      fixedPrimarySum += primary;
      resolved.push({ primary, counter, fill: false, primaryMin, primaryMax });
    }
  }
  const totalGap = gap * Math.max(0, childIds.length - 1);
  const remaining = innerPrimary - fixedPrimarySum - totalGap;
  const fillPrimary = fillCount > 0 ? Math.max(0, remaining / fillCount) : 0;
  for (const r7 of resolved) {
    if (r7.fill) r7.primary = clampSize(fillPrimary, r7.primaryMin, r7.primaryMax);
  }
  const totalUsed = resolved.reduce((s, r7) => s + r7.primary, 0) + totalGap;
  let start = 0;
  let extraGap = 0;
  switch (primaryAlign) {
    case "CENTER":
      start = Math.max(0, (innerPrimary - totalUsed) / 2);
      break;
    case "END":
      start = Math.max(0, innerPrimary - totalUsed);
      break;
    case "SPACE_BETWEEN":
      start = 0;
      if (childIds.length > 1) {
        extraGap = Math.max(
          0,
          (innerPrimary - totalUsed + totalGap) / (childIds.length - 1)
        );
      }
      break;
    default:
      start = 0;
  }
  let cursor = start;
  const gapUsed = primaryAlign === "SPACE_BETWEEN" ? extraGap : gap;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const { primary, counter } = resolved[i];
    let counterPos;
    switch (counterAlign) {
      case "CENTER":
        counterPos = Math.max(0, (innerCounter - counter) / 2);
        break;
      case "END":
        counterPos = Math.max(0, innerCounter - counter);
        break;
      default:
        counterPos = 0;
    }
    const newX = padLeft + (isH ? cursor : counterPos);
    const newY = padTop + (isH ? counterPos : cursor);
    const newW = isH ? primary : counter;
    const newH = isH ? counter : primary;
    patches.set(child.id, {
      ...patches.get(child.id) ?? {},
      x: newX,
      y: newY,
      width: newW,
      height: newH
    });
    cursor += primary + (i < children.length - 1 ? gapUsed : 0);
  }
  const containerSizingP = isH ? container.layoutSizingHorizontal : container.layoutSizingVertical;
  if (containerSizingP === "HUG") {
    const contentPrimary = resolved.reduce((s, r7) => s + r7.primary, 0) + totalGap + (isH ? padLeft + padRight : padTop + padBottom);
    const legacyFloor = !isH ? asNumber(container.pluginData?.["minHeight"]) : void 0;
    const primaryMin = (isH ? container.minWidth : container.minHeight) ?? legacyFloor;
    const primaryMax = isH ? container.maxWidth : container.maxHeight;
    const primary = clampSize(contentPrimary, primaryMin, primaryMax);
    patches.set(containerId, {
      ...patches.get(containerId) ?? {},
      ...isH ? { width: primary } : { height: primary }
    });
  }
}
function layoutWrapContainer(containerId, getNode, patches) {
  const container = getNode(containerId);
  const childIds = (container.children ?? []).filter(
    (id) => getNode(id).layoutPositioning !== "ABSOLUTE"
  );
  if (childIds.length === 0) return;
  const gap = container.layoutGap ?? 0;
  const lineSpacing = container.counterAxisSpacing ?? gap;
  const padTop = container.layoutPaddingTop ?? 0;
  const padRight = container.layoutPaddingRight ?? 0;
  const padBottom = container.layoutPaddingBottom ?? 0;
  const padLeft = container.layoutPaddingLeft ?? 0;
  const primaryAlign = container.primaryAxisAlign ?? "START";
  const counterAlign = container.counterAxisAlign ?? "START";
  const innerW = container.width - padLeft - padRight;
  const items = childIds.map((id) => {
    const child = getNode(id);
    const isFillWidth = child.layoutSizingHorizontal === "FILL";
    const stretch = child.layoutSizingVertical === "FILL" || counterAlign === "STRETCH";
    const width = clampSize(
      isFillWidth ? innerW : child.width,
      child.minWidth,
      child.maxWidth
    );
    const height = clampSize(child.height, child.minHeight, child.maxHeight);
    return { id, width, height, stretch, fullLine: isFillWidth };
  });
  const lines = [];
  let line = [];
  let running = 0;
  for (const item of items) {
    if (item.fullLine) {
      if (line.length) lines.push(line);
      lines.push([item]);
      line = [];
      running = 0;
      continue;
    }
    if (line.length === 0) {
      line = [item];
      running = item.width;
    } else if (running + gap + item.width <= innerW) {
      line.push(item);
      running += gap + item.width;
    } else {
      lines.push(line);
      line = [item];
      running = item.width;
    }
  }
  if (line.length) lines.push(line);
  let lineY = padTop;
  const lineHeights = [];
  for (let li = 0; li < lines.length; li++) {
    const ln = lines[li];
    const lineHeight = ln.reduce((m, it) => Math.max(m, it.height), 0);
    lineHeights.push(lineHeight);
    const sumW = ln.reduce((s, it) => s + it.width, 0);
    const count = ln.length;
    let start = 0;
    let gapUsed = gap;
    if (primaryAlign === "SPACE_BETWEEN" && count > 1) {
      gapUsed = Math.max(0, (innerW - sumW) / (count - 1));
    } else {
      const lineUsed = sumW + gap * Math.max(0, count - 1);
      switch (primaryAlign) {
        case "CENTER":
          start = Math.max(0, (innerW - lineUsed) / 2);
          break;
        case "END":
          start = Math.max(0, innerW - lineUsed);
          break;
        default:
          start = 0;
      }
    }
    let cursorX = start;
    for (let ci = 0; ci < ln.length; ci++) {
      const it = ln[ci];
      const h = it.stretch ? lineHeight : it.height;
      let offsetY;
      switch (counterAlign) {
        case "CENTER":
          offsetY = Math.max(0, (lineHeight - h) / 2);
          break;
        case "END":
          offsetY = Math.max(0, lineHeight - h);
          break;
        default:
          offsetY = 0;
      }
      patches.set(it.id, {
        ...patches.get(it.id) ?? {},
        x: padLeft + cursorX,
        y: lineY + offsetY,
        width: it.width,
        height: h
      });
      cursorX += it.width + (ci < ln.length - 1 ? gapUsed : 0);
    }
    lineY += lineHeight + (li < lines.length - 1 ? lineSpacing : 0);
  }
  if (container.layoutSizingVertical === "HUG") {
    const contentHeight = lineHeights.reduce((s, h) => s + h, 0) + lineSpacing * Math.max(0, lines.length - 1) + padTop + padBottom;
    const legacyFloor = asNumber(container.pluginData?.["minHeight"]);
    const minH = container.minHeight ?? legacyFloor;
    patches.set(containerId, {
      ...patches.get(containerId) ?? {},
      height: clampSize(contentHeight, minH, container.maxHeight)
    });
  }
}
function layoutGridContainer(containerId, getNode, patches) {
  const container = getNode(containerId);
  const childIds = (container.children ?? []).filter(
    (id) => getNode(id).layoutPositioning !== "ABSOLUTE"
  );
  if (childIds.length === 0) return;
  const cols = Math.max(1, Math.floor(container.gridColumnCount ?? 1));
  const layoutGap = container.layoutGap ?? 0;
  const colGap = container.gridColumnGap ?? layoutGap;
  const rowGap = container.gridRowGap ?? layoutGap;
  const padTop = container.layoutPaddingTop ?? 0;
  const padRight = container.layoutPaddingRight ?? 0;
  const padBottom = container.layoutPaddingBottom ?? 0;
  const padLeft = container.layoutPaddingLeft ?? 0;
  const innerW = container.width - padLeft - padRight;
  const colWidth = (innerW - colGap * (cols - 1)) / cols;
  const occupied = /* @__PURE__ */ new Set();
  const cellKey = (r7, c) => `${r7},${c}`;
  const fits = (r7, c, sr, sc) => {
    if (c < 0 || r7 < 0 || c + sc > cols) return false;
    for (let rr = r7; rr < r7 + sr; rr++) {
      for (let cc = c; cc < c + sc; cc++) {
        if (occupied.has(cellKey(rr, cc))) return false;
      }
    }
    return true;
  };
  const occupy = (r7, c, sr, sc) => {
    for (let rr = r7; rr < r7 + sr; rr++) {
      for (let cc = c; cc < c + sc; cc++) occupied.add(cellKey(rr, cc));
    }
  };
  const placements = [];
  const spanOf = (child) => ({
    sr: Math.max(1, Math.floor(child.gridRowSpan ?? 1)),
    sc: Math.min(cols, Math.max(1, Math.floor(child.gridColumnSpan ?? 1)))
  });
  const children = childIds.map(getNode);
  const autoChildren = [];
  for (const child of children) {
    const { sr, sc } = spanOf(child);
    if (child.gridColumnStart != null && child.gridRowStart != null) {
      const c = Math.min(cols - sc, Math.max(0, Math.floor(child.gridColumnStart) - 1));
      const r7 = Math.max(0, Math.floor(child.gridRowStart) - 1);
      occupy(r7, c, sr, sc);
      placements.push({ id: child.id, r: r7, c, sr, sc });
    } else {
      autoChildren.push(child);
    }
  }
  let cursorR = 0;
  let cursorC = 0;
  for (const child of autoChildren) {
    const { sr, sc } = spanOf(child);
    let r7;
    let c;
    if (child.gridColumnStart != null) {
      c = Math.min(cols - sc, Math.max(0, Math.floor(child.gridColumnStart) - 1));
      r7 = 0;
      while (!fits(r7, c, sr, sc)) r7++;
    } else if (child.gridRowStart != null) {
      r7 = Math.max(0, Math.floor(child.gridRowStart) - 1);
      c = 0;
      while (c <= cols - sc && !fits(r7, c, sr, sc)) c++;
      if (c > cols - sc) c = 0;
    } else {
      r7 = cursorR;
      c = cursorC;
      while (!fits(r7, c, sr, sc)) {
        c++;
        if (c + sc > cols) {
          c = 0;
          r7++;
        }
      }
      cursorR = r7;
      cursorC = c + sc;
      if (cursorC >= cols) {
        cursorR = r7 + 1;
        cursorC = 0;
      }
    }
    occupy(r7, c, sr, sc);
    placements.push({ id: child.id, r: r7, c, sr, sc });
  }
  const derivedRows = placements.reduce((m, p) => Math.max(m, p.r + p.sr), 0);
  const rowCount = Math.max(derivedRows, Math.floor(container.gridRowCount ?? 0));
  const rowHeights = new Array(rowCount).fill(0);
  for (const p of placements) {
    const child = getNode(p.id);
    const perRow = p.sr <= 1 ? child.height : Math.max(0, (child.height - rowGap * (p.sr - 1)) / p.sr);
    for (let rr = p.r; rr < p.r + p.sr; rr++) {
      rowHeights[rr] = Math.max(rowHeights[rr], perRow);
    }
  }
  const rowTop = new Array(rowCount).fill(0);
  for (let rr = 1; rr < rowCount; rr++) {
    rowTop[rr] = rowTop[rr - 1] + rowHeights[rr - 1] + rowGap;
  }
  for (const p of placements) {
    const x = padLeft + p.c * (colWidth + colGap);
    const y = padTop + rowTop[p.r];
    const w = colWidth * p.sc + colGap * (p.sc - 1);
    let h = rowGap * (p.sr - 1);
    for (let rr = p.r; rr < p.r + p.sr; rr++) h += rowHeights[rr];
    patches.set(p.id, {
      ...patches.get(p.id) ?? {},
      x,
      y,
      width: Math.max(0, w),
      height: Math.max(0, h)
    });
  }
  if (container.layoutSizingVertical === "HUG") {
    const contentHeight = rowHeights.reduce((s, hh) => s + hh, 0) + rowGap * Math.max(0, rowCount - 1) + padTop + padBottom;
    const legacyFloor = asNumber(container.pluginData?.["minHeight"]);
    const minH = container.minHeight ?? legacyFloor;
    patches.set(containerId, {
      ...patches.get(containerId) ?? {},
      height: clampSize(contentHeight, minH, container.maxHeight)
    });
  }
}
function applyConstraints(doc, frameId, oldBounds, newBounds) {
  const frame = doc.nodes[frameId];
  if (!frame?.children?.length) return doc;
  const dW = newBounds.width - oldBounds.width;
  const dH = newBounds.height - oldBounds.height;
  if (dW === 0 && dH === 0) return doc;
  const isGroup2 = frame.type === "GROUP";
  const hDefault = isGroup2 ? "SCALE" : "LEFT";
  const vDefault = isGroup2 ? "SCALE" : "TOP";
  const patches = /* @__PURE__ */ new Map();
  for (const childId of frame.children) {
    const child = doc.nodes[childId];
    if (!child) continue;
    const cx = constrainAxis(
      child.x,
      child.width,
      oldBounds.width,
      newBounds.width,
      child.constraintHorizontal ?? hDefault
    );
    const cy = constrainAxis(
      child.y,
      child.height,
      oldBounds.height,
      newBounds.height,
      child.constraintVertical ?? vDefault
    );
    patches.set(childId, { x: cx.pos, y: cy.pos, width: cx.size, height: cy.size });
  }
  if (patches.size === 0) return doc;
  const nextNodes = { ...doc.nodes };
  for (const [id, patch] of patches) {
    nextNodes[id] = { ...nextNodes[id], ...patch };
  }
  let next = { ...doc, nodes: nextNodes };
  for (const [id, patch] of patches) {
    const before = doc.nodes[id];
    if (!before?.children?.length) continue;
    if (before.layoutMode && before.layoutMode !== "NONE") continue;
    if (before.slotRole === "link-zone") continue;
    if (patch.width === before.width && patch.height === before.height) continue;
    next = applyConstraints(
      next,
      id,
      { x: before.x, y: before.y, width: before.width, height: before.height },
      { x: patch.x, y: patch.y, width: patch.width, height: patch.height }
    );
  }
  return next;
}
function constrainAxis(pos, size, oldParent, newParent, constraint) {
  const ratio = oldParent > 0 ? newParent / oldParent : 1;
  switch (constraint) {
    case "LEFT":
    case "TOP":
      return { pos, size };
    case "RIGHT":
    case "BOTTOM": {
      const edge = oldParent - (pos + size);
      return { pos: newParent - size - edge, size };
    }
    case "CENTER": {
      const center = pos + size / 2;
      const ratio2 = oldParent > 0 ? center / oldParent : 0.5;
      const newCenter = ratio2 * newParent;
      return { pos: newCenter - size / 2, size };
    }
    case "LEFT_RIGHT":
    case "TOP_BOTTOM": {
      const trailingEdge = oldParent - (pos + size);
      const newSize = Math.max(1, newParent - pos - trailingEdge);
      return { pos, size: newSize };
    }
    case "SCALE": {
      const newPos = pos * ratio;
      const newSize = size * ratio;
      return { pos: newPos, size: newSize };
    }
    default:
      return { pos, size };
  }
}

// src/types/style-config.type.ts
import { z as z2 } from "zod";

// src/types/canvas-composition.type.ts
import { z } from "zod";
var GradientStopSchema = z.object({
  color: z.string(),
  position: z.number().min(0).max(100)
});
var GradientSchema = z.object({
  type: z.enum(["linear", "radial"]),
  angle: z.number().default(90),
  // for linear gradients
  stops: z.array(GradientStopSchema).min(2)
});
var BaseObjectSchema = z.object({
  id: z.string(),
  x: z.number(),
  // pixels from left
  y: z.number(),
  // pixels from top
  width: z.number(),
  height: z.number(),
  rotation: z.number().default(0),
  // degrees
  opacity: z.number().min(0).max(1).default(1),
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  zIndex: z.number().default(0)
});
var CanvasImageSchema = BaseObjectSchema.extend({
  type: z.literal("image"),
  src: z.string(),
  // fit is deprecated and no longer exposed in the UI - kept optional for backward compatibility
  fit: z.enum(["cover", "contain", "fill", "none"]).optional(),
  borderRadius: z.number().default(0)
});
var TextShadowSchema = z.object({
  enabled: z.boolean().default(false),
  color: z.string().default("rgba(0,0,0,0.5)"),
  offsetX: z.number().default(2),
  offsetY: z.number().default(2),
  blur: z.number().default(4)
});
var TextBackgroundSchema = z.object({
  enabled: z.boolean().default(false),
  color: z.string().default("rgba(0,0,0,0.5)"),
  padding: z.number().default(10),
  borderRadius: z.number().default(0)
});
var CanvasTextSchema = BaseObjectSchema.extend({
  type: z.literal("text"),
  content: z.string(),
  fontFamily: z.string().default("Arial"),
  fontSize: z.number().default(32),
  fontWeight: z.union([z.string(), z.number()]).default(400),
  fontStyle: z.enum(["normal", "italic"]).default("normal"),
  color: z.string().default("#000000"),
  colorGradient: GradientSchema.optional(),
  // gradient fill for text (overrides color when set)
  textAlign: z.enum(["left", "center", "right"]).default("left"),
  verticalAlign: z.enum(["top", "middle", "bottom"]).default("top"),
  lineHeight: z.number().default(1.2),
  letterSpacing: z.number().default(0),
  textTransform: z.enum(["none", "uppercase", "lowercase", "capitalize"]).default("none"),
  textDecoration: z.enum(["none", "underline", "line-through"]).default("none"),
  shadow: TextShadowSchema.optional(),
  background: TextBackgroundSchema.optional()
});
var CanvasShapeSchema = BaseObjectSchema.extend({
  type: z.literal("shape"),
  shapeType: z.enum([
    "rectangle",
    "circle",
    "ellipse",
    "line",
    "triangle",
    "star",
    "polygon",
    // Decorative divider shapes
    "dividerWave",
    "dividerCurve",
    "dividerTriangle",
    "dividerArrow",
    "dividerZigzag",
    "dividerDiagonal",
    "dividerTear"
    // Paper tear effect
  ]),
  fill: z.string().optional(),
  fillGradient: GradientSchema.optional(),
  // gradient fill (overrides fill when set)
  stroke: z.string().optional(),
  strokeGradient: GradientSchema.optional(),
  // gradient stroke (overrides stroke when set)
  strokeWidth: z.number().default(0),
  borderRadius: z.number().default(0),
  // for rectangles
  // Polygon-specific properties
  sides: z.number().min(3).max(12).default(6).optional(),
  // number of sides (pentagon=5, hexagon=6, etc.)
  // Star-specific properties
  starPoints: z.number().min(3).max(12).default(5).optional(),
  // number of star points
  innerRadiusRatio: z.number().min(0.1).max(0.9).default(0.5).optional(),
  // ratio of inner to outer radius
  // Divider-specific properties
  flip: z.boolean().default(false).optional(),
  // flip horizontally
  invert: z.boolean().default(false).optional()
  // flip vertically (invert the shape)
});
var CanvasOverlaySchema = BaseObjectSchema.extend({
  type: z.literal("overlay"),
  color: z.string().default("rgba(0,0,0,0.5)"),
  blendMode: z.enum([
    "normal",
    "multiply",
    "screen",
    "overlay",
    "darken",
    "lighten",
    "color-dodge",
    "color-burn",
    "hard-light",
    "soft-light",
    "difference",
    "exclusion",
    "hue",
    "saturation",
    "color",
    "luminosity"
  ]).default("normal")
});
var CanvasGroupSchema = BaseObjectSchema.extend({
  type: z.literal("group"),
  name: z.string().default("Group"),
  // Children positions are relative to the group's x,y
  children: z.lazy(() => z.array(CanvasObjectSchema)).default([]),
  // Frame options (Figma-style)
  clipContent: z.boolean().default(false),
  // Clip children to group bounds
  showFrame: z.boolean().default(false),
  // Show a visible border/background
  frameColor: z.string().optional(),
  // Frame border color
  frameFill: z.string().optional(),
  // Frame background fill
  frameRadius: z.number().default(0)
  // Frame corner radius
});
var CanvasObjectSchema = z.union([
  CanvasImageSchema,
  CanvasTextSchema,
  CanvasShapeSchema,
  CanvasOverlaySchema,
  CanvasGroupSchema
]);
var CanvasCompositionSchema = z.object({
  version: z.literal(1),
  width: z.number().default(600),
  // email standard width
  height: z.number().default(400),
  backgroundColor: z.string().default("#ffffff"),
  backgroundGradient: GradientSchema.optional(),
  // gradient background (overrides backgroundColor when set)
  objects: z.array(CanvasObjectSchema).default([])
});
function createEmptyComposition(width = 600, height = 400) {
  return {
    version: 1,
    width,
    height,
    backgroundColor: "#ffffff",
    objects: []
  };
}
function generateObjectId() {
  return `obj_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}
function createTextObject(content, x, y, options) {
  return {
    type: "text",
    id: generateObjectId(),
    content,
    x,
    y,
    width: 200,
    height: 50,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex: 0,
    fontFamily: "Arial",
    fontSize: 32,
    fontWeight: 400,
    fontStyle: "normal",
    color: "#000000",
    textAlign: "left",
    verticalAlign: "top",
    lineHeight: 1.2,
    letterSpacing: 0,
    textTransform: "none",
    textDecoration: "none",
    ...options
  };
}
function createImageObject(src, x, y, width, height, options) {
  return {
    type: "image",
    id: generateObjectId(),
    src,
    x,
    y,
    width,
    height,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex: 0,
    borderRadius: 0,
    ...options
  };
}
function createShapeObject(shapeType, x, y, width, height, options) {
  return {
    type: "shape",
    id: generateObjectId(),
    shapeType,
    x,
    y,
    width,
    height,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex: 0,
    strokeWidth: 0,
    borderRadius: 0,
    ...options
  };
}
function createOverlayObject(color, compositionWidth, compositionHeight, options) {
  return {
    type: "overlay",
    id: generateObjectId(),
    color,
    x: 0,
    y: 0,
    width: compositionWidth,
    height: compositionHeight,
    rotation: 0,
    opacity: 0.5,
    visible: true,
    locked: false,
    zIndex: 0,
    blendMode: "normal",
    ...options
  };
}
function getDividerShapeTypes() {
  return [
    "dividerWave",
    "dividerCurve",
    "dividerTriangle",
    "dividerArrow",
    "dividerZigzag",
    "dividerDiagonal",
    "dividerTear"
  ];
}
function getDividerSvgPath(shapeType, flip = false, invert = false) {
  let path3;
  switch (shapeType) {
    case "dividerWave":
      path3 = "M0,50 Q25,20 50,50 T100,50 L100,100 L0,100 Z";
      break;
    case "dividerCurve":
      path3 = "M0,100 Q50,0 100,100 L100,100 L0,100 Z";
      break;
    case "dividerTriangle":
      path3 = "M0,100 L50,0 L100,100 Z";
      break;
    case "dividerArrow":
      path3 = "M0,100 L50,20 L100,100 Z";
      break;
    case "dividerZigzag":
      path3 = "M0,100 L12.5,0 L25,100 L37.5,0 L50,100 L62.5,0 L75,100 L87.5,0 L100,100 Z";
      break;
    case "dividerDiagonal":
      path3 = "M0,100 L100,0 L100,100 Z";
      break;
    case "dividerTear":
      path3 = "M0,70 L5,65 L8,72 L15,60 L20,68 L25,55 L32,65 L38,50 L45,62 L50,45 L55,58 L62,42 L68,55 L75,40 L80,52 L85,38 L92,50 L95,35 L100,45 L100,100 L0,100 Z";
      break;
    default:
      path3 = "M0,50 Q25,20 50,50 T100,50 L100,100 L0,100 Z";
  }
  return path3;
}
function sortByZIndex(objects) {
  return [...objects].sort((a, b) => a.zIndex - b.zIndex);
}
function validateComposition(data) {
  return CanvasCompositionSchema.parse(data);
}
function safeParseComposition(data) {
  const result = CanvasCompositionSchema.safeParse(data);
  return result.success ? result.data : null;
}
function calculateBoundingBox(objects) {
  if (objects.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const obj of objects) {
    minX = Math.min(minX, obj.x);
    minY = Math.min(minY, obj.y);
    maxX = Math.max(maxX, obj.x + obj.width);
    maxY = Math.max(maxY, obj.y + obj.height);
  }
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}
function createGroupFromObjects(objects, groupName = "Group") {
  if (objects.length === 0) {
    throw new Error("Cannot create group from empty array");
  }
  const bounds = calculateBoundingBox(objects);
  const removedIds = objects.map((obj) => obj.id);
  const children = objects.map((obj, index) => ({
    ...obj,
    x: obj.x - bounds.x,
    y: obj.y - bounds.y,
    zIndex: index
  }));
  const group = {
    type: "group",
    id: generateObjectId(),
    name: groupName,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex: 0,
    children,
    clipContent: false,
    showFrame: false,
    frameRadius: 0
  };
  return { group, removedIds };
}
function ungroupToObjects(group) {
  return group.children.map((child) => ({
    ...child,
    x: child.x + group.x,
    y: child.y + group.y
  }));
}
function createGroupObject(x, y, width, height, options) {
  return {
    type: "group",
    id: generateObjectId(),
    name: "Group",
    x,
    y,
    width,
    height,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex: 0,
    children: [],
    clipContent: false,
    showFrame: false,
    frameRadius: 0,
    ...options
  };
}
function findObjectById(objects, id, path3 = []) {
  for (const obj of objects) {
    if (obj.id === id) {
      return { object: obj, path: path3 };
    }
    if (obj.type === "group") {
      const found = findObjectById(obj.children, id, [...path3, obj.id]);
      if (found) return found;
    }
  }
  return null;
}
function updateObjectInTree(objects, id, updates) {
  return objects.map((obj) => {
    if (obj.id === id) {
      return { ...obj, ...updates };
    }
    if (obj.type === "group") {
      return {
        ...obj,
        children: updateObjectInTree(obj.children, id, updates)
      };
    }
    return obj;
  });
}
function removeObjectFromTree(objects, id) {
  let removed = null;
  const newObjects = objects.filter((obj) => {
    if (obj.id === id) {
      removed = obj;
      return false;
    }
    return true;
  });
  if (!removed) {
    return {
      objects: newObjects.map((obj) => {
        if (obj.type === "group") {
          const result = removeObjectFromTree(obj.children, id);
          if (result.removed) {
            removed = result.removed;
            return { ...obj, children: result.objects };
          }
        }
        return obj;
      }),
      removed
    };
  }
  return { objects: newObjects, removed };
}
function moveObjectToParent(objects, objectId, newParentId, insertIndex = 0) {
  const { objects: afterRemoval, removed } = removeObjectFromTree(objects, objectId);
  if (!removed) return objects;
  const sourceResult = findObjectById(objects, objectId);
  let absoluteX = removed.x;
  let absoluteY = removed.y;
  if (sourceResult && sourceResult.path.length > 0) {
    for (const parentId of sourceResult.path) {
      const parentResult = findObjectById(objects, parentId);
      if (parentResult && parentResult.object.type === "group") {
        absoluteX += parentResult.object.x;
        absoluteY += parentResult.object.y;
      }
    }
  }
  let newX = absoluteX;
  let newY = absoluteY;
  if (newParentId) {
    const newParentResult = findObjectById(afterRemoval, newParentId);
    if (newParentResult && newParentResult.object.type === "group") {
      newX = absoluteX - newParentResult.object.x;
      newY = absoluteY - newParentResult.object.y;
    }
  }
  const movedObject = {
    ...removed,
    x: newX,
    y: newY
  };
  if (newParentId === null) {
    const result = [...afterRemoval];
    result.splice(insertIndex, 0, movedObject);
    return result;
  }
  return afterRemoval.map((obj) => {
    if (obj.id === newParentId && obj.type === "group") {
      const newChildren = [...obj.children];
      newChildren.splice(insertIndex, 0, movedObject);
      return { ...obj, children: newChildren };
    }
    if (obj.type === "group") {
      return {
        ...obj,
        children: moveObjectToParent(obj.children, objectId, newParentId, insertIndex)
      };
    }
    return obj;
  });
}
function flattenComposition(objects, parentOffset = { x: 0, y: 0 }) {
  const result = [];
  for (const obj of sortByZIndex(objects)) {
    if (obj.type === "group") {
      const groupOffset = {
        x: parentOffset.x + obj.x,
        y: parentOffset.y + obj.y
      };
      result.push(...flattenComposition(obj.children, groupOffset));
    } else {
      result.push({
        ...obj,
        x: obj.x + parentOffset.x,
        y: obj.y + parentOffset.y
      });
    }
  }
  return result;
}
function isGroup(obj) {
  return obj.type === "group";
}
function recalculateGroupBounds(group) {
  if (group.children.length === 0) {
    return group;
  }
  const childBounds = calculateBoundingBox(group.children);
  const offsetX = childBounds.x;
  const offsetY = childBounds.y;
  const adjustedChildren = group.children.map((child) => ({
    ...child,
    x: child.x - offsetX,
    y: child.y - offsetY
  }));
  return {
    ...group,
    x: group.x + offsetX,
    y: group.y + offsetY,
    width: childBounds.width,
    height: childBounds.height,
    children: adjustedChildren
  };
}

// src/types/style-config.type.ts
var ButtonConfigSchema = z2.object({
  text: z2.string(),
  href: z2.string(),
  backgroundColor: z2.string().optional(),
  backgroundImage: z2.string().optional(),
  // Background image URL for textured buttons
  color: z2.string().optional(),
  borderRadius: z2.string().optional(),
  padding: z2.string().optional(),
  innerPadding: z2.string().optional(),
  // MJML inner-padding (distinct from outer padding)
  fontSize: z2.string().optional(),
  fontWeight: z2.union([z2.string(), z2.number()]).optional(),
  width: z2.string().optional(),
  height: z2.string().optional(),
  // Fixed height for buttons with background images
  alignment: z2.enum(["left", "center", "right"]).optional(),
  border: z2.string().optional(),
  hoverBackgroundColor: z2.string().optional(),
  hoverColor: z2.string().optional()
});
var ImageConfigSchema = z2.object({
  src: z2.string(),
  originalSrc: z2.string().optional(),
  // Untreated original image URL (for reverting edge treatment)
  edgeTreatedSrc: z2.string().optional(),
  // Cached edge-treated image URL
  alt: z2.string().optional(),
  width: z2.string().optional(),
  height: z2.string().optional(),
  href: z2.string().optional(),
  alignment: z2.enum(["left", "center", "right"]).optional(),
  objectFit: z2.enum(["cover", "contain", "fill", "none", "scale-down"]).optional(),
  fullWidth: z2.boolean().optional(),
  // MJML full-width attribute
  fluidOnMobile: z2.boolean().optional(),
  // MJML fluid-on-mobile attribute
  // AI image generation fields
  imagePrompt: z2.string().optional(),
  // Detailed prompt for AI image generation (data-nm-image-prompt)
  imageEdge: z2.string().optional(),
  // Edge treatment: curved-bottom, wave-top, etc. (data-nm-image-edge)
  // Canvas composition (Figma-like editor for text overlays, shapes, etc.)
  canvasComposition: CanvasCompositionSchema.optional(),
  // Full canvas state with all objects
  composedSrc: z2.string().optional(),
  // Baked image URL (rendered from canvas composition)
  composedHash: z2.string().optional()
  // Hash of composition for staleness detection
});
var GradientConfigSchema = z2.object({
  enabled: z2.boolean(),
  type: z2.enum(["linear", "radial"]),
  colors: z2.string(),
  // Comma-separated: "#fff, #000"
  direction: z2.string().optional(),
  // For linear: "180deg", "to bottom", etc.
  position: z2.string().optional(),
  // For radial: "center", "top left", etc.
  shape: z2.enum(["circle", "ellipse"]).optional()
  // For radial: shape of gradient (defaults to circle)
});
var OverlayConfigSchema = z2.object({
  enabled: z2.boolean(),
  color: z2.string(),
  opacity: z2.number().min(0).max(1),
  // 0-1
  blendMode: z2.string().optional()
  // CSS blend mode: "multiply", "overlay", etc.
});
var ShapeDividerConfigSchema = z2.object({
  enabled: z2.boolean(),
  type: z2.enum([
    "wave",
    "curve",
    "triangle",
    "arrow",
    "zigzag",
    "diagonal",
    "scallop",
    "organic",
    "tilt",
    "mountains",
    "drops",
    "clouds"
  ]),
  color: z2.string(),
  height: z2.string(),
  position: z2.enum(["top", "bottom"]),
  flip: z2.boolean().optional(),
  invert: z2.boolean().optional()
});
var TransitionImageConfigSchema = z2.object({
  enabled: z2.boolean(),
  transitionSrc: z2.string().optional(),
  transparentBackgroundColor: z2.string().optional()
});
var HeaderBarLinkSchema = z2.object({
  text: z2.string(),
  href: z2.string(),
  color: z2.string().optional()
});
var HeaderBarConfigSchema = z2.object({
  links: z2.array(HeaderBarLinkSchema),
  backgroundColor: z2.string().optional(),
  alignment: z2.enum(["left", "center", "right", "space-between"]).optional()
});
var SocialIconSchema = z2.object({
  platform: z2.enum([
    "facebook",
    "twitter",
    "instagram",
    "linkedin",
    "youtube",
    "pinterest",
    "tiktok",
    "github",
    "email"
  ]),
  href: z2.string()
});
var SocialIconsConfigSchema = z2.object({
  icons: z2.array(SocialIconSchema),
  iconSize: z2.string().optional(),
  iconSpacing: z2.string().optional(),
  iconColor: z2.string().optional(),
  backgroundColor: z2.string().optional(),
  mode: z2.enum(["horizontal", "vertical"]).optional()
  // MJML mj-social mode attribute
});
var ProductConfigSchema = z2.object({
  productId: z2.string().optional(),
  title: z2.string().optional(),
  price: z2.string().optional(),
  imageUrl: z2.string().optional(),
  productUrl: z2.string().optional(),
  description: z2.string().optional(),
  showButton: z2.boolean().optional(),
  buttonText: z2.string().optional(),
  buttonBackgroundColor: z2.string().optional()
});
var TableConfigSchema = z2.object({
  rows: z2.number(),
  columns: z2.number(),
  cellPadding: z2.string().optional(),
  borderColor: z2.string().optional(),
  borderWidth: z2.string().optional(),
  headerBackgroundColor: z2.string().optional(),
  evenRowBackgroundColor: z2.string().optional(),
  oddRowBackgroundColor: z2.string().optional()
});
var HtmlConfigSchema = z2.object({
  rawHtml: z2.string()
});
var DividerConfigSchema = z2.object({
  width: z2.string().optional(),
  borderWidth: z2.string().optional(),
  borderStyle: z2.enum(["solid", "dashed", "dotted", "double"]).optional(),
  borderColor: z2.string().optional(),
  alignment: z2.enum(["left", "center", "right"]).optional()
});
var SpacerConfigSchema = z2.object({
  height: z2.string()
});
var PaddingObjectSchema = z2.object({
  top: z2.string().optional(),
  right: z2.string().optional(),
  bottom: z2.string().optional(),
  left: z2.string().optional()
});
var StyleConfigSchema = z2.object({
  // Layout
  padding: z2.union([z2.string(), PaddingObjectSchema]).optional(),
  // MJML shorthand or object
  margin: z2.union([z2.string(), PaddingObjectSchema]).optional(),
  width: z2.string().optional(),
  maxWidth: z2.string().optional(),
  // Maximum width constraint
  height: z2.string().optional(),
  minHeight: z2.string().optional(),
  // For sections with background images that need minimum height
  // Typography
  fontSize: z2.string().optional(),
  fontWeight: z2.union([z2.string(), z2.number()]).optional(),
  fontFamily: z2.string().optional(),
  color: z2.string().optional(),
  lineHeight: z2.string().optional(),
  letterSpacing: z2.string().optional(),
  textAlign: z2.enum(["left", "center", "right", "justify"]).optional(),
  textDecoration: z2.string().optional(),
  fontStyle: z2.enum(["normal", "italic", "oblique"]).optional(),
  textTransform: z2.enum(["none", "uppercase", "lowercase", "capitalize"]).optional(),
  // Background
  backgroundColor: z2.string().optional(),
  backgroundImage: z2.string().optional(),
  backgroundSize: z2.string().optional(),
  backgroundPosition: z2.string().optional(),
  backgroundRepeat: z2.string().optional(),
  // Border
  border: z2.string().optional(),
  // Shorthand: "1px solid #000"
  borderRadius: z2.string().optional(),
  borderTop: z2.string().optional(),
  borderRight: z2.string().optional(),
  borderBottom: z2.string().optional(),
  borderLeft: z2.string().optional(),
  borderWidth: z2.string().optional(),
  // Individual border width
  borderStyle: z2.enum(["solid", "dashed", "dotted", "double", "none"]).optional(),
  // Individual border style
  borderColor: z2.string().optional(),
  // Individual border color
  // Block-type specific configurations
  button: ButtonConfigSchema.optional(),
  image: ImageConfigSchema.optional(),
  gradient: GradientConfigSchema.optional(),
  overlay: OverlayConfigSchema.optional(),
  shapeDivider: ShapeDividerConfigSchema.optional(),
  transitionImage: TransitionImageConfigSchema.optional(),
  headerBar: HeaderBarConfigSchema.optional(),
  socialIcons: SocialIconsConfigSchema.optional(),
  product: ProductConfigSchema.optional(),
  table: TableConfigSchema.optional(),
  html: HtmlConfigSchema.optional(),
  divider: DividerConfigSchema.optional(),
  spacer: SpacerConfigSchema.optional(),
  // Advanced layout
  verticalAlign: z2.enum(["top", "middle", "bottom"]).optional(),
  zIndex: z2.number().optional(),
  opacity: z2.number().min(0).max(1).optional(),
  boxShadow: z2.string().optional(),
  blendMode: z2.string().optional(),
  // CSS mix-blend-mode
  // Container-specific properties (for sections)
  containerBackgroundColor: z2.string().optional(),
  // MJML container-background-color for sections
  containerWidth: z2.string().optional(),
  // Container width for sections
  // Direct overlay properties (alternative to nested overlay config)
  overlayColor: z2.string().optional(),
  // Overlay color for sections
  overlayOpacity: z2.number().min(0).max(1).optional(),
  // Overlay opacity for sections
  // Responsive
  mobileHidden: z2.boolean().optional(),
  desktopHidden: z2.boolean().optional(),
  cssClass: z2.string().optional(),
  // MJML css-class attribute (e.g., mobile-stack, mobile-hide)
  className: z2.string().optional(),
  // Additional CSS class name
  // Custom CSS
  customCss: z2.string().optional(),
  customClasses: z2.array(z2.string()).optional(),
  // Sub-blocks (for complex nested structures like header bars)
  subBlocks: z2.array(z2.any()).optional()
  // Keep as-is from existing schema
});
function mergeStyleConfig(base, updates) {
  return {
    ...base || {},
    ...updates
  };
}
function validateStyleConfig(config) {
  return StyleConfigSchema.parse(config);
}
function safeParseStyleConfig(config) {
  const result = StyleConfigSchema.safeParse(config);
  return result.success ? result.data : null;
}
function parsePaddingShorthand(padding) {
  if (!padding) return null;
  const parts = padding.trim().split(/\s+/);
  if (parts.length === 1) {
    return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
  } else if (parts.length === 2) {
    return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
  } else if (parts.length === 3) {
    return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
  } else if (parts.length === 4) {
    return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
  }
  return null;
}
function buildPaddingShorthand(top, right, bottom, left) {
  if (!top && !right && !bottom && !left) return void 0;
  const t2 = top || "0";
  const r7 = right || "0";
  const b = bottom || "0";
  const l = left || "0";
  if (t2 === r7 && r7 === b && b === l) {
    return t2;
  } else if (t2 === b && r7 === l) {
    return `${t2} ${r7}`;
  } else if (r7 === l) {
    return `${t2} ${r7} ${b}`;
  } else {
    return `${t2} ${r7} ${b} ${l}`;
  }
}

// src/visual-rendering/band-detection.ts
var OVERLAP_PX = 2;
var SPANNING_SHARE = 0.7;
function bandifyAbsoluteScene(doc) {
  const untouched = {
    doc,
    bandIds: [],
    bridgingNodeIds: [],
    spanningNodeIds: [],
    changed: false
  };
  if (doc.rootChildren.length !== 1) return untouched;
  const root = doc.nodes[doc.rootChildren[0]];
  if (!root || root.type !== "FRAME") return untouched;
  if (root.layoutMode && root.layoutMode !== "NONE") return untouched;
  const childIds = root.children ?? [];
  if (childIds.length < 2) return untouched;
  const children = childIds.map((id) => doc.nodes[id]).filter((n) => !!n);
  const visibleChildren = children.filter((n) => n.visible !== false);
  if (!visibleChildren.length) return untouched;
  const contentTop = Math.min(...visibleChildren.map((n) => n.y));
  const contentBottom = Math.max(...visibleChildren.map((n) => n.y + n.height));
  const contentHeight = Math.max(1, contentBottom - contentTop);
  const spanningNodeIds = [];
  const hiddenIds = [];
  const items = [];
  for (const n of children) {
    if (n.visible === false) {
      hiddenIds.push(n.id);
      continue;
    }
    if (isBackgroundish(n) || n.height >= contentHeight * SPANNING_SHARE) {
      spanningNodeIds.push(n.id);
      continue;
    }
    items.push({ id: n.id, top: n.y, bottom: n.y + n.height });
  }
  if (items.length < 2) return untouched;
  let clusters = clusterByOverlap(items);
  const bridgingNodeIds = [];
  let changedBridging = true;
  let guard = 0;
  while (changedBridging && guard < 10) {
    changedBridging = false;
    guard += 1;
    for (const cluster of clusters) {
      if (cluster.length < 3) continue;
      for (const candidate of cluster) {
        const rest = cluster.filter((i) => i !== candidate);
        const parts = clusterByOverlap(rest);
        if (parts.length >= 2) {
          bridgingNodeIds.push(candidate.id);
          clusters = clusterByOverlap(
            clusters.flat().filter((i) => !bridgingNodeIds.includes(i.id))
          );
          changedBridging = true;
          break;
        }
      }
      if (changedBridging) break;
    }
  }
  if (clusters.length < 2) return untouched;
  const work = structuredClone(doc);
  const workRoot = work.nodes[work.rootChildren[0]];
  const originalOrder = [...workRoot.children ?? []];
  const memberToBand = /* @__PURE__ */ new Map();
  const bandDefs = [];
  let bandCounter = 0;
  for (const cluster of clusters.sort((a, b) => clusterTop(a) - clusterTop(b))) {
    const memberIds = cluster.map((i) => i.id);
    const only = memberIds.length === 1 ? work.nodes[memberIds[0]] : void 0;
    if (only && (only.type === "FRAME" || only.type === "GROUP")) {
      bandDefs.push({
        id: only.id,
        top: only.y,
        bottom: only.y + only.height,
        memberIds
      });
      continue;
    }
    const top = Math.floor(clusterTop(cluster));
    const bottom = Math.ceil(Math.max(...cluster.map((i) => i.bottom)));
    const bandId = uniqueBandId(work, bandCounter += 1);
    for (const id of memberIds) memberToBand.set(id, bandId);
    bandDefs.push({ id: bandId, top, bottom, memberIds });
  }
  for (const [index, def] of bandDefs.entries()) {
    if (work.nodes[def.id]) continue;
    work.nodes[def.id] = {
      id: def.id,
      type: "FRAME",
      name: `Section ${index + 1}`,
      parentId: workRoot.id,
      x: 0,
      y: def.top,
      width: root.width,
      height: Math.max(1, def.bottom - def.top),
      clipContent: false,
      children: def.memberIds
    };
    for (const id of def.memberIds) {
      const member = work.nodes[id];
      member.parentId = def.id;
      member.y = member.y - def.top;
    }
  }
  const placed = /* @__PURE__ */ new Set();
  const newChildren = [];
  for (const id of originalOrder) {
    const bandId = memberToBand.get(id);
    if (!bandId) {
      newChildren.push(id);
      continue;
    }
    if (!placed.has(bandId)) {
      placed.add(bandId);
      newChildren.push(bandId);
    }
  }
  workRoot.children = newChildren;
  return {
    doc: work,
    bandIds: bandDefs.map((d) => d.id),
    bridgingNodeIds,
    spanningNodeIds,
    changed: true
  };
}
function clusterTop(cluster) {
  return Math.min(...cluster.map((i) => i.top));
}
function clusterByOverlap(items) {
  if (!items.length) return [];
  const sorted = [...items].sort((a, b) => a.top - b.top);
  const clusters = [];
  let current = [sorted[0]];
  let currentBottom = sorted[0].bottom;
  for (const item of sorted.slice(1)) {
    if (item.top < currentBottom - OVERLAP_PX) {
      current.push(item);
      currentBottom = Math.max(currentBottom, item.bottom);
    } else {
      clusters.push(current);
      current = [item];
      currentBottom = item.bottom;
    }
  }
  clusters.push(current);
  return clusters;
}
function isBackgroundish(n) {
  return n.slotRole === "background" || n.slotRole === "background-image" || n.slotRole === "link-zone" || n.pluginData?.isBackground === true || n.constraintVertical === "TOP_BOTTOM";
}
function uniqueBandId(doc, counter) {
  let id = `band-${counter}`;
  let bump = 0;
  while (doc.nodes[id]) {
    bump += 1;
    id = `band-${counter}-${bump}`;
  }
  return id;
}

// src/types/scene-document.ts
import { z as z3 } from "zod";
var SceneNodeTypeEnum = z3.enum([
  "FRAME",
  "RECTANGLE",
  "ELLIPSE",
  "TEXT",
  "IMAGE",
  "GROUP",
  "PATH",
  "LINE",
  "STAR",
  "POLYGON",
  "TRAPEZOID",
  "SEAL",
  // LINK_ZONE_GROUP partitions its bbox into row/column-aligned LINK_ZONE
  // children by weight. Children are non-renderable in the rasterized output;
  // the compiler walks them to emit per-zone <a> overlays on the section image.
  "LINK_ZONE_GROUP",
  "LINK_ZONE"
]);
var SlotRoleEnum = z3.enum([
  "headline",
  "subheadline",
  "tagline",
  "body-text",
  "cta",
  "badge",
  "hero-image",
  "product-image",
  "accent-image",
  "background-image",
  "background",
  "icon",
  "link-zone"
]);
var ProductFieldRoleEnum = z3.enum([
  "image",
  "title",
  "price",
  "compareAtPrice",
  "description",
  "cta",
  "vendor"
]);
var BlendModeEnum = z3.enum([
  "NORMAL",
  "MULTIPLY",
  "SCREEN",
  "OVERLAY",
  "DARKEN",
  "LIGHTEN",
  "COLOR_DODGE",
  "COLOR_BURN",
  "HARD_LIGHT",
  "SOFT_LIGHT",
  "DIFFERENCE",
  "EXCLUSION",
  "HUE",
  "SATURATION",
  "COLOR",
  "LUMINOSITY"
]);
var ImageTransformSchema = z3.object({
  scale: z3.number(),
  offsetX: z3.number(),
  offsetY: z3.number(),
  refWidth: z3.number().positive().optional(),
  refHeight: z3.number().positive().optional()
});
var ImageAdjustmentsSchema = z3.object({
  /** 0 = original, 1 = fully grayscale. CSS grayscale(). */
  grayscale: z3.number().min(0).max(1).optional(),
  /** 1 = identity. CSS brightness(). */
  brightness: z3.number().min(0).optional(),
  /** 1 = identity. CSS contrast(). */
  contrast: z3.number().min(0).optional(),
  /** 1 = identity, 0 = grayscale. CSS saturate(). */
  saturation: z3.number().min(0).optional(),
  /** Degrees. CSS hue-rotate(). */
  hueRotate: z3.number().optional(),
  /** Gaussian blur stdDeviation in px, applied to the image content only
   *  (silhouette/clip stays crisp — unlike node.blur, which blurs the
   *  composited layer). CSS blur(). */
  blur: z3.number().min(0).optional(),
  /** Two-color gradient map: shadows = color at luma 0, highlights = color
   *  at luma 1, mix = strength (1 = full duotone). */
  duotone: z3.object({
    shadows: z3.string(),
    highlights: z3.string(),
    mix: z3.number().min(0).max(1).optional()
  }).optional()
});
var GradientStopSchema2 = z3.object({
  position: z3.number().min(0).max(1),
  color: z3.string()
});
var PaintBlendModeSchema = BlendModeEnum.optional();
var SolidPaintSchema = z3.object({
  type: z3.literal("SOLID"),
  color: z3.string(),
  opacity: z3.number().min(0).max(1).optional(),
  blendMode: PaintBlendModeSchema,
  visible: z3.boolean().optional()
});
var LinearGradientPaintSchema = z3.object({
  type: z3.literal("GRADIENT_LINEAR"),
  stops: z3.array(GradientStopSchema2).min(2),
  angle: z3.number().optional(),
  startPoint: z3.object({ x: z3.number(), y: z3.number() }).optional(),
  endPoint: z3.object({ x: z3.number(), y: z3.number() }).optional(),
  opacity: z3.number().min(0).max(1).optional(),
  blendMode: PaintBlendModeSchema,
  visible: z3.boolean().optional()
});
var RadialGradientPaintSchema = z3.object({
  type: z3.literal("GRADIENT_RADIAL"),
  stops: z3.array(GradientStopSchema2).min(2),
  center: z3.object({ x: z3.number(), y: z3.number() }).optional(),
  radius: z3.number().optional(),
  opacity: z3.number().min(0).max(1).optional(),
  blendMode: PaintBlendModeSchema,
  visible: z3.boolean().optional()
});
var AngularGradientPaintSchema = z3.object({
  type: z3.literal("GRADIENT_ANGULAR"),
  stops: z3.array(GradientStopSchema2).min(2),
  // Sweep centre as a normalized 0..1 fraction of the node box (like a linear
  // gradient's startPoint/endPoint, NOT a radial gradient's pixel `center`).
  // Absent = {0.5, 0.5}. Maps to CSS `at {x*100}% {y*100}%`, and converts from
  // the translation column of Figma's gradientTransform (which is normalized).
  center: z3.object({ x: z3.number(), y: z3.number() }).optional(),
  // Start rotation in degrees, matching CSS `conic-gradient(from Xdeg ...)`:
  // 0 puts stop-0 at 12 o'clock and the sweep runs clockwise. The renderer maps
  // this to a Skia sweep startAngle of (rotation - 90) because Skia's 0deg sits
  // at 3 o'clock. Figma's angular gradientTransform rotation converts into this
  // same clockwise-from-top degree value. Absent = 0.
  rotation: z3.number().optional(),
  opacity: z3.number().min(0).max(1).optional(),
  blendMode: PaintBlendModeSchema,
  visible: z3.boolean().optional()
});
var DiamondGradientPaintSchema = z3.object({
  type: z3.literal("GRADIENT_DIAMOND"),
  stops: z3.array(GradientStopSchema2).min(2),
  // Diamond centre as a normalized 0..1 fraction of the node box (like linear/
  // angular, NOT the radial gradient's pixel center). Absent = {0.5, 0.5}.
  center: z3.object({ x: z3.number(), y: z3.number() }).optional(),
  // Extent multiplier: 1 (default) puts the last stop where the diamond's points
  // touch the box edge midpoints; <1 tightens the ramp, >1 pushes it past the
  // edges. Converts from the scale of Figma's diamond gradientTransform.
  radius: z3.number().optional(),
  opacity: z3.number().min(0).max(1).optional(),
  blendMode: PaintBlendModeSchema,
  visible: z3.boolean().optional()
});
var ImagePaintSchema = z3.object({
  type: z3.literal("IMAGE"),
  src: z3.string(),
  scaleMode: z3.enum(["FILL", "FIT", "TILE"]).optional(),
  // Explicit crop (pan/zoom) of the image inside the shape it fills.
  // Wins over scaleMode; same semantics as node.imageTransform on IMAGE
  // nodes. Absent = scaleMode-derived (FILL/cover default).
  imageTransform: ImageTransformSchema.optional(),
  // Color treatments (duotone, grayscale, ...) applied to this fill's image
  // before any overlay fills/strokes composite on top.
  imageAdjustments: ImageAdjustmentsSchema.optional(),
  opacity: z3.number().min(0).max(1).optional(),
  blendMode: PaintBlendModeSchema,
  visible: z3.boolean().optional()
});
var PaintSchema = z3.union([
  SolidPaintSchema,
  LinearGradientPaintSchema,
  RadialGradientPaintSchema,
  AngularGradientPaintSchema,
  DiamondGradientPaintSchema,
  ImagePaintSchema
]);
var ScrimSchema = z3.object({
  color: z3.string(),
  opacity: z3.number().min(0).max(1),
  gradient: z3.boolean().optional(),
  gradientDirection: z3.enum(["top", "bottom", "left", "right"]).optional()
});
var EffectSchema = z3.object({
  // Shadows share params; NOISE carries its own (noiseType/size/density/...) and
  // ignores the shadow fields. Kept a plain (non-discriminated) enum tag so old
  // scenes validate unchanged and a node may mix a shadow with a noise effect.
  type: z3.enum(["DROP_SHADOW", "INNER_SHADOW", "NOISE"]),
  // Shadow color, OR (NOISE) the primary grain color.
  color: z3.string().optional(),
  offsetX: z3.number().optional(),
  offsetY: z3.number().optional(),
  /** Blur radius (CSS box-shadow blur / Skia gaussian sigma). Shadows only. */
  radius: z3.number().min(0).optional(),
  /** CSS box-shadow spread. Skia-approximate (see the block comment). Shadows only. */
  spread: z3.number().optional(),
  // ── NOISE only (field names mirror Figma's NoiseEffect: noiseType/noiseSize/
  //    density; the Figma converter still rasters legacy NOISE payloads via the
  //    plugin composite, so these are author/renderer-facing). ──
  /** MONO: single `color` grain. DUO: `color` + `secondaryColor` grain. MULTI: random-RGB grain. */
  noiseType: z3.enum(["MONO", "DUO", "MULTI"]).optional(),
  /** Grain cell size in px (larger = chunkier grain). Default 2. */
  size: z3.number().positive().optional(),
  /** 0..1 fraction of grain cells that are filled (coverage). Default 0.5. */
  density: z3.number().min(0).max(1).optional(),
  /** Overall grain opacity 0..1. Default 1. */
  opacity: z3.number().min(0).max(1).optional(),
  /** DUO second grain color; the first is `color`. */
  secondaryColor: z3.string().optional(),
  visible: z3.boolean().optional()
});
var SceneNodeSchema = z3.object({
  id: z3.string(),
  type: SceneNodeTypeEnum,
  name: z3.string().optional(),
  parentId: z3.string().nullable(),
  children: z3.array(z3.string()).optional(),
  // Transform
  x: z3.number(),
  y: z3.number(),
  width: z3.number(),
  height: z3.number(),
  rotation: z3.number().optional(),
  // Visual
  visible: z3.boolean().optional(),
  locked: z3.boolean().optional(),
  opacity: z3.number().min(0).max(1).optional(),
  blendMode: BlendModeEnum.optional(),
  blur: z3.number().min(0).optional(),
  // Gaussian blur applied to whatever is rendered behind this node, scoped to
  // the node's bounds (and corner radius, if any). CSS analog: backdrop-filter:
  // blur(Npx). Frosted-glass card pattern: pair backdropBlur with a translucent
  // fill on the same node.
  backdropBlur: z3.number().min(0).optional(),
  // Progressive (directional) LAYER blur: the blur radius ramps linearly along
  // the axis from startPosition (startRadius) to endPosition (endRadius). Both
  // positions are normalized 0..1 fractions of the node box (matching the
  // startPoint/endPoint convention of a linear gradient). This is Figma's
  // progressive layer blur; it has no CSS/SVG form, so the renderer approximates
  // it with a stack of increasingly-blurred, gradient-masked bands (see
  // renderProgressiveBlur) and the HTML exporter rasterizes any node carrying
  // it. Replaces the uniform `blur` when both are set. Backdrop progressive blur
  // is not modelled (v1).
  blurProgressive: z3.object({
    startPosition: z3.object({ x: z3.number(), y: z3.number() }),
    endPosition: z3.object({ x: z3.number(), y: z3.number() }),
    startRadius: z3.number().min(0),
    endRadius: z3.number().min(0)
  }).optional(),
  // Drop / inner shadows. Rendered by both the Skia rasterizer (drop-shadow
  // image filter) and the HTML exporter (box-shadow / text-shadow) so canvas
  // and export stay 1:1.
  effects: z3.array(EffectSchema).optional(),
  // Fills & Strokes
  fills: z3.array(PaintSchema).optional(),
  strokes: z3.array(PaintSchema).optional(),
  // Alpha mask. Renderer composes the node into a layer, then draws this
  // paint over the layer with BlendMode.DstIn — only the alpha channel of
  // the mask stops matters, RGB is ignored. Equivalent to CSS
  // -webkit-mask-image. Typically a GRADIENT_LINEAR with stops alternating
  // between #000000 (visible) and rgba(0,0,0,0) (hidden).
  mask: PaintSchema.optional(),
  strokeWeight: z3.number().optional(),
  strokeAlign: z3.enum(["INSIDE", "OUTSIDE", "CENTER"]).optional(),
  // Join/cap apply to vector strokes (PATH/LINE/polygon-family). Rect/ellipse
  // box strokes are always mitred/butt in both engines.
  strokeJoin: z3.enum(["MITER", "BEVEL", "ROUND"]).optional(),
  strokeCap: z3.enum(["BUTT", "ROUND", "SQUARE"]).optional(),
  // Dash pattern in px ([dash, gap, ...]); empty/absent = solid.
  dashPattern: z3.array(z3.number()).optional(),
  // cornerRadius accepts a number OR a $brand/$slot/$auto merge-tag string at
  // authoring time. The canvas compile path coerces the resolved CSS value
  // ("6px") back to a number before rasterize, so consumers still see a
  // number at runtime.
  cornerRadius: z3.union([z3.number(), z3.string()]).optional(),
  cornerRadii: z3.array(z3.number()).optional(),
  // Clipping
  clipContent: z3.boolean().optional(),
  // Vector mask (shape clip) on a GROUP/FRAME container: an SVG path that clips
  // the container's own fills AND all of its children to the path's interior,
  // before the alpha `mask` paint composes. This is Figma's vector-mask
  // capability (a bottom sibling shape masking everything above it), a sibling
  // of `mask` (which is an alpha/gradient fade, not a hard shape clip) — the two
  // may coexist (clip first, then mask). The path data is authored in the node's
  // LOCAL PIXEL space (origin = node top-left, units = px, matching width/height)
  // and is drawn VERBATIM — no tight-bounds refit — i.e. the `pathFit:'none'`
  // convention of PATH.pathData. That keeps the two engines 1:1: the CanvasKit
  // renderer clips with the path as-is and the HTML exporter emits it unchanged
  // as `clip-path: path('...')`, whose CSS coordinate box is that same local px
  // space. Only meaningful on GROUP/FRAME; ignored on other node types.
  clipPath: z3.string().optional(),
  // Auto-layout (FRAME / GROUP containers). These drive computeLayout()
  // (visual-rendering/scene-layout) which bakes absolute x/y/width/height from
  // the structural spec. Mirror api/.../common/dto/scene-document.dto.ts — they
  // must round-trip through parseSceneDocument or the auto-layout an author
  // (canvas agent or the editor) emitted is silently stripped.
  //
  // 'GRID' is CSS-grid-style auto layout: children flow into a fixed column
  // count (gridColumnCount), auto-placed left-to-right / top-to-bottom, with
  // optional explicit placement + spans per child (see the grid* fields below).
  // It is NOT inbox-safe as live MJML (MJML has no grid), so a GRID band always
  // rasterizes on email compile — see isLiveEligible in compiler/section-frames.
  layoutMode: z3.enum(["NONE", "HORIZONTAL", "VERTICAL", "GRID"]).optional(),
  layoutGap: z3.number().optional(),
  layoutPaddingTop: z3.number().optional(),
  layoutPaddingRight: z3.number().optional(),
  layoutPaddingBottom: z3.number().optional(),
  layoutPaddingLeft: z3.number().optional(),
  primaryAxisAlign: z3.enum(["START", "CENTER", "END", "SPACE_BETWEEN"]).optional(),
  counterAxisAlign: z3.enum(["START", "CENTER", "END", "STRETCH"]).optional(),
  // Per-child sizing on each axis. FIXED keeps the explicit width/height, FILL
  // stretches to share remaining primary-axis space, HUG shrinks to content.
  layoutSizingHorizontal: z3.enum(["FIXED", "FILL", "HUG"]).optional(),
  layoutSizingVertical: z3.enum(["FIXED", "FILL", "HUG"]).optional(),
  // Auto-layout WRAP (Figma parity). Meaningful only on HORIZONTAL containers —
  // Figma only wraps along the primary axis of a horizontal stack. 'WRAP' flows
  // children onto multiple lines, breaking before a child whose running line
  // width would exceed the container's inner (padding-adjusted) width. A
  // HUG-width container has no fixed width to break against, so it ignores wrap
  // and behaves as 'NO_WRAP'. Absent is treated as 'NO_WRAP'.
  layoutWrap: z3.enum(["NO_WRAP", "WRAP"]).optional(),
  // Gap between wrapped lines (counter axis). When absent, layoutGap is reused
  // for the between-line gap too, matching a single-gap Figma auto-layout.
  counterAxisSpacing: z3.number().optional(),
  // Min/max size clamps (px), applied on top of HUG/FILL/FIXED sizing wherever
  // the solver assigns a size. Each axis is clamped into [min, max] with CSS
  // clamp() semantics (min wins over max on conflict). On a container's own HUG
  // height, minHeight also acts as the floor that pluginData.minHeight used to
  // provide — that legacy floor is honored as a fallback when minHeight is
  // unset. v1: clamping a FILL child does not redistribute the freed remainder
  // to its siblings.
  minWidth: z3.number().optional(),
  maxWidth: z3.number().optional(),
  minHeight: z3.number().optional(),
  maxHeight: z3.number().optional(),
  // Figma-style absolute positioning: an ABSOLUTE child of an auto-layout
  // container is excluded from the flow (no slot, no gap, no HUG
  // contribution) and keeps its authored x/y relative to the container.
  layoutPositioning: z3.enum(["AUTO", "ABSOLUTE"]).optional(),
  // ── GRID auto-layout (layoutMode 'GRID' only) ─────────────────────────────
  // Track sizing v1 is equal-fraction: the container's inner width is split
  // into gridColumnCount equal columns (fr-equivalent) after subtracting the
  // column gaps; row heights are content-driven (see scene-layout). Named /
  // mixed track sizes (fixed px + fr), fr weights, template areas, subgrid, and
  // dense packing are all DEFERRED. Meaningful only on the GRID container.
  /** Number of equal columns the container's inner width is divided into (>=1). */
  gridColumnCount: z3.number().int().positive().optional(),
  /** Fixed row count. Omit to auto-flow rows from the child count. Acts as a
   *  minimum: children overflowing it spill into implicit rows. */
  gridRowCount: z3.number().int().positive().optional(),
  /** Gap between columns (px). Falls back to layoutGap when unset. */
  gridColumnGap: z3.number().optional(),
  /** Gap between rows (px). Falls back to layoutGap when unset. */
  gridRowGap: z3.number().optional(),
  // ── Per-GRID-child placement (child of a GRID container only) ─────────────
  /** Columns this child spans (>=1, default 1). Clamped to gridColumnCount. */
  gridColumnSpan: z3.number().int().positive().optional(),
  /** Rows this child spans (>=1, default 1). */
  gridRowSpan: z3.number().int().positive().optional(),
  /** Explicit 1-based column line the child starts at. Omit for auto-placement. */
  gridColumnStart: z3.number().int().positive().optional(),
  /** Explicit 1-based row line the child starts at. Omit for auto-placement. */
  gridRowStart: z3.number().int().positive().optional(),
  // Constraints: how this node repositions/resizes when its (non-auto-layout)
  // parent FRAME is resized. Consumed by applyConstraints().
  constraintHorizontal: z3.enum(["LEFT", "RIGHT", "CENTER", "LEFT_RIGHT", "SCALE"]).optional(),
  constraintVertical: z3.enum(["TOP", "BOTTOM", "CENTER", "TOP_BOTTOM", "SCALE"]).optional(),
  // TEXT
  text: z3.string().optional(),
  fontSize: z3.number().optional(),
  fontFamily: z3.string().optional(),
  fontWeight: z3.number().optional(),
  fontStyle: z3.enum(["normal", "italic"]).optional(),
  textAlign: z3.enum(["left", "center", "right", "justify"]).optional(),
  verticalAlign: z3.enum(["top", "middle", "bottom"]).optional(),
  lineHeight: z3.number().optional(),
  letterSpacing: z3.number().optional(),
  textDecoration: z3.enum(["none", "underline", "line-through"]).optional(),
  textTransform: z3.enum(["none", "uppercase", "lowercase", "capitalize"]).optional(),
  // Extra vertical gap (px) inserted between paragraphs (text separated by a
  // newline), matching Figma's paragraphSpacing. 0/absent => no extra gap, the
  // single-paragraph fast path. Applied between '\n'-separated blocks only, not
  // between soft-wrapped lines within a paragraph.
  paragraphSpacing: z3.number().optional(),
  // First-line indent (px) applied to each paragraph, matching Figma's
  // paragraphIndent.
  paragraphIndent: z3.number().optional(),
  // Text auto-resize (Figma parity). Governs how the box derives from content
  // when the measure step commits dimensions:
  //   AUTO_HEIGHT — width fixed; height grows/shrinks to the measured text.
  //   AUTO_WIDTH  — width AND height derive from the longest natural line; the
  //                 text never wraps.
  //   FIXED       — width/height box-locked; oversized text shrinks to fit.
  // ABSENT is the legacy default and stays byte-for-byte identical to the
  // pre-field behaviour: the rasterizer shrinks-to-fit within node.height and
  // the editor grows the box while typing. The renderer only diverges from that
  // default when this is explicitly AUTO_HEIGHT/AUTO_WIDTH — it then draws at
  // the natural font size (no shrink) and trusts the committed geometry, which
  // the measure step is responsible for having grown. FIXED and absent are
  // behaviourally identical in the rasterizer (both shrink-to-fit); FIXED just
  // pins the intent so the measure step won't grow the box.
  textAutoResize: z3.enum(["AUTO_WIDTH", "AUTO_HEIGHT", "FIXED"]).optional(),
  // IMAGE
  imageSrc: z3.string().optional(),
  // 'fill' is a legacy stretch mode kept only so old docs validate; renderer
  // treats it as 'cover'. New code should use 'cover' or 'contain'.
  imageFit: z3.enum(["cover", "contain", "fill"]).optional(),
  imageTransform: ImageTransformSchema.optional(),
  // Color treatments (duotone, grayscale, brightness, ...) applied to the
  // image content itself, before overlay fills/strokes composite on top.
  imageAdjustments: ImageAdjustmentsSchema.optional(),
  imageScrim: ScrimSchema.optional(),
  imageAlt: z3.string().optional(),
  // Tiled (repeating) image fill -- a generic pattern primitive. When set, the
  // image repeats across the node at tileWidth x tileHeight (CSS px) instead of
  // filling once. Any tile works (imported Figma Pattern cell, or a user's
  // image), so it's not tied to a fixed generator catalogue. Editable in the
  // properties panel.
  tileWidth: z3.number().positive().optional(),
  tileHeight: z3.number().positive().optional(),
  // Editable source-of-truth for a Pattern fill (Figma-parity: tile source +
  // type/scale/spacing/alignment). The editor bakes this into one repeating
  // cell (imageSrc) + tileWidth/tileHeight above; re-baking on edit keeps them
  // in sync. imageSrc is what the renderer draws; this drives it.
  pattern: z3.object({
    presetKey: z3.string().optional(),
    tileImageSrc: z3.string().optional(),
    tileType: z3.enum(["grid", "hex"]),
    scale: z3.number().positive(),
    spacingX: z3.number().min(0),
    spacingY: z3.number().min(0),
    alignment: z3.enum([
      "top-left",
      "top-center",
      "top-right",
      "mid-left",
      "center",
      "mid-right",
      "bottom-left",
      "bottom-center",
      "bottom-right"
    ]),
    tileColor: z3.string().optional(),
    bgColor: z3.string().optional(),
    tileNatW: z3.number().positive().optional(),
    tileNatH: z3.number().positive().optional()
  }).optional(),
  // PATTERN (an IMAGE node sourced from canvas_patterns -- carries enough
  // metadata that the editor can re-render the SVG with new colors at any
  // point without re-fetching identity. imageSrc is the rasterized PNG; this
  // block is the editable source of truth that drives it.)
  patternKey: z3.string().optional(),
  /** Currently-selected preset chip from `canvas_patterns.presets`, if any */
  patternPresetId: z3.string().optional(),
  /**
   * Per-instance color overrides. Keys depend on the pattern's color strategy:
   * - slots: keyed by slot id (e.g. { bg: '#000', fg: '#fff' })
   * - gradient-derive: dot-paths into the strategy
   *   (e.g. { 'palette.defaultHex': '#ff6600', 'palette.variation': 'analogous' })
   * Wins over preset values; merged with preset.apply at render time.
   */
  patternColors: z3.record(z3.string(), z3.string()).optional(),
  // GENERATOR (IMAGE node produced by a pattern generator from shared-util/
  // canvas-patterns/generators). Lets the editor re-run the generator on
  // parameter changes to morph the shape (in addition to recoloring). May
  // coexist with patternKey when a generated pattern is also seeded into the
  // library (common case): patternKey carries library provenance,
  // generatorKey + generatorParams carry the live editable state.
  generatorKey: z3.string().optional(),
  /**
   * Current generator parameter values. Keys are param ids; values are a
   * primitive or a string array (for icon-list params — a set of Iconify ids
   * the editor resolves to SVG bodies at render time). Runtime shape for a
   * given generator comes from `GENERATORS[key].paramsSchema`.
   */
  generatorParams: z3.record(
    z3.string(),
    z3.union([z3.number(), z3.string(), z3.boolean(), z3.array(z3.string())])
  ).optional(),
  // PATH
  pathData: z3.string().optional(),
  // POLYGON / STAR / SEAL
  sides: z3.number().optional(),
  innerRadius: z3.number().optional(),
  // SEAL: 'sharp' renders triangular spikes (alternating outer/inner anchors,
  // straight edges); 'wavy' renders a smooth scalloped boundary using a fine
  // sinusoidal sampling. Defaults to 'sharp'.
  bumpStyle: z3.enum(["sharp", "wavy"]).optional(),
  // TRAPEZOID: horizontal offset (in px) of the top edge relative to the
  // bottom edge. Positive shifts the top-right corner left (right edge slopes
  // outward toward the bottom — the sale-tag look). Negative produces the
  // inverse. 0 = rectangle. Treated as 0 if absent.
  skew: z3.number().optional(),
  // LINE
  points: z3.array(z3.number()).optional(),
  // Email/Social-specific
  slotRole: SlotRoleEnum.optional(),
  linkUrl: z3.string().optional(),
  // Product binding. A "product block" is any subtree whose root carries
  // productBinding; descendant (or self) nodes tagged with productField are
  // rewritten in lockstep when the bound product is swapped or refreshed.
  // Bound values are MATERIALIZED into the ordinary fields (text, imageSrc,
  // imageAlt, linkUrl) at bind time — renderers and compilers stay
  // product-unaware; the metadata only powers swap/refresh, the properties
  // panel, and AI tooling. A node may carry both (a lone bound image).
  productBinding: z3.object({
    productId: z3.string(),
    variantId: z3.string().optional()
  }).optional(),
  productField: ProductFieldRoleEnum.optional(),
  // LINK_ZONE_GROUP: how children partition the parent bbox.
  direction: z3.enum(["row", "column"]).optional(),
  // LINK_ZONE: flex weight relative to siblings. Absent = 1.
  weight: z3.number().nonnegative().optional(),
  // Compilation hints (for HTML output)
  compileAs: z3.enum(["html", "image"]).optional(),
  htmlTag: z3.string().optional(),
  // Per-section compile mode for the top-level "section" frames of a
  // single-canvas email (the email is one SceneDocument; its bands are
  // frames). Absent is treated as 'image' — the default raster behaviour every
  // designed section already gets. 'link-split' opts into the sliced
  // compositor (per-region <img> + <a>). 'live' compiles the auto-layout
  // subtree to real MJML (selectable text, real <a> links, dark-mode aware)
  // and is only honoured when isLiveEligible(frame) holds — a flow-structured
  // frame, since absolute positioning is not inbox-safe for live text.
  // Footers are the canonical 'live' section. 'html' is a passthrough band:
  // the frame displays a rendered snapshot of a library section's HTML
  // (pluginData.nexmailSection carries the reference + baked HTML) and the
  // compiler ships that HTML verbatim — the snapshot image never ships. Used
  // for sections whose source of truth is raw HTML (e.g. a synced footer)
  // that cannot convert to scene nodes.
  sectionCompileMode: z3.enum(["image", "link-split", "live", "html"]).optional(),
  // Workspace frame reference (for FRAME nodes in workspace documents)
  frameCanvasId: z3.string().optional(),
  frameContentType: z3.enum(["canvas", "template"]).optional(),
  frameType: z3.enum(["email", "social_ad"]).optional(),
  // Locks geometry on email-template-backed FRAME nodes — workspace move/resize
  // handlers skip these so users can't drag the email out of safe dimensions.
  emailLocked: z3.boolean().optional(),
  // Extensible
  pluginData: z3.record(z3.string(), z3.unknown()).optional()
});
var SceneDocumentSchema = z3.object({
  version: z3.literal(2),
  width: z3.number().positive(),
  height: z3.number().positive(),
  nodes: z3.record(z3.string(), SceneNodeSchema),
  rootChildren: z3.array(z3.string()),
  canvasRenderMode: z3.enum(["image", "html", "auto"]).optional(),
  // Whether the email-compile slicer may auto-derive clickable link zones from
  // loose `linkUrl` nodes (the `regionsFromElementLinks` fallback). Absent or
  // `true` => auto-derive (the behaviour AI-authored and imported emails rely
  // on). `false` => author-managed links only: the slicer honours an explicit
  // link-zone group but never fabricates zones, so a manually-built email with
  // no group compiles to a single PNG instead of silently tiling its links.
  autoLinkZones: z3.boolean().optional(),
  fills: z3.array(PaintSchema).optional()
});
function getChildren(doc, nodeId) {
  const node = doc.nodes[nodeId];
  if (!node?.children) return [];
  return node.children.map((id) => doc.nodes[id]).filter((n) => n != null);
}
function getRootNodes(doc) {
  return doc.rootChildren.map((id) => doc.nodes[id]).filter((n) => n != null);
}
function walkNodes(doc, visitor) {
  function walk2(nodeId, depth) {
    const node = doc.nodes[nodeId];
    if (!node) return;
    visitor(node, depth);
    if (node.children) {
      for (const childId of node.children) {
        walk2(childId, depth + 1);
      }
    }
  }
  for (const rootId of doc.rootChildren) {
    walk2(rootId, 0);
  }
}
function findNodeBySlotRole(doc, role) {
  return Object.values(doc.nodes).find((n) => n.slotRole === role);
}
function findNodes(doc, predicate) {
  return Object.values(doc.nodes).filter(predicate);
}
function createEmptySceneDocument(width = 600, height = 400) {
  return {
    version: 2,
    width,
    height,
    nodes: {},
    rootChildren: []
  };
}
function parseSceneDocument(data) {
  const result = SceneDocumentSchema.safeParse(data);
  return result.success ? result.data : null;
}
function isSceneDocument(data) {
  return typeof data === "object" && data !== null && "version" in data && data.version === 2;
}
function normalizeImageFills(node) {
  if (node.fills?.length) return node.fills;
  if (!node.imageScrim) return [];
  const scrim = node.imageScrim;
  if (scrim.gradient) {
    const dir = scrim.gradientDirection || "bottom";
    const transparentColor = withAlpha2(scrim.color, 0);
    const solidColor = withAlpha2(scrim.color, scrim.opacity);
    const coords = scrimDirectionToGradientCoords(dir);
    return [
      {
        type: "GRADIENT_LINEAR",
        stops: [
          { position: 0, color: transparentColor },
          { position: 1, color: solidColor }
        ],
        startPoint: coords.startPoint,
        endPoint: coords.endPoint
      }
    ];
  }
  return [
    {
      type: "SOLID",
      color: scrim.color,
      opacity: scrim.opacity
    }
  ];
}
function scrimDirectionToGradientCoords(dir) {
  switch (dir) {
    case "bottom":
      return { startPoint: { x: 0, y: 0 }, endPoint: { x: 0, y: 1 } };
    case "top":
      return { startPoint: { x: 0, y: 1 }, endPoint: { x: 0, y: 0 } };
    case "right":
      return { startPoint: { x: 0, y: 0 }, endPoint: { x: 1, y: 0 } };
    case "left":
      return { startPoint: { x: 1, y: 0 }, endPoint: { x: 0, y: 0 } };
  }
}
function withAlpha2(color, alpha) {
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
  }
  const hex = color.replace("#", "");
  const r7 = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16);
  const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.slice(2, 4), 16);
  const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.slice(4, 6), 16);
  return `rgba(${r7}, ${g}, ${b}, ${alpha})`;
}

// src/canvas-patterns/palette-derivation.ts
import { oklch, formatHex, parse, clampChroma } from "culori";
function derivePalette(opts) {
  if (opts.steps < 1) {
    throw new Error(`derivePalette: steps must be >= 1 (got ${opts.steps})`);
  }
  const parsed = parse(opts.baseHex);
  if (!parsed) {
    throw new Error(`derivePalette: invalid base color "${opts.baseHex}"`);
  }
  const base = oklch(parsed);
  if (!base) {
    throw new Error(`derivePalette: failed to convert "${opts.baseHex}" to OKLCH`);
  }
  const baseHue = base.h ?? 0;
  const baseChroma = base.c;
  const baseLightness = base.l;
  let stops;
  switch (opts.variation) {
    case "monochrome":
      stops = generateMonochrome(baseChroma, baseHue, opts.steps);
      break;
    case "analogous":
      stops = generateAnalogous(baseLightness, baseChroma, baseHue, opts.steps);
      break;
    case "complementary":
      stops = generateComplementary(baseChroma, baseHue, opts.steps);
      break;
    case "triadic":
      stops = generateTriadic(baseChroma, baseHue, opts.steps);
      break;
  }
  if (opts.direction === "light-to-dark") stops.reverse();
  return stops.map((stop) => formatHex(clampChroma(stop)) ?? "#000000");
}
var L_MIN = 0.18;
var L_MAX = 0.78;
var C_MAX = 0.18;
function generateMonochrome(baseC, baseH, n) {
  const c = Math.min(baseC, C_MAX);
  return Array.from({ length: n }, (_, i) => ({
    mode: "oklch",
    l: lerp(L_MIN, L_MAX, t(i, n)),
    c,
    h: baseH
  }));
}
function generateAnalogous(baseL, baseC, baseH, n) {
  const hueRangeDeg = 50;
  const c = Math.min(baseC, C_MAX);
  return Array.from({ length: n }, (_, i) => {
    const ti = t(i, n);
    return {
      mode: "oklch",
      l: clamp(baseL - 0.15 + 0.3 * ti, L_MIN, L_MAX),
      c,
      h: ((baseH - hueRangeDeg / 2 + hueRangeDeg * ti) % 360 + 360) % 360
    };
  });
}
function generateComplementary(baseC, baseH, n) {
  const half = Math.ceil(n / 2);
  return [
    ...generateMonochrome(baseC, baseH, half),
    ...generateMonochrome(baseC, (baseH + 180) % 360, n - half)
  ];
}
function generateTriadic(baseC, baseH, n) {
  const third = Math.ceil(n / 3);
  return [
    ...generateMonochrome(baseC, baseH, third),
    ...generateMonochrome(baseC, (baseH + 120) % 360, third),
    ...generateMonochrome(baseC, (baseH + 240) % 360, n - 2 * third)
  ];
}
function t(i, n) {
  return n <= 1 ? 0.5 : i / (n - 1);
}
function lerp(a, b, t2) {
  return a + (b - a) * t2;
}
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

// src/canvas-patterns/apply-svg-colors.ts
function applySvgColors(opts) {
  const entries = Object.entries(opts.recolor).map(([from, to]) => [normalizeHex(from), to]).filter((e) => e[0] !== null);
  if (entries.length === 0) return opts.svgMarkup;
  const sentinel = (i) => `\0NEXCOLOR_${i}\0`;
  let out = opts.svgMarkup;
  for (let i = 0; i < entries.length; i++) {
    out = replaceHexCaseInsensitive(out, entries[i][0], sentinel(i));
  }
  for (let i = 0; i < entries.length; i++) {
    out = out.split(sentinel(i)).join(entries[i][1]);
  }
  return out;
}
function normalizeHex(raw) {
  const h = raw.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(h)) {
    return `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  if (/^#[0-9a-f]{6}$/.test(h)) return h;
  return null;
}
function replaceHexCaseInsensitive(svg, longHex, replacement) {
  const hex = longHex.slice(1);
  let out = svg.replace(new RegExp(`#${hex}`, "gi"), replacement);
  const r1 = hex[0], r22 = hex[1], g1 = hex[2], g2 = hex[3], b1 = hex[4], b2 = hex[5];
  if (r1 === r22 && g1 === g2 && b1 === b2) {
    out = out.replace(
      new RegExp(`#${r1}${g1}${b1}(?![0-9a-f])`, "gi"),
      replacement
    );
  }
  return out;
}

// src/canvas-patterns/pattern-fill.ts
var PATTERN_ALIGNMENTS = [
  "top-left",
  "top-center",
  "top-right",
  "mid-left",
  "center",
  "mid-right",
  "bottom-left",
  "bottom-center",
  "bottom-right"
];
var DEFAULT_PATTERN = {
  presetKey: "dot",
  tileType: "grid",
  scale: 1,
  spacingX: 1,
  spacingY: 1,
  alignment: "center",
  tileColor: "#3f8f2e"
};
var C = "__C__";
var BUILTIN_TILES = [
  { key: "dot", label: "Dot", size: 24, markup: `<circle cx="12" cy="12" r="6" fill="${C}"/>` },
  { key: "ring", label: "Ring", size: 24, markup: `<circle cx="12" cy="12" r="6" fill="none" stroke="${C}" stroke-width="2.5"/>` },
  { key: "plus", label: "Plus", size: 24, markup: `<path d="M10.5 4h3v6h6v3h-6v6h-3v-6h-6v-3h6z" fill="${C}"/>` },
  { key: "cross", label: "Cross", size: 24, markup: `<path d="M5 7l2-2 5 5 5-5 2 2-5 5 5 5-2 2-5-5-5 5-2-2 5-5z" fill="${C}"/>` },
  { key: "diamond", label: "Diamond", size: 24, markup: `<path d="M12 3l9 9-9 9-9-9z" fill="${C}"/>` },
  { key: "square", label: "Square", size: 24, markup: `<rect x="6" y="6" width="12" height="12" rx="2" fill="${C}"/>` },
  { key: "triangle", label: "Triangle", size: 24, markup: `<path d="M12 4l9 16H3z" fill="${C}"/>` },
  { key: "line", label: "Line", size: 24, markup: `<rect x="0" y="10" width="24" height="4" fill="${C}"/>` },
  { key: "star", label: "Star", size: 24, markup: `<path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 21.4l1.4-6.8L2.2 9.9l6.9-.8z" fill="${C}"/>` }
];
function getBuiltinTile(key) {
  return BUILTIN_TILES.find((t2) => t2.key === key);
}
function resolveTile(p) {
  if (p.tileImageSrc) {
    const natW = p.tileNatW && p.tileNatW > 0 ? p.tileNatW : 48;
    const natH = p.tileNatH && p.tileNatH > 0 ? p.tileNatH : 48;
    const href = p.tileImageSrc;
    return {
      natW,
      natH,
      inner: (x, y, w, h) => `<image href="${escapeXmlAttr(href)}" x="${r3(x)}" y="${r3(y)}" width="${r3(w)}" height="${r3(h)}" preserveAspectRatio="xMidYMid meet"/>`
    };
  }
  const tile = getBuiltinTile(p.presetKey ?? "dot");
  if (!tile) return null;
  const color = p.tileColor ?? "#000000";
  const body = tile.markup.split(C).join(color);
  return {
    natW: tile.size,
    natH: tile.size,
    inner: (x, y, w, h) => {
      const sx = w / tile.size;
      const sy = h / tile.size;
      return `<g transform="translate(${r3(x)} ${r3(y)}) scale(${r3(sx)} ${r3(sy)})">${body}</g>`;
    }
  };
}
function buildPatternCellSvg(p) {
  const tile = resolveTile(p);
  if (!tile) return null;
  const scale = clampNum(p.scale, 0.05, 8) || 1;
  const tileW = Math.max(1, tile.natW * scale);
  const tileH = Math.max(1, tile.natH * scale);
  const gapX = Math.max(0, tileW * clampNum(p.spacingX, 0, 50));
  const gapY = Math.max(0, tileH * clampNum(p.spacingY, 0, 50));
  const cellW = Math.max(1, Math.round(tileW + gapX));
  const cellH = Math.max(1, Math.round(tileH + gapY));
  const [ah, av] = alignmentFractions(p.alignment);
  const tx = (cellW - tileW) * ah;
  const ty = (cellH - tileH) * av;
  const bg = p.bgColor ? `<rect x="0" y="0" width="100%" height="100%" fill="${escapeXmlAttr(p.bgColor)}"/>` : "";
  if (p.tileType === "hex") {
    const h2 = cellH * 2;
    const parts = [
      bg,
      tile.inner(tx, ty, tileW, tileH),
      tile.inner(tx + cellW / 2, ty + cellH, tileW, tileH),
      tile.inner(tx + cellW / 2 - cellW, ty + cellH, tileW, tileH),
      tile.inner(tx + cellW / 2 + cellW, ty + cellH, tileW, tileH)
    ];
    return {
      svg: svgDoc(cellW, h2, parts.join("")),
      cellW,
      cellH: h2
    };
  }
  return {
    svg: svgDoc(cellW, cellH, bg + tile.inner(tx, ty, tileW, tileH)),
    cellW,
    cellH
  };
}
function svgDoc(w, h, inner) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${inner}</svg>`;
}
function alignmentFractions(a) {
  const h = a.endsWith("left") ? 0 : a.endsWith("right") ? 1 : 0.5;
  const v = a.startsWith("top") ? 0 : a.startsWith("bottom") ? 1 : 0.5;
  return [h, v];
}
function clampNum(n, lo, hi) {
  if (n == null || Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}
function r3(n) {
  return Math.round(n * 100) / 100;
}
function escapeXmlAttr(s) {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// src/canvas-patterns/recolor-pattern.ts
function buildRecolorMap(input) {
  const overrides = resolveOverrides(input);
  const strategy = input.strategy ?? (input.slots && input.slots.length > 0 ? { type: "slots" } : { type: "fixed" });
  if (strategy.type === "fixed") return {};
  if (strategy.type === "slots") {
    const map2 = {};
    for (const slot of input.slots ?? []) {
      const newHex = overrides[slot.id];
      if (newHex && newHex !== slot.defaultHex) {
        map2[slot.defaultHex] = newHex;
      }
    }
    return map2;
  }
  const baseNew = overrides["palette.defaultHex"] ?? strategy.palette.defaultHex;
  const variation = overrides["palette.variation"] ?? strategy.palette.variation;
  const direction = overrides["palette.direction"] ?? strategy.palette.direction;
  const derived = derivePalette({
    baseHex: baseNew,
    steps: strategy.palette.steps,
    variation,
    direction
  });
  const map = {};
  if (strategy.background) {
    const bgNew = overrides["background.defaultHex"] ?? strategy.background.defaultHex;
    if (bgNew !== strategy.background.defaultHex) {
      map[strategy.background.defaultHex] = bgNew;
    }
  }
  strategy.palette.originalSteps.forEach((origHex, i) => {
    const newHex = derived[i];
    if (newHex && newHex !== origHex) {
      map[origHex] = newHex;
    }
  });
  return map;
}
function resolveOverrides(input) {
  const acc = {};
  if (input.preset) {
    const preset = (input.presets ?? []).find((p) => p.id === input.preset);
    if (!preset) {
      throw new Error(`buildRecolorMap: preset "${input.preset}" not found`);
    }
    if (preset.apply !== "as-stored") {
      Object.assign(acc, preset.apply);
    }
  }
  if (input.colors) {
    Object.assign(acc, input.colors);
  }
  for (const [key, value] of Object.entries(acc)) {
    const m = value.match(/^\{\{brand\.(\w+)\}\}$/);
    if (!m) continue;
    const brandColor = input.brand?.[m[1]];
    if (brandColor) {
      acc[key] = brandColor;
    } else {
      delete acc[key];
    }
  }
  return acc;
}

// src/canvas-patterns/rasterize-svg-browser.ts
async function rasterizeSvgToObjectUrl(opts) {
  const { svg, width, height, type = "image/png" } = opts;
  if (width < 1 || height < 1) {
    throw new Error(`rasterizeSvgToObjectUrl: width/height must be >= 1 (got ${width}x${height})`);
  }
  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);
  try {
    const img = await loadImage(svgUrl);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("rasterizeSvgToObjectUrl: 2d context unavailable");
    ctx.drawImage(img, 0, 0, width, height);
    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => b ? resolve(b) : reject(new Error("canvas.toBlob returned null")),
        type
      );
    });
    return URL.createObjectURL(pngBlob);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}
function revokePatternImageSrc(url) {
  if (!url) return;
  if (url.startsWith("blob:")) URL.revokeObjectURL(url);
}
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`loadImage: failed to load ${src.slice(0, 60)}...`));
    img.src = src;
  });
}

// src/canvas-patterns/generators/_helpers/svg-emit.ts
function emitSvg(opts) {
  const { width, height, children } = opts;
  return `<svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" version="1.1">${children}</svg>`;
}
function rect(x, y, w, h, fill) {
  return `<rect x="${r4(x)}" y="${r4(y)}" width="${r4(w)}" height="${r4(h)}" fill="${fill}"/>`;
}
function path(d, fill, stroke) {
  const strokeAttr = stroke ? ` stroke="${stroke}"` : "";
  return `<path d="${d}" fill="${fill}"${strokeAttr}/>`;
}
function polygon(points, fill) {
  const coords = points.map((p) => `${r4(p.x)},${r4(p.y)}`).join(" ");
  return `<polygon points="${coords}" fill="${fill}"/>`;
}
function circle(cx, cy, radius, fill) {
  return `<circle cx="${r4(cx)}" cy="${r4(cy)}" r="${r4(radius)}" fill="${fill}"/>`;
}
function r4(n) {
  return Math.round(n * 100) / 100;
}

// src/canvas-patterns/generators/layered-waves.ts
var DEFAULT_SIZE = { width: 900, height: 600 };
var DEFAULT_BG = "#0f172a";
var DEFAULT_BASE_COLOR = "#3b82f6";
var PARAMS_SCHEMA = {
  layers: { type: "number", min: 3, max: 8, step: 1, default: 5, label: "Layers" },
  amplitude: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.35, label: "Amplitude" },
  frequency: { type: "number", min: 1, max: 6, step: 1, default: 3, label: "Frequency" },
  smoothness: { type: "number", min: 0.2, max: 1, step: 0.1, default: 0.6, label: "Smoothness" },
  seed: { type: "seed", default: "waves-1", label: "Seed" }
};
function generate(params) {
  const { width, height } = DEFAULT_SIZE;
  const { layers, amplitude, frequency, smoothness, seed } = params;
  const rng = seededRng(seed);
  const noise = seededNoise2d(seed);
  const palette = derivePalette({
    baseHex: DEFAULT_BASE_COLOR,
    steps: layers,
    variation: "monochrome",
    direction: "light-to-dark"
  });
  const numPoints = Math.max(8, Math.round(frequency * 5) + 2);
  const amplitudePx = amplitude * height * 0.18;
  const peakReachPx = amplitudePx * 1.25;
  const topMargin = Math.min(height * 0.45, peakReachPx + 8);
  const children = [];
  children.push(rect(0, 0, width, height, DEFAULT_BG));
  for (let i = 0; i < layers; i++) {
    const tLayer = layers <= 1 ? 0.5 : i / (layers - 1);
    const baseline = topMargin + tLayer * (height * 0.92 - topMargin);
    const phase = rng.nextBetween(0, Math.PI * 2);
    const points = [];
    for (let j = 0; j < numPoints; j++) {
      const x = j / (numPoints - 1) * width;
      const tx = x / width;
      const coarse = Math.sin(tx * frequency * Math.PI * 2 + phase);
      const wobble = noise(tx * 4, i * 1.5) * 0.25;
      const yOffset = (coarse + wobble) * amplitudePx;
      const y = Math.max(0, Math.min(height, baseline + yOffset));
      points.push({ x, y });
    }
    const curveD = openCatmullRomToBezier(points, smoothness);
    const closedD = `${curveD} L${width},${height} L0,${height} Z`;
    children.push(path(closedD, palette[i]));
  }
  return {
    svgMarkup: emitSvg({ width, height, children: children.join("") }),
    colorStrategy: {
      type: "gradient-derive",
      background: { defaultHex: DEFAULT_BG },
      palette: {
        defaultHex: DEFAULT_BASE_COLOR,
        steps: layers,
        originalSteps: palette,
        direction: "light-to-dark",
        variation: "monochrome"
      }
    },
    colorSlots: [],
    naturalWidth: width,
    naturalHeight: height
  };
}
var layeredWavesGenerator = {
  key: "layered-waves",
  label: "Layered Waves",
  category: "organic",
  defaultSize: DEFAULT_SIZE,
  paramsSchema: PARAMS_SCHEMA,
  generate
};

// src/canvas-patterns/generators/stacked-peaks.ts
var DEFAULT_SIZE2 = { width: 900, height: 600 };
var DEFAULT_BG2 = "#0f172a";
var DEFAULT_BASE_COLOR2 = "#10b981";
var PARAMS_SCHEMA2 = {
  peaks: { type: "number", min: 4, max: 14, step: 1, default: 7, label: "Peaks" },
  jitter: { type: "number", min: 0, max: 1, step: 0.05, default: 0.4, label: "Jitter" },
  sharpness: { type: "number", min: 0.2, max: 1, step: 0.05, default: 0.7, label: "Sharpness" },
  layers: { type: "number", min: 3, max: 8, step: 1, default: 5, label: "Layers" },
  seed: { type: "seed", default: "peaks-1", label: "Seed" }
};
function generate2(params) {
  const { width, height } = DEFAULT_SIZE2;
  const { peaks, jitter, sharpness, layers, seed } = params;
  const rng = seededRng(seed);
  const noise = seededNoise2d(seed);
  const palette = derivePalette({
    baseHex: DEFAULT_BASE_COLOR2,
    steps: layers,
    variation: "monochrome",
    direction: "light-to-dark"
  });
  const numPoints = peaks * 2 + 1;
  const amplitudePx = height * 0.14;
  const peakReachPx = amplitudePx * 1.2;
  const topMargin = Math.min(height * 0.5, peakReachPx + 10);
  const children = [];
  children.push(rect(0, 0, width, height, DEFAULT_BG2));
  for (let i = 0; i < layers; i++) {
    const tLayer = layers <= 1 ? 0.5 : i / (layers - 1);
    const baseline = topMargin + tLayer * (height * 0.95 - topMargin);
    const points = [];
    for (let j = 0; j < numPoints; j++) {
      const t2 = j / (numPoints - 1);
      let x = t2 * width;
      if (j !== 0 && j !== numPoints - 1 && jitter > 0) {
        const jitterPx = width / numPoints * jitter * 0.5;
        x += noise(t2 * 3.7, i * 0.9) * jitterPx;
      }
      const isPeak = j % 2 === 1;
      const swing = isPeak ? -1 : sharpness * 0.55;
      const wobble = noise(t2 * 5 + i * 2.1, i * 1.4) * 0.25;
      const yOffset = (swing + wobble) * amplitudePx;
      const y = Math.max(0, Math.min(height, baseline + yOffset));
      points.push({ x, y });
    }
    const parts = [`M${r5(points[0].x)},${r5(points[0].y)}`];
    for (let k = 1; k < points.length; k++) {
      parts.push(`L${r5(points[k].x)},${r5(points[k].y)}`);
    }
    parts.push(`L${width},${height} L0,${height} Z`);
    children.push(path(parts.join(" "), palette[i]));
  }
  return {
    svgMarkup: emitSvg({ width, height, children: children.join("") }),
    colorStrategy: {
      type: "gradient-derive",
      background: { defaultHex: DEFAULT_BG2 },
      palette: {
        defaultHex: DEFAULT_BASE_COLOR2,
        steps: layers,
        originalSteps: palette,
        direction: "light-to-dark",
        variation: "monochrome"
      }
    },
    colorSlots: [],
    naturalWidth: width,
    naturalHeight: height
  };
}
function r5(n) {
  return Math.round(n * 100) / 100;
}
var stackedPeaksGenerator = {
  key: "stacked-peaks",
  label: "Stacked Peaks",
  category: "organic",
  defaultSize: DEFAULT_SIZE2,
  paramsSchema: PARAMS_SCHEMA2,
  generate: generate2
};

// src/canvas-patterns/generators/blob-scene.ts
var DEFAULT_SIZE3 = { width: 900, height: 600 };
var DEFAULT_BG3 = "#0f172a";
var DEFAULT_BLOB = "#f97316";
var PARAMS_SCHEMA3 = {
  complexity: { type: "number", min: 4, max: 14, step: 1, default: 7, label: "Complexity" },
  contrast: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.45, label: "Contrast" },
  gap: { type: "number", min: 0, max: 1, step: 0.05, default: 0.3, label: "Gap" },
  fixedEnds: { type: "boolean", default: true, label: "Fixed ends" },
  blobCount: { type: "number", min: 1, max: 4, step: 1, default: 2, label: "Blobs" },
  seed: { type: "seed", default: "blob-1", label: "Seed" }
};
function anchorPoint(anchor, width, height) {
  switch (anchor) {
    case "tl":
      return { x: 0, y: 0 };
    case "tr":
      return { x: width, y: 0 };
    case "bl":
      return { x: 0, y: height };
    case "br":
      return { x: width, y: height };
    case "t":
      return { x: width / 2, y: 0 };
    case "r":
      return { x: width, y: height / 2 };
    case "b":
      return { x: width / 2, y: height };
    case "l":
      return { x: 0, y: height / 2 };
  }
}
var ANCHORS = ["tl", "tr", "bl", "br", "t", "r", "b", "l"];
function generate3(params) {
  const { width, height } = DEFAULT_SIZE3;
  const { complexity, contrast, gap, fixedEnds, blobCount, seed } = params;
  const rng = seededRng(seed);
  const noise = seededNoise2d(seed);
  const children = [];
  children.push(rect(0, 0, width, height, DEFAULT_BG3));
  const minDim = Math.min(width, height);
  const baseRadiusMin = minDim * (0.3 - gap * 0.12);
  const baseRadiusMax = minDim * (0.55 - gap * 0.15);
  const shuffledAnchors = rng.shuffle(ANCHORS);
  for (let b = 0; b < blobCount; b++) {
    let cx;
    let cy;
    let radius;
    if (fixedEnds) {
      const anchor = shuffledAnchors[b % shuffledAnchors.length];
      const pt = anchorPoint(anchor, width, height);
      cx = pt.x;
      cy = pt.y;
      radius = rng.nextBetween(minDim * 0.55, minDim * 0.85);
    } else {
      cx = rng.nextBetween(width * 0.25, width * 0.75);
      cy = rng.nextBetween(height * 0.25, height * 0.75);
      radius = rng.nextBetween(baseRadiusMin, baseRadiusMax);
    }
    const points = [];
    for (let i = 0; i < complexity; i++) {
      const angle = i / complexity * Math.PI * 2;
      const n = noise(Math.cos(angle) * 1.5 + b * 13, Math.sin(angle) * 1.5 + b * 7);
      const r7 = radius * (1 + n * contrast * 0.45);
      points.push(polarToCartesian(angle, r7, cx, cy));
    }
    const pathData = catmullRomToBezier(points, 0.5);
    children.push(path(pathData, DEFAULT_BLOB));
  }
  const colorSlots = [
    { id: "bg", label: "Background", role: "background", defaultHex: DEFAULT_BG3 },
    { id: "blob", label: "Blob", role: "primary", defaultHex: DEFAULT_BLOB }
  ];
  return {
    svgMarkup: emitSvg({ width, height, children: children.join("") }),
    colorStrategy: { type: "slots" },
    colorSlots,
    naturalWidth: width,
    naturalHeight: height
  };
}
var blobSceneGenerator = {
  key: "blob-scene",
  label: "Blob Scene",
  category: "organic",
  defaultSize: DEFAULT_SIZE3,
  paramsSchema: PARAMS_SCHEMA3,
  generate: generate3
};

// src/canvas-patterns/generators/_helpers/poisson.ts
function poissonDiskSample(opts) {
  const { width, height, minDist, rng } = opts;
  const k = opts.k ?? 30;
  const maxPoints = opts.maxPoints ?? 2e3;
  const cellSize = minDist / Math.sqrt(2);
  const cols = Math.max(1, Math.ceil(width / cellSize));
  const rows = Math.max(1, Math.ceil(height / cellSize));
  const grid = new Array(cols * rows).fill(null);
  const gridIndex = (p) => Math.floor(p.x / cellSize) + Math.floor(p.y / cellSize) * cols;
  const active = [];
  const result = [];
  const first = {
    x: rng.nextBetween(0, width),
    y: rng.nextBetween(0, height)
  };
  active.push(first);
  result.push(first);
  grid[gridIndex(first)] = first;
  while (active.length > 0 && result.length < maxPoints) {
    const activeIdx = rng.nextInt(0, active.length);
    const origin = active[activeIdx];
    let placed = false;
    for (let attempt = 0; attempt < k; attempt++) {
      const angle = rng.nextBetween(0, Math.PI * 2);
      const r7 = minDist + rng.nextBetween(0, minDist);
      const cand = {
        x: origin.x + Math.cos(angle) * r7,
        y: origin.y + Math.sin(angle) * r7
      };
      if (cand.x < 0 || cand.x >= width || cand.y < 0 || cand.y >= height) continue;
      const col = Math.floor(cand.x / cellSize);
      const row = Math.floor(cand.y / cellSize);
      let valid = true;
      for (let dr = -2; dr <= 2 && valid; dr++) {
        for (let dc = -2; dc <= 2 && valid; dc++) {
          const nc = col + dc;
          const nr = row + dr;
          if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;
          const neighbor = grid[nc + nr * cols];
          if (!neighbor) continue;
          const dx = cand.x - neighbor.x;
          const dy = cand.y - neighbor.y;
          if (dx * dx + dy * dy < minDist * minDist) valid = false;
        }
      }
      if (valid) {
        active.push(cand);
        result.push(cand);
        grid[gridIndex(cand)] = cand;
        placed = true;
        break;
      }
    }
    if (!placed) active.splice(activeIdx, 1);
  }
  return result;
}

// src/canvas-patterns/generators/symbol-scatter.ts
var DEFAULT_SIZE4 = { width: 900, height: 600 };
var DEFAULT_BG4 = "#0f172a";
var DEFAULT_SYMBOL_COLOR = "#60a5fa";
var FALLBACK_BODY = '<circle cx="12" cy="12" r="9" fill="currentColor"/>';
var PARAMS_SCHEMA4 = {
  density: { type: "number", min: 0.1, max: 1, step: 0.05, default: 0.5, label: "Density" },
  sizeVariation: { type: "number", min: 0, max: 1, step: 0.05, default: 0.3, label: "Size variation" },
  rotationVariation: {
    type: "number",
    min: 0,
    max: 1,
    step: 0.05,
    default: 0.5,
    label: "Rotation variation"
  },
  icons: {
    type: "icon-list",
    default: ["lucide:star"],
    label: "Icons",
    description: "Pick one or more icons. Mixing multiple yields varied compositions (same seed = same mix)."
  },
  seed: { type: "seed", default: "scatter-1", label: "Seed" }
};
function generate4(params, resources) {
  const { width, height } = DEFAULT_SIZE4;
  const { density, sizeVariation, rotationVariation, seed } = params;
  const rng = seededRng(seed);
  const baseSize = 14;
  const minDist = baseSize * 3.5 / (0.3 + density * 1.3);
  const points = poissonDiskSample({
    width,
    height,
    minDist,
    maxPoints: 400,
    rng
  });
  const resolvedBodies = resources?.iconBodies ?? [];
  const bodies = resolvedBodies.length > 0 ? resolvedBodies : [FALLBACK_BODY];
  const children = [];
  children.push(rect(0, 0, width, height, DEFAULT_BG4));
  for (const p of points) {
    const size = baseSize * (1 + rng.nextBetween(-sizeVariation, sizeVariation) * 0.5);
    const rotation = rng.nextBetween(0, Math.PI * 2) * rotationVariation;
    const body = bodies[rng.nextInt(0, bodies.length)];
    children.push(stampIconBody(body, p.x, p.y, size, rotation, DEFAULT_SYMBOL_COLOR));
  }
  const colorSlots = [
    { id: "bg", label: "Background", role: "background", defaultHex: DEFAULT_BG4 },
    { id: "symbol", label: "Symbols", role: "primary", defaultHex: DEFAULT_SYMBOL_COLOR }
  ];
  return {
    svgMarkup: emitSvg({ width, height, children: children.join("") }),
    colorStrategy: { type: "slots" },
    colorSlots,
    naturalWidth: width,
    naturalHeight: height
  };
}
function round3(n) {
  return Math.round(n * 100) / 100;
}
function stampIconBody(body, cx, cy, size, rotationRad, color) {
  const scale = size * 2 / 24;
  const degrees = rotationRad * 180 / Math.PI;
  const transform = `translate(${round3(cx)},${round3(cy)}) rotate(${round3(degrees)}) scale(${round3(scale)}) translate(-12,-12)`;
  return `<g transform="${transform}" color="${color}" fill="${color}">${body}</g>`;
}
var symbolScatterGenerator = {
  key: "symbol-scatter",
  label: "Symbol Scatter",
  category: "geometric",
  defaultSize: DEFAULT_SIZE4,
  paramsSchema: PARAMS_SCHEMA4,
  generate: generate4
};

// src/canvas-patterns/generators/_helpers/delaunay.ts
import { Delaunay } from "d3-delaunay";
function triangulate(points) {
  if (points.length < 3) return [];
  const flat = new Float64Array(points.length * 2);
  points.forEach((p, i) => {
    flat[i * 2] = p.x;
    flat[i * 2 + 1] = p.y;
  });
  const delaunay = new Delaunay(flat);
  const triangles = [];
  for (let i = 0; i < delaunay.triangles.length; i += 3) {
    const a = points[delaunay.triangles[i]];
    const b = points[delaunay.triangles[i + 1]];
    const c = points[delaunay.triangles[i + 2]];
    triangles.push({
      a,
      b,
      c,
      centroid: { x: (a.x + b.x + c.x) / 3, y: (a.y + b.y + c.y) / 3 }
    });
  }
  return triangles;
}

// src/canvas-patterns/generators/low-poly-grid.ts
var DEFAULT_SIZE5 = { width: 900, height: 600 };
var DEFAULT_BASE_COLOR3 = "#6366f1";
var PARAMS_SCHEMA5 = {
  resolution: { type: "number", min: 5, max: 22, step: 1, default: 11, label: "Resolution" },
  jitter: { type: "number", min: 0, max: 1, step: 0.05, default: 0.5, label: "Jitter" },
  depth: { type: "number", min: 0, max: 1, step: 0.05, default: 0.55, label: "Depth" },
  layers: { type: "number", min: 2, max: 4, step: 1, default: 3, label: "Layers" },
  seed: { type: "seed", default: "poly-1", label: "Seed" }
};
function generate5(params) {
  const { width, height } = DEFAULT_SIZE5;
  const { resolution, jitter, depth, layers, seed } = params;
  const rng = seededRng(seed);
  const noise = seededNoise2d(seed);
  const cols = Math.max(3, resolution);
  const rows = Math.max(3, Math.round(cols * (height / width)));
  const cellW = width / (cols - 1);
  const cellH = height / (rows - 1);
  const points = [];
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      let x = j * cellW;
      let y = i * cellH;
      if (i > 0 && i < rows - 1 && j > 0 && j < cols - 1) {
        x += rng.nextBetween(-cellW * 0.42, cellW * 0.42) * jitter;
        y += rng.nextBetween(-cellH * 0.42, cellH * 0.42) * jitter;
      }
      points.push({ x, y });
    }
  }
  const paletteSteps = layers * 2;
  const palette = derivePalette({
    baseHex: DEFAULT_BASE_COLOR3,
    steps: paletteSteps,
    variation: "monochrome",
    direction: "dark-to-light"
  });
  const triangles = triangulate(points);
  const children = [];
  for (const tri of triangles) {
    const ty = tri.centroid.y / height;
    const n = noise(tri.centroid.x / 180, tri.centroid.y / 180);
    const rawIdx = ty * paletteSteps + n * depth * paletteSteps * 0.5;
    const idx = Math.max(0, Math.min(paletteSteps - 1, Math.floor(rawIdx)));
    children.push(polygon([tri.a, tri.b, tri.c], palette[idx]));
  }
  return {
    svgMarkup: emitSvg({ width, height, children: children.join("") }),
    colorStrategy: {
      type: "gradient-derive",
      palette: {
        defaultHex: DEFAULT_BASE_COLOR3,
        steps: paletteSteps,
        originalSteps: palette,
        direction: "dark-to-light",
        variation: "monochrome"
      }
    },
    colorSlots: [],
    naturalWidth: width,
    naturalHeight: height
  };
}
var lowPolyGridGenerator = {
  key: "low-poly-grid",
  label: "Low Poly Grid",
  category: "geometric",
  defaultSize: DEFAULT_SIZE5,
  paramsSchema: PARAMS_SCHEMA5,
  generate: generate5
};

// src/canvas-patterns/generators/tiles.ts
var TILE_STYLES = ["hexagons", "chevron", "scales", "diamond", "stripes", "polka"];
var DEFAULT_SIZE6 = { width: 900, height: 600 };
var DEFAULT_BG5 = "#0f172a";
var DEFAULT_TILE = "#60a5fa";
var PARAMS_SCHEMA6 = {
  style: {
    type: "enum",
    options: TILE_STYLES,
    default: "hexagons",
    label: "Style"
  },
  tileSize: {
    type: "number",
    min: 18,
    max: 120,
    step: 2,
    default: 40,
    label: "Tile size"
  },
  weight: {
    type: "number",
    min: 0.1,
    max: 1,
    step: 0.05,
    default: 0.45,
    label: "Weight",
    description: "Fill ratio (dot size / stripe thickness). Ignored for hexagons, scales, diamond."
  },
  seed: { type: "seed", default: "tile-1", label: "Seed" }
};
function generate6(params) {
  const { width, height } = DEFAULT_SIZE6;
  const { style, tileSize, weight, seed } = params;
  const rng = seededRng(seed);
  const children = [rect(0, 0, width, height, DEFAULT_BG5)];
  switch (style) {
    case "hexagons":
      children.push(...hexagonTiles(width, height, tileSize, DEFAULT_TILE));
      break;
    case "chevron":
      children.push(...chevronTiles(width, height, tileSize, weight, DEFAULT_TILE));
      break;
    case "scales":
      children.push(...scaleTiles(width, height, tileSize, DEFAULT_TILE));
      break;
    case "diamond":
      children.push(...diamondLatticeTiles(width, height, tileSize, DEFAULT_TILE));
      break;
    case "stripes":
      children.push(...stripeTiles(width, height, tileSize, weight, DEFAULT_TILE));
      break;
    case "polka":
      children.push(...polkaTiles(width, height, tileSize, weight, DEFAULT_TILE, rng.next()));
      break;
  }
  const colorSlots = [
    { id: "bg", label: "Background", role: "background", defaultHex: DEFAULT_BG5 },
    { id: "tile", label: "Tile", role: "primary", defaultHex: DEFAULT_TILE }
  ];
  return {
    svgMarkup: emitSvg({ width, height, children: children.join("") }),
    colorStrategy: { type: "slots" },
    colorSlots,
    naturalWidth: width,
    naturalHeight: height
  };
}
function hexagonTiles(width, height, radius, fill) {
  const out = [];
  const dx = 1.5 * radius;
  const dy = Math.sqrt(3) * radius;
  for (let col = -1; col * dx < width + dx; col++) {
    const cx = col * dx;
    const yOffset = col % 2 === 0 ? 0 : dy / 2;
    for (let row = -1; row * dy + yOffset < height + dy; row++) {
      const cy = row * dy + yOffset;
      const pts = [];
      for (let i = 0; i < 6; i++) {
        pts.push(polarToCartesian(i * Math.PI / 3, radius, cx, cy));
      }
      out.push(polygon(pts, fill));
    }
  }
  return out;
}
function stripeTiles(width, height, tileSize, weight, fill) {
  const out = [];
  const diag = Math.hypot(width, height);
  const stripeW = tileSize * weight;
  const period = tileSize;
  const cx = width / 2;
  const cy = height / 2;
  const stripes = [];
  for (let y = -diag; y < diag; y += period) {
    stripes.push(
      `<rect x="${-diag}" y="${r6(y)}" width="${r6(diag * 2)}" height="${r6(stripeW)}" fill="${fill}"/>`
    );
  }
  out.push(`<g transform="rotate(45 ${r6(cx)} ${r6(cy)})">${stripes.join("")}</g>`);
  return out;
}
function scaleTiles(width, height, radius, fill) {
  const out = [];
  const step = radius * 2;
  const rowStep = radius * 1.2;
  for (let row = -1; row * rowStep < height + rowStep; row++) {
    const y = row * rowStep;
    const xOffset = row % 2 === 0 ? 0 : radius;
    for (let col = -1; col * step + xOffset < width + step; col++) {
      const cx = col * step + xOffset;
      out.push(circle(cx, y, radius, fill));
    }
  }
  return out;
}
function diamondLatticeTiles(width, height, size, fill) {
  const out = [];
  const dx = size * 2;
  const dy = size * 2;
  for (let row = -1; row * dy < height + dy; row++) {
    const yOffset = row % 2 === 0 ? 0 : size;
    for (let col = -1; col * dx + yOffset < width + dx; col++) {
      const cx = col * dx + yOffset;
      const cy = row * dy;
      const pts = [
        { x: cx, y: cy - size },
        { x: cx + size, y: cy },
        { x: cx, y: cy + size },
        { x: cx - size, y: cy }
      ];
      out.push(polygon(pts, fill));
    }
  }
  return out;
}
function chevronTiles(width, height, tileSize, weight, fill) {
  const out = [];
  const pitch = tileSize * 0.6;
  const amplitude = tileSize;
  const strokeW = tileSize * weight;
  for (let row = -1; row * amplitude < height + amplitude; row++) {
    const y = row * amplitude;
    const parts = [];
    for (let x = -pitch; x < width + pitch; x += pitch) {
      if (parts.length === 0) parts.push(`M${r6(x)},${r6(y)}`);
      else parts.push(`L${r6(x)},${r6(y + (Math.abs(Math.round((x - pitch * 0.5) / pitch)) % 2 === 0 ? amplitude : 0))}`);
    }
    parts.length = 0;
    let up = true;
    for (let i = 0, x = -pitch; x < width + pitch * 2; i++, x += pitch) {
      const py = up ? y : y + amplitude;
      parts.push(`${i === 0 ? "M" : "L"}${r6(x)},${r6(py)}`);
      up = !up;
    }
    out.push(
      `<path d="${parts.join(" ")}" fill="none" stroke="${fill}" stroke-width="${r6(strokeW)}" stroke-linejoin="miter"/>`
    );
  }
  return out;
}
function polkaTiles(width, height, spacing, weight, fill, _rngSample) {
  const out = [];
  const radius = spacing / 2 * weight;
  const half = spacing / 2;
  for (let y = half; y < height + half; y += spacing) {
    for (let x = half; x < width + half; x += spacing) {
      out.push(circle(x, y, radius, fill));
    }
  }
  return out;
}
function r6(n) {
  return Math.round(n * 100) / 100;
}
var tilesGenerator = {
  key: "tiles",
  label: "Tiles",
  category: "geometric",
  defaultSize: DEFAULT_SIZE6,
  paramsSchema: PARAMS_SCHEMA6,
  generate: generate6
};

// src/canvas-patterns/generators/types.ts
function getDefaultParams(schema) {
  const out = {};
  for (const key in schema) {
    out[key] = schema[key].default;
  }
  return out;
}

// src/canvas-patterns/generators/index.ts
var GENERATORS = {
  [layeredWavesGenerator.key]: layeredWavesGenerator,
  [stackedPeaksGenerator.key]: stackedPeaksGenerator,
  [blobSceneGenerator.key]: blobSceneGenerator,
  [symbolScatterGenerator.key]: symbolScatterGenerator,
  [lowPolyGridGenerator.key]: lowPolyGridGenerator,
  [tilesGenerator.key]: tilesGenerator
};
function registerGenerator(definition) {
  GENERATORS[definition.key] = definition;
}
function getGenerator(key) {
  return GENERATORS[key];
}
function listGenerators() {
  return Object.values(GENERATORS);
}

// src/constants/industries.ts
var INDUSTRY_OPTIONS = [
  "Advertising/Marketing Agency",
  "Airlines",
  "Animals & Pet Supplies",
  "App",
  "Arts and Crafts",
  "B2B",
  "Baby & Children",
  "Banking / Credit Card",
  "Beverage",
  "Cameras & Optics",
  "E-commerce",
  "Ebook and Whitepaper",
  "Education / School",
  "Electronics",
  "Entertainment",
  "Exercise and Fitness",
  "Fashion",
  "Financial",
  "Food Items",
  "Garden",
  "Government",
  "Health & Beauty",
  "Healthcare",
  "Hobbies",
  "Home Decor & Gardening",
  "Hotel & Hospitality",
  "Insurance",
  "Movie",
  "Music",
  "News",
  "Nonprofit / Charity",
  "Optical",
  "Outdoors",
  "Personal Care",
  "Podcast",
  "Publication and Blog",
  "Real Estate",
  "Vehicles & Parts",
  "Video Gaming",
  "Other"
];
export {
  AngularGradientPaintSchema,
  BUILTIN_TILES,
  BlendModeEnum,
  ButtonConfigSchema,
  CanvasCompositionSchema,
  CanvasGroupSchema,
  CanvasImageSchema,
  CanvasObjectSchema,
  CanvasOverlaySchema,
  CanvasShapeSchema,
  CanvasTextSchema,
  DEFAULT_PATTERN,
  DEFAULT_WARP,
  DiamondGradientPaintSchema,
  DividerConfigSchema,
  EMOJI_FONT_FACE_CSS,
  EMOJI_FONT_FAMILY,
  EMOJI_FONT_URL,
  EffectSchema,
  GENERATORS,
  GradientConfigSchema,
  GradientSchema,
  GradientStopSchema2 as GradientStopSchema,
  HeaderBarConfigSchema,
  HeaderBarLinkSchema,
  HtmlConfigSchema,
  INDUSTRY_OPTIONS,
  ImageAdjustmentsSchema,
  ImageConfigSchema,
  ImagePaintSchema,
  ImageTransformSchema,
  LinearGradientPaintSchema,
  OverlayConfigSchema,
  PATTERN_ALIGNMENTS,
  PaintBlendModeSchema,
  PaintSchema,
  ProductConfigSchema,
  ProductFieldRoleEnum,
  RadialGradientPaintSchema,
  SHAPE_GENERATORS,
  SYMBOL_FONT_FACE_CSS,
  SYMBOL_FONT_FAMILY,
  SYMBOL_FONT_URL,
  SceneDocumentSchema,
  SceneNodeSchema,
  SceneNodeTypeEnum,
  ScrimSchema,
  ShapeDividerConfigSchema,
  SlotRoleEnum,
  SocialIconSchema,
  SocialIconsConfigSchema,
  SolidPaintSchema,
  SpacerConfigSchema,
  StyleConfigSchema,
  TableConfigSchema,
  TransitionImageConfigSchema,
  WARP_TYPES,
  addFontVariant,
  adjustmentsToColorMatrix,
  adjustmentsToCssFilter,
  applyConstraints,
  applyFillToPaint,
  applyOpacity,
  applySvgColors,
  applyTextTransform,
  bandifyAbsoluteScene,
  blobPathData,
  buildFontFaceCss,
  buildGoogleFontVariantUrls,
  buildGradientCss,
  buildOverlayCss,
  buildOverlayMjml,
  buildPaddingShorthand,
  buildPatternCellSvg,
  buildPolygonPath,
  buildRecolorMap,
  buildShapeDividerMjml,
  buildShapeDividerSvg,
  buildShapeGenPath,
  buildStarPath,
  buildTextParagraph,
  calculateBoundingBox,
  collectSceneFontVariants,
  commitDocSize,
  computeBackgroundStyle,
  computeBorder,
  computeClassNames,
  computeFontWeight,
  computeInlineStyles,
  computeLayout,
  computeMargin,
  computePadding,
  computeWarpScale,
  createEmptyComposition,
  createEmptySceneDocument,
  createGroupFromObjects,
  createGroupObject,
  createImageObject,
  createOverlayObject,
  createShapeObject,
  createTextObject,
  degradeGoogleFontUrl,
  derivePalette,
  diagonalEdgePathData,
  findNodeBySlotRole,
  findNodes,
  findObjectById,
  flattenComposition,
  generateObjectId,
  getBuiltinTile,
  getChildren,
  getDefaultParams,
  getDividerShapeTypes,
  getDividerSvgPath,
  getGenerator,
  getRichList,
  getRichSpans,
  getRootNodes,
  getShapeDividerTypes,
  googleFontVariantUrls,
  hasDuotone,
  hasVisibleAdjustments,
  htmlToScene,
  htmlToScenes,
  invalidateOutlineCache,
  isGroup,
  isSceneDocument,
  isTextOnGradient,
  isUnderlined,
  listGenerators,
  measureText,
  measureTextHeight,
  mergeStyleConfig,
  mountainsEdgePathData,
  moveObjectToParent,
  nodeNeedsRaster,
  normalizeHex,
  normalizeImageFills,
  parseColor,
  parseColorComponents,
  parseGradientCss,
  parsePaddingShorthand,
  parseSceneDocument,
  peaksEdgePathData,
  primaryFontFamily,
  rasterizeSvgToObjectUrl,
  rebaseTransformToBitmap,
  recalculateGroupBounds,
  registerGenerator,
  removeObjectFromTree,
  renderDocumentBackground,
  renderNode,
  renderWarpedText,
  resolveLinearGradientCoords,
  revokePatternImageSrc,
  safeParseComposition,
  safeParseStyleConfig,
  scallopsEdgePathData,
  sceneToHtml,
  shapeGenDefaults,
  sortByZIndex,
  spansForLine,
  splitIntoRuns,
  stepsEdgePathData,
  suggestTextOnGradientPadding,
  textHasEmoji,
  textHasSymbol,
  tightenGroups,
  tornEdgePathData,
  ungroupToObjects,
  updateObjectInTree,
  validateColor,
  validateComposition,
  validateDimension,
  validateGradientColors,
  validateShapeDividerConfig,
  validateStyleConfig,
  walkNodes,
  waveEdgePathData,
  zigzagPathData
};
